/**
 * Chapter 1 layouts — each beat is a function that sets target values
 * on a shared entity pool. The engine lerps entities toward their
 * targets each frame, so transitioning between beats is a continuous
 * physical morph: entity 3 that was a satellite in beat 1 becomes
 * the right side of a pair in beat 2, becomes the friend in beat 3.
 *
 * Entity semantics across the whole chapter:
 *   id 0      — the hero / "you" position. Always meaningful.
 *   id 1..5   — primary role slots (companion, archetypes, branch people)
 *   id 6..20  — secondary slots (paths, scatter dots, timeline axis)
 *   id 21..47 — ambient / floor dots (fade when unused)
 *
 * Each layout also returns optional decoration metadata (rings, beams,
 * labels) for the beat — rendered on top of entities by the engine.
 */
import { DEFAULT_TILT, type Warmth } from "./scene3d";

export const ENTITY_COUNT = 48;

export interface Entity {
  id: number;
  x: number;
  y: number;
  z: number;
  tx: number;
  ty: number;
  tz: number;
  size: number;
  targetSize: number;
  alpha: number;
  targetAlpha: number;
  warmth: Warmth;
  targetWarmth: Warmth;
  halo: number;
  targetHalo: number;
  pulse: boolean;

  // Wave emission — each entity can drive ripples in the ground plane.
  // Amp is typically 0..1, freq/speed are radial wavenumber and angular
  // rate. Engine lerps these toward targets, so turning emissions on
  // and off and morphing between scenes is automatic and smooth.
  emit: number;
  targetEmit: number;
  emitFreq: number;
  targetEmitFreq: number;
  emitSpeed: number;
  targetEmitSpeed: number;

  // Wave color RGB — each entity paints its own waves. Engine blends
  // colors at each mesh point by the weighted sum of nearby emitters,
  // so overlapping colors visibly mix (interference in hue-space).
  waveR: number;
  waveG: number;
  waveB: number;
  targetWaveR: number;
  targetWaveG: number;
  targetWaveB: number;

  /**
   * Integrated phase — accumulates `emitSpeed · dt` each frame. The
   * wave equation uses this instead of `time · speed` so that when
   * emitSpeed lerps between beats, the phase value itself stays
   * continuous (no jumps). Eliminates the "violent shake" during
   * transitions.
   */
  phase: number;
}

/** Color palette for entity wave colors. Used to give each person a
 *  distinct signature in Chapter 2 scenes. */
export type RGB = readonly [number, number, number];
export const WAVE_PALETTE = {
  cream: [242, 238, 228] as RGB,
  amber: [255, 176, 70] as RGB,
  cyan: [80, 210, 255] as RGB,
  magenta: [255, 110, 200] as RGB,
  green: [130, 230, 160] as RGB,
  purple: [185, 135, 245] as RGB,
  warmpink: [255, 140, 170] as RGB,
  teal: [60, 200, 200] as RGB,
  orange: [255, 140, 80] as RGB,
  yellow: [255, 220, 100] as RGB,
  blue: [130, 170, 255] as RGB,
  rose: [245, 120, 140] as RGB,
};

export type LabelEntry = {
  text: string;
  entityId?: number;
  at?: { x: number; y: number; z: number };
  offsetY?: number;
  warmth?: Warmth;
  alpha?: number;
  size?: number;
};

export type DecoEntry =
  | {
      kind: "ring";
      at: { x: number; y: number; z: number };
      radius: number;
      warmth: Warmth;
      alpha: number;
      width?: number;
      /** Optional RGB override — use instead of the warmth lookup. */
      rgb?: readonly [number, number, number];
    }
  | {
      kind: "line";
      from: { x: number; y: number; z: number };
      to: { x: number; y: number; z: number };
      warmth: Warmth;
      alpha: number;
      width?: number;
      /** Optional RGB override — use instead of warmth lookup. */
      rgb?: readonly [number, number, number];
    }
  | {
      kind: "beam";
      base: { x: number; y: number; z: number };
      top: { x: number; y: number; z: number };
      warmth: Warmth;
      alpha: number;
    }
  | {
      kind: "entityLine";
      fromId: number;
      toId: number;
      warmth: Warmth;
      alpha: number;
      width?: number;
    }
  | {
      /**
       * A wave-distorted polyline between two world points. The wave
       * oscillates perpendicular to the line (in the world y axis) with
       * sin(tP·freq·2π − phase). Phase advances over time, so the wave
       * visibly travels. Envelope peaks at line midpoint and fades to
       * 0 at the endpoints — like a plucked string or traveling packet.
       */
      kind: "waveLine";
      from: { x: number; y: number; z: number };
      to: { x: number; y: number; z: number };
      phase: number;
      frequency: number;
      amplitude: number;
      noise?: number;
      envelope?: boolean;
      samples?: number;
      warmth: Warmth;
      alpha: number;
      width?: number;
      /** Optional RGB override — use instead of the warmth lookup. */
      rgb?: readonly [number, number, number];
    }
  | {
      /**
       * Wireframe globe — parallels + meridians around a center
       * point, with real spherical wave physics displacing the
       * surface. `rotation` spins the globe about its polar axis.
       * `axisTilt` tilts the polar axis off vertical (Earth ≈ 0.41 rad).
       * The renderer samples wave contributions from every live
       * emitter in the entity pool and pushes each vertex radially
       * outward by the summed wave height — waves travel along the
       * sphere's great-circle arcs, decay with angular distance,
       * and interfere at points where multiple emitters meet.
       */
      kind: "globe";
      at: { x: number; y: number; z: number };
      radius: number;
      rotation: number;
      axisTilt: number;
      /** How far surface waves push vertices radially. */
      waveAmplitude: number;
      warmth: Warmth;
      alpha: number;
      /** Optional RGB override (e.g., for warm accents). */
      rgb?: readonly [number, number, number];
    }
  | {
      /**
       * Localized 3D wave patch — reveals a small piece of a specific
       * emitter's traveling wave around a center point. Used by beat 18
       * to "light up" the actual wave that just hit the hero. The patch
       * samples sin(distFromEmitter·freq − phase) over a small disc and
       * draws a mini wireframe with soft radial falloff.
       */
      kind: "wavePatch";
      /** Center of the patch on the ground plane. */
      at: { x: number; z: number };
      /** Radius of the patch in world units. */
      radius: number;
      /** Source emitter — used to compute the wave function. */
      emitterX: number;
      emitterZ: number;
      frequency: number;
      /** Live integrated phase from the source emitter. */
      phase: number;
      amplitude: number;
      rgb: readonly [number, number, number];
      /** Overall alpha — fade externally over the patch's lifetime. */
      alpha: number;
    }
  | {
      /**
       * Dot cloud — renders many small dots at 3D positions in a single
       * pass. Used by beatConnectedWorld to draw the ~800 land-mass dots
       * that form a recognizable Earth. Far more efficient than one
       * decoration per dot; the renderer projects each point, sorts by
       * depth, and dims points behind the optional occluder sphere.
       */
      kind: "dotCloud";
      points: ReadonlyArray<{ x: number; y: number; z: number }>;
      /** Screen-space pixel radius of each dot (scaled by depth). */
      pixelRadius: number;
      /** Base alpha for front-facing dots. Back dots dimmed. */
      alpha: number;
      warmth: Warmth;
      rgb?: readonly [number, number, number];
      /** Optional sphere — dots behind the sphere's far side dim. */
      occluder?: { x: number; y: number; z: number; radius: number };
      /** Optional per-point alpha multiplier (0..1). Same length as
       *  points. Lets scroll views fade individual rows smoothly. */
      alphas?: ReadonlyArray<number>;
    }
  | {
      /**
       * Expanding 3D spherical wavefront — models real light-like
       * propagation: a coherent wave-packet emitted from `origin`
       * whose radius grows with time and whose intensity falls with
       * 1/r² (inverse-square / energy conservation). Rendered as a
       * Fibonacci-sampled dot shell so the whole sphere reads as a
       * propagating surface, not a ring. Additive compositing —
       * where two shells overlap, light adds (real interference).
       *
       * `phaseT` is live beat time; the shell uses it to add a
       * tangential phase wobble so each dot pulses like a wave-packet
       * crest rather than a static dot.
       */
      kind: "wavefront";
      origin: { x: number; y: number; z: number };
      /** Current shell radius in world units. */
      radius: number;
      /** Shell intensity at the current radius (post 1/r² falloff, 0..1). */
      intensity: number;
      rgb: readonly [number, number, number];
      /** Shell thickness as a fraction of radius (for phase wobble width). */
      thickness: number;
      /** Live beat time for phase animation. */
      phaseT: number;
    }
  | {
      /**
       * Layered atmospheric glow around a sphere — outer bloom for
       * distant scatter, tight limb rim for the lit edge, and a shell
       * of drifting wisps above the surface so you feel the atmosphere
       * rather than just see a clean silhouette. All drawn additively
       * so it reads as light, not paint.
       */
      kind: "atmosphere";
      at: { x: number; y: number; z: number };
      radius: number;
      /** Inner atmospheric tint near the limb (e.g. warm horizon glow). */
      innerRgb: readonly [number, number, number];
      /** Outer scatter tint (e.g. deep cyan / ionosphere). */
      outerRgb: readonly [number, number, number];
      /** Overall multiplier (0..1). */
      alpha: number;
      /** Live time for wisp drift — pass beat t. */
      time: number;
    }
  | {
      /**
       * Ring tangent to a sphere at a given unit-direction. Used for
       * pin-drop effects at arc destinations — the ring lies flat on
       * the tangent plane at that surface point, so it reads as a
       * "ping" at that location rather than a flat floor ring.
       */
      kind: "sphereRing";
      center: { x: number; y: number; z: number };
      /** Unit direction from center to the surface point. */
      normal: { x: number; y: number; z: number };
      /** Distance from center (typically sphere radius + small lift). */
      distance: number;
      /** Ring radius in the tangent plane. */
      radius: number;
      warmth: Warmth;
      alpha: number;
      width?: number;
      rgb?: readonly [number, number, number];
    };

export interface BeatLayoutResult {
  labels: LabelEntry[];
  decorations: DecoEntry[];
  /** Multiplier on the wave-mesh alpha. 0 hides the mesh entirely,
   *  1 is default. Lets specific beats (e.g., beatBlind) hide the
   *  field so only localized ripples show. */
  meshAlphaMul?: number;
  /** Camera zoom multiplier. 1 = default; <1 zooms out (for the
   *  "pull back to every room on earth" beat). */
  cameraZoom?: number;
  /** Mesh extent multiplier. 1 = default (1.9 world units each side).
   *  >1 expands the plane so it keeps filling the screen when the
   *  camera pulls back. */
  meshExtentMul?: number;
  /** Additive Y-rotation offset (radians). Each beat can sit at a
   *  slightly different angle so consecutive scenes don't all read
   *  as identical camera setups. Auto-rotate still applies on top. */
  cameraRotYOffset?: number;
  /** Additive X-tilt offset (radians). Positive tilts the camera
   *  more top-down; negative flattens it toward side-on. Use to
   *  swing between eye-level and overhead across beats for Johnny
   *  Harris-style camera immersion. */
  cameraTiltOffset?: number;
  /** Beats that render a solid sphere (e.g., globe) can declare it
   *  here so the engine culls back-facing entities — otherwise
   *  nodes on the far side of the sphere show through. */
  occluderSphere?: { x: number; y: number; z: number; radius: number };
}

export type BeatLayoutFn = (entities: Entity[], t: number) => BeatLayoutResult;

// Utility: seeded pseudo-random in [0,1)
function fract(x: number): number {
  return x - Math.floor(x);
}
function rand(seed: number): number {
  return fract(Math.sin(seed * 12.9898) * 43758.5453);
}

/**
 * Sample the combined wave height at a world (x, z) point. Same math
 * as the engine uses when rendering the mesh. Layouts call this to
 * compute wave gradients for physics-driven behaviors (e.g. a node
 * being pushed around by other people's waves).
 */
export function sampleWaveHeight(
  entities: Entity[],
  wx: number,
  wz: number
): number {
  let height = 0;
  for (const e of entities) {
    if (e.emit < 0.005) continue;
    const dx = wx - e.x;
    const dz = wz - e.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    const decay = Math.exp(-dist * 0.95);
    height += e.emit * decay * Math.sin(dist * e.emitFreq - e.phase);
  }
  return height;
}

function setTarget(
  e: Entity,
  x: number,
  y: number,
  z: number,
  opts: {
    size?: number;
    alpha?: number;
    warmth?: Warmth;
    halo?: number;
    pulse?: boolean;
    emit?: number;
    emitFreq?: number;
    emitSpeed?: number;
    waveColor?: RGB;
  } = {}
): void {
  e.tx = x;
  e.ty = y;
  e.tz = z;
  if (opts.size !== undefined) e.targetSize = opts.size;
  if (opts.alpha !== undefined) e.targetAlpha = opts.alpha;
  if (opts.warmth !== undefined) e.targetWarmth = opts.warmth;
  if (opts.halo !== undefined) e.targetHalo = opts.halo;
  if (opts.pulse !== undefined) e.pulse = opts.pulse;
  if (opts.emit !== undefined) e.targetEmit = opts.emit;
  if (opts.emitFreq !== undefined) e.targetEmitFreq = opts.emitFreq;
  if (opts.emitSpeed !== undefined) e.targetEmitSpeed = opts.emitSpeed;
  if (opts.waveColor !== undefined) {
    e.targetWaveR = opts.waveColor[0];
    e.targetWaveG = opts.waveColor[1];
    e.targetWaveB = opts.waveColor[2];
  }
}

/**
 * Park any entities we're not using in ambient floor positions —
 * scattered faintly at y≈0 so they never disappear completely. Seeded
 * variety in size, brightness, and tiny halo gives the floor a
 * starfield feel instead of a uniform dim grid.
 */
function parkAsAmbient(entities: Entity[], fromId: number): void {
  for (let i = fromId; i < entities.length; i++) {
    const seed = i * 11 + 3;
    const rx = (rand(seed) - 0.5) * 3.4;
    const rz = (rand(seed + 7) - 0.5) * 3;
    const sizeVar = 0.35 + rand(seed + 21) * 0.65;
    const alphaVar = 0.14 + rand(seed + 29) * 0.28;
    const haloVar = rand(seed + 37) < 0.2 ? 0.04 + rand(seed + 43) * 0.04 : 0;
    setTarget(entities[i], rx, 0.02 + rand(seed + 13) * 0.12, rz, {
      size: sizeVar,
      alpha: alphaVar,
      warmth: "cream",
      halo: haloVar,
      pulse: false,
      emit: 0,
    });
  }
}

// ──────────────────────────────────────────────────────────────────────
// Beat 1 — "nothing great happens alone."
// hero at center + 14 satellites emerging in a soft 3D shell around it.
// ──────────────────────────────────────────────────────────────────────

export const beat1: BeatLayoutFn = (entities, t) => {
  // Beat 1 — "think about the best thing that ever happened to you."
  // A single warm memory rises out of deep darkness toward the viewer.
  // Small peripheral glimmers twinkle in at various depths — other
  // memories you didn't ask for. The center dominates. This is the
  // one you're thinking about, becoming vivid. Nothing fans out,
  // nothing is networked yet — it's just recall.

  const ARRIVAL = 4.5;
  const arriveP = Math.min(1, t / ARRIVAL);
  const eased = arriveP * arriveP * (3 - 2 * arriveP);

  // Main memory — travels from deep z toward the foreground, growing
  // and brightening as it arrives.
  const memZ = -2.6 + eased * 2.4;
  const memSize = 0.7 + eased * 1.7;
  const memAlpha = 0.15 + eased * 0.82;
  const memHalo = 0.12 + eased * 0.55;
  const settled = Math.max(0, Math.min(1, (t - ARRIVAL) / 1.2));
  const breathe = settled * (0.5 + 0.5 * Math.sin(t * 1.1));

  setTarget(entities[0], 0, -0.4, memZ, {
    size: memSize + breathe * 0.22,
    alpha: memAlpha,
    warmth: "amber",
    halo: memHalo + breathe * 0.22,
    pulse: settled > 0.4,
  });

  // Peripheral glimmers — tiny, at varied depths, staggered twinkle.
  // They're the other memories brushing the edge of your attention.
  const glimmerCount = 9;
  for (let i = 1; i <= glimmerCount; i++) {
    const seed = i * 7.13;
    const angle = rand(seed) * Math.PI * 2;
    const dist = 1.25 + rand(seed + 3) * 1.15;
    const depthBack = -0.4 - rand(seed + 7) * 1.7;
    const x = Math.cos(angle) * dist;
    const y = -0.3 + (rand(seed + 11) - 0.5) * 0.75;
    const z = Math.sin(angle) * dist * 0.55 + depthBack;

    const appearAt = 1.6 + i * 0.38;
    const gP = Math.max(0, Math.min(1, (t - appearAt) / 1.3));
    const twFreq = 1.4 + rand(seed + 17) * 1.4;
    const twPhase = rand(seed + 23) * Math.PI * 2;
    const twinkle = 0.6 + 0.4 * Math.sin(t * twFreq + twPhase);

    setTarget(entities[i], x, y, z, {
      size: 0.5 + gP * 0.3,
      alpha: gP * 0.38 * twinkle,
      warmth: "cream",
      halo: 0.03 * gP,
      pulse: false,
    });
  }

  parkAsAmbient(entities, glimmerCount + 1);

  // No label — the animation carries it. A text overlay at "you"
  // would flatten the moment.
  //
  // Camera values are biased toward beatConnectedWorld's (zoom 1.1,
  // rotY 0.2, tilt -0.05) so the engine's between-beat lerp resolves
  // as a gentle drift rather than a visible swing.
  return {
    labels: [],
    decorations: [],
    cameraZoom: 1.15,
    cameraRotYOffset: 0.12,
    cameraTiltOffset: -0.15,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Promise-arc trio — "the job / the love / the life you want"
//
// The climactic build. Each beat picks up exactly where the previous
// left off — same hero, same harmonic chord companions from beatHold
// and beatOutcomes — and ramps amplitude + frequency UP fast. The
// hero's emitFreq climbs so the audio hum (mapped 90·2^(f/7) Hz)
// rises through the arc, taking the "buzz" with it. Companions stay
// in the field as a quiet harmonic bed during job + love, then
// SURGE back to full at life — the full chord resolves with the
// hero's peak wave. By "the life you want" the system is at
// saturation: biggest amplitude, highest pitch, widest halo, fullest
// chord. State of inertia.
//
// Amplitude curve: ease-out-quad so each beat's climb is FAST at
// start, leveling toward end. That's the "amplitude goes up fast"
// the user asked for — not a slow swell, a punch-up-and-hold.
//
// Buzz/frequency target chain (engine lerps slowly so actual freq
// chases the target across the whole arc):
//   outcomes hero  4.4 Hz  → 156 Hz audio
//   job target     5.5 Hz  → ~165 Hz (climbing)
//   love target    6.5 Hz  → ~175 Hz (climbing harder)
//   life target    8.0 Hz  → ~185–195 Hz (peak — felt as buzz)
// ──────────────────────────────────────────────────────────────────────

interface ChordPosition {
  angle: number;
  r: number;
  yOff: number;
  color: RGB;
  freq: number;
  peakEmit: number;
  peakSize: number;
  peakHalo: number;
  breathRate: number;
  breathPhase: number;
}

// Shared harmonic-chord positions used by beatHold, beatOutcomes, and
// the promise trio. Same physical cluster across all five beats —
// nothing ever teleports.
const CHORD_COMPANIONS: ReadonlyArray<ChordPosition> = [
  { angle: 0.15,              r: 0.52, yOff:  0.02, color: WAVE_PALETTE.warmpink, freq: 3.3, peakEmit: 0.80, peakSize: 1.74, peakHalo: 0.40, breathRate: 1.05, breathPhase: 0.4 },
  { angle: Math.PI + 0.1,     r: 0.50, yOff: -0.03, color: WAVE_PALETTE.yellow,   freq: 5.5, peakEmit: 0.72, peakSize: 1.64, peakHalo: 0.36, breathRate: 0.75, breathPhase: 1.4 },
  { angle: Math.PI / 2 + 0.08,r: 0.55, yOff:  0.03, color: WAVE_PALETTE.green,    freq: 6.6, peakEmit: 0.66, peakSize: 1.57, peakHalo: 0.34, breathRate: 1.25, breathPhase: 2.7 },
  { angle: 5 * Math.PI / 4 - 0.1, r: 0.44, yOff: 0.01, color: WAVE_PALETTE.rose,  freq: 2.2, peakEmit: 0.74, peakSize: 1.70, peakHalo: 0.38, breathRate: 0.55, breathPhase: 3.3 },
  { angle: -Math.PI / 5,      r: 0.46, yOff: -0.02, color: WAVE_PALETTE.purple,   freq: 8.8, peakEmit: 0.60, peakSize: 1.50, peakHalo: 0.32, breathRate: 1.45, breathPhase: 4.8 },
];

// Render the chord. emitScale = 0 means companions are present as
// dim visual nodes only (no wave contribution) — promise trio needs
// this so the mesh shows ONE uniform hero wave instead of 5 voices
// cross-interfering into a mess. emitScale ≥ 1 = full chord
// (beatOutcomes level).
function renderChordCompanions(
  entities: Entity[],
  t: number,
  emitScale: number,
  rock: number
): void {
  for (let i = 0; i < CHORD_COMPANIONS.length; i++) {
    const c = CHORD_COMPANIONS[i];
    const x = Math.cos(c.angle) * c.r + rock;
    const z = Math.sin(c.angle) * c.r * 0.78;
    const breath = 0.5 + 0.5 * Math.sin(t * c.breathRate + c.breathPhase);
    // Visual floor — companions stay visible as dim people even when
    // their wave emission drops to zero, so the cluster doesn't
    // disappear from view during the promise trio.
    const visibility = Math.max(0.35, emitScale);
    setTarget(entities[i + 1], x, -0.5 + c.yOff, z, {
      size: c.peakSize * (0.72 + visibility * 0.28) + breath * 0.06,
      alpha: 0.55 + visibility * 0.4,
      warmth: "amber",
      halo: c.peakHalo * visibility + breath * 0.05,
      pulse: false,
      emit: c.peakEmit * emitScale + breath * 0.03 * emitScale,
      emitFreq: c.freq,
      emitSpeed: 2.2 + i * 0.12,
      waveColor: c.color,
    });
  }
}

// Helper — lerp between two RGB colors.
function lerpRgb(a: RGB, b: RGB, t: number): RGB {
  return [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
  ];
}

// Promise trio physics: as the wave grows across job → love → life
// its FREQUENCY DROPS (longer wavelength carries more energy — real
// wave physics). The hum synth rides that drop into deep felt-bass
// territory (~136 → ~125 → ~113 Hz) — a rumble you feel in the chest,
// not a buzz in the ears. Colors ride the pitch descent too: lower Hz
// = longer EM wavelength = warmer/redder end of the spectrum. Job
// reads yellow-gold, love rose-pink, life deep ruby-red. One uniform
// hero wave each beat — the chord collapses into a single cycle.

// Promise trio — ONE continuous wave growing gradually from outcomes
// through life. No seeding, no aggressive cuts — just targets that
// change slightly between beats, and the engine lerps smoothly.
//
// Physics calibrated for SMOOTH FLAT HARMONY:
//   - emit stays modest (0.82 → 0.95 → 1.05). Previous 1.45 created
//     towering spikes + deep craters as the wave oscillated through
//     its own position — looked aggressive, not harmonious.
//   - emitFreq stays low (3.6 → 2.8 → 2.2). Long wavelengths = flat
//     gentle curves on the mesh, no tight ripples facet through the
//     grid. 42+ samples per wavelength at these values = silky.
//   - emitSpeed moderate (3.8 → 4.6 → 5.2). Waves roll at a pace the
//     eye can follow — not so fast they blur, not so slow they feel
//     stalled. Period ~1.2s — matches a calm breathing rhythm.
//   - Color drifts warm gold → rose → pale hopeful white-blue. The
//     final hopeful tone lands at life.
// Everything else comes from the engine's POS/WAVE/FREQ_LERP: the
// hero slides smoothly from outcomes' amber-4.4Hz state through each
// promise beat's target, so what the viewer sees is ONE wave growing.

// beatPromiseJob — "the job." Warm gold. First visible lift above
// outcomes' state. Engine lerps — no jump-cut.
export const beatPromiseJob: BeatLayoutFn = (entities, t) => {
  const DUR = 1.55;
  const p = Math.min(1, t / DUR);
  const bloom = p * (2 - p);

  const colorStart: RGB = [255, 215, 115];
  const colorEnd: RGB = [255, 195, 95];
  const color = lerpRgb(colorStart, colorEnd, bloom);

  setTarget(entities[0], 0, -0.5, 0, {
    size: 2.15,
    alpha: 1,
    warmth: "amber",
    halo: 0.42,
    pulse: false,
    emit: 0.82,
    emitFreq: 3.6,
    emitSpeed: 3.8,
    waveColor: color,
  });

  parkAsAmbient(entities, 1);

  return {
    labels: [],
    decorations: [],
    meshAlphaMul: 1.1,
    cameraZoom: EMOTION_ZOOM,
    cameraRotYOffset: 0.3,
    cameraTiltOffset: EMOTION_TILT,
  };
};

// beatPromiseLove — "the love." Warm rose. Next step of the same
// wave — engine lerps from job's gold to this target gradually.
export const beatPromiseLove: BeatLayoutFn = (entities, t) => {
  const DUR = 1.55;
  const p = Math.min(1, t / DUR);
  const bloom = p * (2 - p);

  const colorStart: RGB = [255, 150, 170];
  const colorEnd: RGB = [255, 170, 190];
  const color = lerpRgb(colorStart, colorEnd, bloom);

  setTarget(entities[0], 0, -0.5, 0, {
    size: 2.28,
    alpha: 1,
    warmth: "amber",
    halo: 0.55,
    pulse: false,
    emit: 0.95,
    emitFreq: 2.8,
    emitSpeed: 4.6,
    waveColor: color,
  });

  parkAsAmbient(entities, 1);

  return {
    labels: [],
    decorations: [],
    meshAlphaMul: 1.2,
    cameraZoom: EMOTION_ZOOM,
    cameraRotYOffset: -0.3,
    cameraTiltOffset: EMOTION_TILT,
  };
};

// beatPromiseLife — "the life you want." Hopeful pale blue-white —
// the celestial landing color. Same wave grown to its peak state;
// engine lerps from love smoothly. Held across the full 2.1s so the
// pattern has time to evolve and read as beautiful harmony.
export const beatPromiseLife: BeatLayoutFn = (entities, t) => {
  const DUR = 2.1;
  const p = Math.min(1, t / DUR);
  const bloom = p * (2 - p);

  const colorStart: RGB = [210, 230, 255];
  const colorEnd: RGB = [240, 248, 255];
  const color = lerpRgb(colorStart, colorEnd, bloom);

  setTarget(entities[0], 0, -0.5, 0, {
    size: 2.45,
    alpha: 1,
    warmth: "cool",
    halo: 0.72,
    pulse: false,
    emit: 1.05,
    emitFreq: 2.2,
    emitSpeed: 5.2,
    waveColor: color,
  });

  parkAsAmbient(entities, 1);

  return {
    labels: [],
    decorations: [],
    meshAlphaMul: 1.3,
    cameraZoom: EMOTION_ZOOM,
    cameraRotYOffset: 0,
    cameraTiltOffset: EMOTION_TILT,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beat2 — "it lives inside someone else."
//
// Four figures orbit a shared center — the hero and three others,
// bound by the same gravity. Three of them are dim (including the
// hero). ONE of the orbiters burns bright amber from the inside —
// they're the one who carries the life you're looking for. The
// shared orbit IS the visual proof they're all connected, and the
// bright one's position in that orbit makes it obvious: you're
// already in relation with them, the thing you want is living
// inside someone who's already circling you.
// ──────────────────────────────────────────────────────────────────────

function smoothstep01(x: number): number {
  const p = Math.max(0, Math.min(1, x));
  return p * p * (3 - 2 * p);
}

export const beat2: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    for (const e of entities) {
      e.emit = 0;
      e.targetEmit = 0;
    }
  }

  // ── Orbit physics ─────────────────────────────────────────────────
  // 4 point-bodies in a perfect circular orbit around the origin,
  // lying flat in the XZ plane at y = Y0. All bodies share the same
  // angular velocity ω, so they hold a fixed angular spacing forever
  // (constellation dynamics — no differential rotation).
  //
  //   p_i(t) = ( R · cos(θ_i + ω·t), Y0, R · sin(θ_i + ω·t) )
  //
  // The camera's tilt projection turns this circle into an ellipse
  // automatically. Because the visible orbit-path ring is drawn from
  // the same (x, y, z) equations via the same projection, the bodies
  // trace exactly along the ring — no mismatch.
  //
  // ω = 0.32 rad/s → full revolution every ~19.6s → 67° of rotation
  // visible in the 3.5-second beat. Slow and deliberate, not rushed.
  const OMEGA = 0.32;
  const R = 0.95;
  const Y0 = -0.5;
  const HERO_IDX = 0;
  const FOCAL_IDX = 1;
  const orbitAngle = t * OMEGA;

  // Fixed angular positions on the orbit, π/2 apart:
  //   HERO:  3π/2 (starts at the BACK of the orbit, z = −R, recedes)
  //   FOCAL: π/2  (starts at the FRONT, z = +R, closest to viewer)
  //   #2:    0    (right)
  //   #3:    π    (left)
  // The focal being closest to the viewer at t=0 makes its bloom
  // read immediately; the hero drifts in from the back of the orbit.
  const baseAngles: Record<number, number> = {
    [HERO_IDX]: Math.PI * 1.5,
    [FOCAL_IDX]: Math.PI * 0.5,
    2: 0,
    3: Math.PI,
  };

  // For each body compute world position and a depth cue in [0, 1].
  // z = +R is toward the viewer (front), z = −R is away (back). We
  // normalise to depth01 and use it for atmospheric attenuation —
  // the back of the orbit fades like a point at distance in fog.
  // (The engine's perspective already shrinks far points; we only
  // add alpha + halo haze, not size, to avoid doubling the effect.)
  const body = (i: number) => {
    const ang = baseAngles[i] + orbitAngle;
    const x = Math.cos(ang) * R;
    const z = Math.sin(ang) * R;
    const depth01 = (z / R + 1) * 0.5; // 0 back, 1 front
    return { x, z, depth01 };
  };

  // ── Envelope ──────────────────────────────────────────────────────
  // Everything fades in together at t=0; the orbit is ALREADY moving.
  // The focal's inner bloom starts slightly after reveal and rises
  // over ~1.3s so the ignition feels intentional, not instant.
  const reveal = smoothstep01(t / 0.7);
  const bloom = smoothstep01((t - 0.7) / 1.3);

  // ── Hero — cream, dim, a quiet body on the same orbit. ───────────
  {
    const p = body(HERO_IDX);
    setTarget(entities[HERO_IDX], p.x, Y0, p.z, {
      size: 1.35,
      alpha: (0.45 + p.depth01 * 0.35) * reveal,
      warmth: "cream",
      halo: (0.04 + p.depth01 * 0.05) * reveal,
      pulse: false,
      emit: 0,
      waveColor: [205, 196, 175],
    });
  }

  // ── Focal — amber, bloom-from-within, emits warmth. ──────────────
  {
    const p = body(FOCAL_IDX);
    setTarget(entities[FOCAL_IDX], p.x, Y0, p.z, {
      size: 1.55 + bloom * 0.25,
      alpha: (0.7 + p.depth01 * 0.3) * reveal,
      warmth: "amber",
      halo: (0.12 + bloom * 0.4) * (0.65 + p.depth01 * 0.35) * reveal,
      pulse: bloom > 0.6,
      emit: 0.7 * bloom,
      emitFreq: 3.4,
      emitSpeed: 2.3,
      waveColor: WAVE_PALETTE.amber,
    });
  }

  // ── Two companion bodies — cream, dim, atmospheric. ──────────────
  for (const i of [2, 3]) {
    const p = body(i);
    setTarget(entities[i], p.x, Y0, p.z, {
      size: 1.15,
      alpha: (0.35 + p.depth01 * 0.3) * reveal,
      warmth: "cream",
      halo: (0.03 + p.depth01 * 0.03) * reveal,
      pulse: false,
      emit: 0,
      waveColor: [200, 192, 172],
    });
  }

  parkAsAmbient(entities, 4);

  // ── Decorations ──────────────────────────────────────────────────
  const decorations: DecoEntry[] = [];

  // The orbit path itself — same equation space as the bodies, so
  // they travel exactly along it. Faint cream, a physical trace in
  // the gravity field they all share.
  decorations.push({
    kind: "ring",
    at: { x: 0, y: Y0, z: 0 },
    radius: R,
    warmth: "cream",
    rgb: [230, 222, 200],
    alpha: 0.2 * reveal,
    width: 0.55,
  });

  // Radiant rings expanding from the focal's current orbit position.
  // Two overlapping emissions phased half a period apart keep the
  // waveform continuous — never a dead moment, never a stutter.
  if (bloom > 0.05) {
    const f = body(FOCAL_IDX);
    const ringPeriod = 2.2;
    const ringT1 = (t / ringPeriod) % 1;
    const ringT2 = ((t / ringPeriod) + 0.5) % 1;
    const radialOut = (rt: number) => 0.08 + rt * 0.6;
    decorations.push({
      kind: "ring",
      at: { x: f.x, y: Y0, z: f.z },
      radius: radialOut(ringT1),
      warmth: "amber",
      rgb: WAVE_PALETTE.amber,
      alpha: (1 - ringT1) * 0.55 * bloom,
      width: 0.95,
    });
    decorations.push({
      kind: "ring",
      at: { x: f.x, y: Y0, z: f.z },
      radius: radialOut(ringT2),
      warmth: "amber",
      rgb: WAVE_PALETTE.amber,
      alpha: (1 - ringT2) * 0.32 * bloom,
      width: 0.75,
    });
  }

  return {
    labels: [],
    decorations,
    // Tilt near the classic isometric angle so the orbit reads as a
    // proper ellipse — wide enough to see bodies pass front-to-back,
    // not so steep that the ring collapses into a line.
    cameraZoom: 1.08,
    cameraRotYOffset: 0.1,
    cameraTiltOffset: 0.38,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 3 — "the mentor. the friend. the stranger who said yes."
// Three pair-nodes along the x-axis.
// ──────────────────────────────────────────────────────────────────────

export const beat3: BeatLayoutFn = (entities, t) => {
  const pairs = [
    { x: -1.2, label: "mentor" },
    { x: 0, label: "friend" },
    { x: 1.2, label: "stranger" },
  ];
  // Entity 0, 2, 4 = primary; 1, 3, 5 = companion behind
  const decorations: DecoEntry[] = [];
  const labels: LabelEntry[] = [];
  for (let p = 0; p < pairs.length; p++) {
    const primaryId = p * 2;
    const companionId = p * 2 + 1;
    const visT = Math.min(1, Math.max(0, (t - 0.2 - p * 0.6) / 0.9));
    setTarget(entities[primaryId], pairs[p].x, -0.5, 0, {
      size: 1.5 * visT,
      alpha: 0.9 * visT,
      warmth: "amber",
      halo: 0.18 * visT,
      pulse: false,
    });
    setTarget(entities[companionId], pairs[p].x, -0.5, -0.5, {
      size: 1.3 * visT,
      alpha: 0.75 * visT,
      warmth: "cream",
      halo: 0.05 * visT,
      pulse: false,
    });
    decorations.push({
      kind: "entityLine",
      fromId: primaryId,
      toId: companionId,
      warmth: "cream",
      alpha: 0.5 * visT,
      width: 1,
    });
    labels.push({
      text: pairs[p].label,
      at: { x: pairs[p].x, y: -0.2, z: -0.25 },
      offsetY: 0,
      warmth: "cream",
      alpha: 0.8 * visT,
      size: 11,
    });
  }

  // Remaining entities settle to floor starfield
  parkAsAmbient(entities, 6);

  // Beat 3 — swing all the way to the right, lift into ¾ — total
  // angle change from beat 2 is ~110°, feels like the camera flew
  // around the subject.
  return {
    labels,
    decorations,
    cameraZoom: 1.0,
    cameraRotYOffset: 0.9,
    cameraTiltOffset: 0.32,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 4 — "people decide what becomes possible in a life."
// Tree: entity 0 = root, entities 1-3 = three branch people (amber),
// entities 4-11 = leaf possibilities.
// ──────────────────────────────────────────────────────────────────────

export const beat4: BeatLayoutFn = (entities, t) => {
  const root = { x: 0, y: -1.0, z: 0 };
  setTarget(entities[0], root.x, root.y, root.z, {
    size: 1.7,
    alpha: 0.95,
    warmth: "cream",
    halo: 0.18,
    pulse: false,
  });

  const mids = [
    { x: -0.7, y: -0.5, z: -0.1 },
    { x: 0.1, y: -0.5, z: 0.3 },
    { x: 0.8, y: -0.5, z: -0.25 },
  ];
  const leaves = [
    { x: -1.1, y: 0.05, z: -0.4 },
    { x: -0.4, y: 0.05, z: 0.3 },
    { x: 0.55, y: 0.05, z: 0.6 },
    { x: 1.2, y: 0.05, z: -0.5 },
  ];

  const decorations: DecoEntry[] = [];

  mids.forEach((m, i) => {
    const id = 1 + i;
    const visT = Math.min(1, Math.max(0, (t - 0.2 - i * 0.4) / 0.9));
    setTarget(entities[id], m.x, m.y, m.z, {
      size: 1.4 * visT,
      alpha: 0.9 * visT,
      warmth: "amber",
      halo: 0.2 * visT,
      pulse: false,
    });
    decorations.push({
      kind: "line",
      from: root,
      to: m,
      warmth: "amber",
      alpha: 0.55 * visT,
      width: 1,
    });
  });

  leaves.forEach((lf, i) => {
    const id = 4 + i;
    const visT = Math.min(1, Math.max(0, (t - 1.4 - i * 0.3) / 0.9));
    setTarget(entities[id], lf.x, lf.y, lf.z, {
      size: 1.0 * visT,
      alpha: 0.7 * visT,
      warmth: "cream",
      halo: 0.06 * visT,
      pulse: false,
    });
    // Connect each leaf to the nearest mid
    let nearestMidIdx = 0;
    let nearestDist = Infinity;
    mids.forEach((m, mi) => {
      const d = (m.x - lf.x) ** 2 + (m.z - lf.z) ** 2;
      if (d < nearestDist) {
        nearestDist = d;
        nearestMidIdx = mi;
      }
    });
    decorations.push({
      kind: "line",
      from: mids[nearestMidIdx],
      to: lf,
      warmth: "cream",
      alpha: 0.4 * visT,
      width: 1,
    });
  });

  parkAsAmbient(entities, 12);

  // Beat 4 — keep momentum swinging further right + pull up high.
  // The "possibilities" beat — wide, elevated, almost god-like.
  return {
    labels: [
      { text: "today", entityId: 0, offsetY: 26, warmth: "cream", alpha: 0.6 },
    ],
    decorations,
    cameraZoom: 0.9,
    cameraRotYOffset: 1.8,
    cameraTiltOffset: 0.55,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 5 — "there's a unique skill in finding them. holding them.
// reading them." Center reader + scattered field dots that glow as the
// radar sweeps over them.
// ──────────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────────
// beat5 — "so we started asking." (beat id 10)
// Physics lens: THE FIRST WAVE AFTER SILENCE. Direct continuation of
// beat 9's ember re-ignition — same hero position, same persistent
// starfield, but now the hero emits its FIRST wave of a new thought.
// A single expanding shell radiates outward — the question itself,
// taking shape and reaching into the void. The mesh, which had
// gone flat during silence, picks up its first ripple. Same
// visual language as every other Act II wave-physics beat.
// ──────────────────────────────────────────────────────────────────────
export const beat5: BeatLayoutFn = (entities, t) => {
  const center = { x: 0, y: -0.5, z: 0 };

  // Emerge factor — smoothstep over 0.9s. Slow enough that beat 10
  // rises naturally from beat 9's re-ignition state rather than
  // popping in. Hero warms up, first wave shell expands, mesh
  // starts to ripple.
  const emergeT = Math.min(1, t / 0.9);
  const emerge = emergeT * emergeT * (3 - 2 * emergeT);

  // Hero — picks up EXACTLY where beat 9's ignition phase left off.
  // Same position, same amber. Starts at ember brightness, warms
  // up smoothly. Emits a gentle wave — the first thought pushing
  // out into space.
  const heroBreath = 0.5 + 0.5 * Math.sin(t * 0.8);
  setTarget(entities[0], center.x, center.y, center.z, {
    size: 0.9 + emerge * 1.1 + heroBreath * 0.08,
    alpha: 0.35 + emerge * 0.65,
    warmth: "amber",
    halo: 0.08 + emerge * 0.32 + heroBreath * 0.04,
    pulse: true,
    emit: 0.28 * emerge,
    emitFreq: 1.8,
    emitSpeed: 1.8,
    waveColor: [232, 195, 140],
  });

  // Distant starfield — all 47 entities stay at their beat-9
  // positions (spherical shell, radius 3-5.5). This IS the
  // continuity — the same entities from silence still out there.
  // Alpha gently lifts as the new thought warms the space.
  for (let i = 1; i < entities.length; i++) {
    const s = i * 11 + 201;
    const theta = rand(s) * Math.PI * 2;
    const phi = Math.acos(2 * rand(s + 11) - 1);
    const r = 3 + rand(s + 23) * 2.5;
    const x = r * Math.sin(phi) * Math.cos(theta);
    const y = r * Math.cos(phi) * 0.35 - 0.5;
    const z = r * Math.sin(phi) * Math.sin(theta);
    setTarget(entities[i], x, y, z, {
      size: 0.45 + rand(s + 37) * 0.35,
      alpha: (0.1 + rand(s + 43) * 0.15) * (0.6 + emerge * 0.4),
      warmth: "cream",
      halo: rand(s + 53) < 0.15 ? 0.06 : 0,
      pulse: false,
      emit: 0,
    });
  }

  const decorations: DecoEntry[] = [];

  // Persistent cosmic backdrop — same starfield that's been under
  // every Act II beat. Continuity anchor across the silence boundary.
  decorations.push({
    kind: "dotCloud",
    points: ALONE_BG_DOTS,
    pixelRadius: 0.5,
    alpha: 0.3 * (0.7 + emerge * 0.3),
    warmth: "cream",
    rgb: [205, 210, 222],
    alphas: ALONE_STAR_ALPHAS,
  });

  // The first wave shell — the question reaching outward.
  // Starts at t=0.2s (letting beat 9's dying-photon shell fade
  // before a new one begins), lives through the beat, gentle
  // expansion, 1/r² falloff with a soft envelope.
  const shellT = t - 0.2;
  if (shellT > 0 && shellT < 3.0) {
    const shellR = shellT * 0.55;
    const r0 = 0.28;
    const rr = Math.max(shellR, r0);
    const inv = (r0 * r0) / (rr * rr);
    const envIn = Math.min(1, shellT / 0.5);
    const envOut = Math.max(0, 1 - Math.pow(shellT / 3.0, 1.4));
    const shellI = Math.min(0.3, inv) * envIn * envOut * emerge;
    if (shellI > 0.015) {
      decorations.push({
        kind: "wavefront",
        origin: center,
        radius: shellR,
        intensity: shellI,
        rgb: [232, 195, 140],
        thickness: 0.22,
        phaseT: shellT,
      });
    }
  }

  return {
    labels: [],
    decorations,
    // Mesh picks up the hero's first wave. Gentle — we're watching
    // the fabric of space accept a new ripple after the flatness
    // of silence. meshExtent expanded so the wave reaches peripheral
    // vision.
    meshAlphaMul: 0.45 * emerge,
    meshExtentMul: 1.3,
    // Intimate close zoom — we're WITH the hero as the first wave
    // of a new thought radiates outward. Near-eye tilt so the ripple
    // sweeps past the viewer rather than being observed from above.
    cameraZoom: 1.55,
    cameraRotYOffset: 0.12,
    cameraTiltOffset: -0.14,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 6 — "some people already have it."
// Exponential contagion: one amber hero at center → four infect in
// gen 1 → eight in gen 2 → sixteen in gen 3 → the rest in gen 4.
// Each wave spreads faster than the last. White nodes turn amber at
// the moment they're infected, then carry the skill to the next wave.
// By the end of the beat, the whole field glows.
// ──────────────────────────────────────────────────────────────────────

interface InfectionNode {
  id: number;
  parentId: number;
  x: number;
  y: number;
  z: number;
  infectTime: number;
}

const INFECTION_TREE: InfectionNode[] = buildInfectionTree();

function buildInfectionTree(): InfectionNode[] {
  const nodes: InfectionNode[] = [];

  // Gen 0 — hero
  nodes.push({
    id: 0,
    parentId: -1,
    x: 0,
    y: -0.5,
    z: 0,
    infectTime: 0,
  });

  const generations = [
    { count: 4, radiusBase: 0.55, radiusJitter: 0.1, yMin: -0.65, ySpan: 0.3, startTime: 0.7, stagger: 0.18 },
    { count: 8, radiusBase: 1.0, radiusJitter: 0.15, yMin: -0.65, ySpan: 0.45, startTime: 1.8, stagger: 0.09 },
    { count: 16, radiusBase: 1.45, radiusJitter: 0.18, yMin: -0.6, ySpan: 0.65, startTime: 2.9, stagger: 0.05 },
    { count: ENTITY_COUNT - 29, radiusBase: 1.95, radiusJitter: 0.3, yMin: -0.3, ySpan: 0.45, startTime: 3.95, stagger: 0.03 },
  ];

  let prevGenStart = 0;
  let prevGenEnd = 1;

  for (const gen of generations) {
    const genStart = nodes.length;
    for (let i = 0; i < gen.count; i++) {
      const angleSeed = nodes.length * 0.618;
      const angle = (i / gen.count) * Math.PI * 2 + angleSeed;
      const radius = gen.radiusBase + (rand(nodes.length * 7 + 3) - 0.5) * gen.radiusJitter;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius * 0.68;
      const y = gen.yMin + rand(nodes.length * 5 + 11) * gen.ySpan;

      // Find nearest parent in previous generation
      let bestParent = prevGenStart;
      let bestDist = Infinity;
      for (let p = prevGenStart; p < prevGenEnd; p++) {
        const dx = nodes[p].x - x;
        const dz = nodes[p].z - z;
        const d = dx * dx + dz * dz;
        if (d < bestDist) {
          bestDist = d;
          bestParent = p;
        }
      }

      nodes.push({
        id: nodes.length,
        parentId: bestParent,
        x,
        y,
        z,
        infectTime: gen.startTime + i * gen.stagger,
      });
    }
    prevGenStart = genStart;
    prevGenEnd = nodes.length;
  }

  return nodes;
}

export const beat6: BeatLayoutFn = (entities, t) => {
  const decorations: DecoEntry[] = [];

  for (const node of INFECTION_TREE) {
    const e = entities[node.id];
    if (!e) continue;

    const elapsed = t - node.infectTime;
    const progress = Math.max(0, Math.min(1, elapsed / 0.55));
    const isHero = node.id === 0;
    const converted = progress >= 0.82;

    // Size swells through infection, settles to a steady brightness.
    const sizeBase = isHero ? 2.4 : 0.85;
    const sizeSwell = progress * (isHero ? 0 : 0.75);

    // Alpha ramps from dim white to bright amber.
    const alpha = 0.26 + progress * 0.68;

    // Halo blooms sharply at conversion moment, then settles.
    const bloomProfile =
      progress < 0.5
        ? progress * 2
        : 1 - (progress - 0.5) * 1.4;
    const halo = isHero ? 0.48 : bloomProfile * 0.28;

    setTarget(e, node.x, node.y, node.z, {
      size: sizeBase + sizeSwell,
      alpha,
      warmth: converted ? "amber" : "cream",
      halo,
      pulse: isHero,
    });

    // Lineage line from parent — fades in during the infection bloom.
    if (node.parentId >= 0 && progress > 0.05) {
      decorations.push({
        kind: "entityLine",
        fromId: node.parentId,
        toId: node.id,
        warmth: "amber",
        alpha: 0.32 * Math.min(1, progress * 1.8),
        width: 1,
      });
    }
  }

  // Story beat 5 — "really good with people." Big swing to the far
  // opposite side from beat 4 — feels like the camera vaulted across.
  return {
    labels: [],
    decorations,
    cameraZoom: 1.1,
    cameraRotYOffset: -2.4,
    cameraTiltOffset: -0.15,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 7 — "they always know what to say. who to call. when to show up."
// Timeline axis with three amber beams at event points.
// ──────────────────────────────────────────────────────────────────────

export const beat7: BeatLayoutFn = (entities, t) => {
  const events = [
    { x: -0.9, label: "said it" },
    { x: 0.1, label: "made the call" },
    { x: 1.0, label: "showed up" },
  ];

  const axisLeft = { x: -1.4, y: -0.3, z: 0 };
  const axisRight = { x: 1.4, y: -0.3, z: 0 };

  const decorations: DecoEntry[] = [
    {
      kind: "line",
      from: axisLeft,
      to: axisRight,
      warmth: "cream",
      alpha: 0.55,
      width: 1,
    },
  ];
  const labels: LabelEntry[] = [];

  events.forEach((ev, i) => {
    const visT = Math.min(1, Math.max(0, (t - 0.2 - i * 0.6) / 0.8));
    const topId = i; // 0..2
    const baseId = 3 + i; // 3..5
    setTarget(entities[topId], ev.x, -0.85, 0, {
      size: 1.7 * visT,
      alpha: visT,
      warmth: "amber",
      halo: 0.28 * visT,
      pulse: false,
    });
    setTarget(entities[baseId], ev.x, -0.3, 0, {
      size: 1.1 * visT,
      alpha: 0.7 * visT,
      warmth: "cream",
      halo: 0,
      pulse: false,
    });
    decorations.push({
      kind: "beam",
      base: { x: ev.x, y: -0.3, z: 0 },
      top: { x: ev.x, y: -0.3 + (-0.55 * visT), z: 0 },
      warmth: "amber",
      alpha: 0.6 * visT,
    });
    if (visT > 0.9) {
      labels.push({
        text: ev.label,
        entityId: topId,
        offsetY: -16,
        warmth: "amber",
        alpha: 0.9,
      });
    }
  });

  // Axis marker dots between events
  for (let i = 6; i < 14; i++) {
    const tpos = (i - 6) / 7;
    const x = -1.4 + tpos * 2.8;
    setTarget(entities[i], x, -0.3, 0, {
      size: 0.6,
      alpha: 0.45,
      warmth: "cream",
      halo: 0,
      pulse: false,
    });
  }

  parkAsAmbient(entities, 14);

  // Story beat 6 — "who to call." Swing back to the right + lift.
  // Timeline of right moves unfolds diagonally across the floor.
  return {
    labels,
    decorations,
    cameraZoom: 1.0,
    cameraRotYOffset: 1.4,
    cameraTiltOffset: 0.38,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 8 — "until now, it couldn't be taught."
// Central core with entities arranged on expanding rings; amber, warm.
// ──────────────────────────────────────────────────────────────────────

export const beat8: BeatLayoutFn = (entities, t) => {
  setTarget(entities[0], 0, -0.15, 0, {
    size: 2.6,
    alpha: 1,
    warmth: "amber",
    halo: 0.6,
    pulse: true,
  });

  // Entities 1+ arrange on concentric rings on ground plane
  for (let i = 1; i < entities.length; i++) {
    const ringIdx = Math.floor((i - 1) / 12) + 1; // ring 1..4
    const angleIdx = (i - 1) % 12;
    const baseRadius = ringIdx * 0.42;
    const wobble = Math.sin(t * 0.6 + i * 0.3) * 0.04;
    const radius = baseRadius + wobble;
    const angle = (angleIdx / 12) * Math.PI * 2 + ringIdx * 0.2 + t * 0.08;
    setTarget(entities[i], Math.cos(angle) * radius, -0.05, Math.sin(angle) * radius, {
      size: 0.9 - ringIdx * 0.08,
      alpha: 0.7 - ringIdx * 0.1,
      warmth: "amber",
      halo: 0.08,
      pulse: false,
    });
  }

  // Expanding decorative rings
  const ringCount = 5;
  const period = 4.2;
  const decorations: DecoEntry[] = [];
  for (let i = 0; i < ringCount; i++) {
    const phase = ((t + i * (period / ringCount)) % period) / period;
    const r = 0.2 + phase * 1.8;
    decorations.push({
      kind: "ring",
      at: { x: 0, y: -0.05, z: 0 },
      radius: r,
      warmth: "amber",
      alpha: (1 - phase) * 0.55,
      width: 1.4 * (1 - phase * 0.4),
    });
  }
  // Central light column
  decorations.push({
    kind: "beam",
    base: { x: 0, y: -0.15, z: 0 },
    top: { x: 0, y: -1.4, z: 0 },
    warmth: "amber",
    alpha: 0.5,
  });

  return {
    labels: [],
    decorations,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 8 — "watch what happens to them."
// The hero becomes a magnet. Streams of amber particles flow toward
// the center from all angles and depths, each staggered, each fading
// in as they emerge from the void and fading out as they merge into
// the hero's halo. You're watching opportunities converging.
// Four expanding rings and a faint rotating beam network give the
// scene constant motion.
// ──────────────────────────────────────────────────────────────────────

export const beatWatch: BeatLayoutFn = (entities, t) => {
  // Count how many particles have already arrived at the hero.
  // Hero size and halo grow with each absorption — the magnet gets
  // more powerful as it pulls more in.
  let arrived = 0;
  for (let i = 1; i < entities.length; i++) {
    const seed = i * 7 + 3;
    const startTime = rand(seed + 41) * 3.4;
    const duration = 1.3 + rand(seed + 43) * 0.9;
    if (t >= startTime + duration) arrived++;
  }
  const absorbFactor = arrived / Math.max(1, entities.length - 1);
  const heroSize = 2.1 + absorbFactor * 3.4; // 2.1 → 5.5
  const heroHalo = 0.5 + absorbFactor * 0.5; // 0.5 → 1.0

  setTarget(entities[0], 0, -0.45, 0, {
    size: heroSize,
    alpha: 1,
    warmth: "amber",
    halo: heroHalo,
    pulse: true,
  });

  // Streaming inflow — each particle takes one trip from edge to hero.
  // Staggered start times spread arrivals across the beat.
  for (let i = 1; i < entities.length; i++) {
    const seed = i * 7 + 3;
    const startTime = rand(seed + 41) * 3.4;
    const duration = 1.3 + rand(seed + 43) * 0.9;
    const angle = rand(seed) * Math.PI * 2;
    const startRadius = 2.3 + rand(seed + 23) * 0.5;
    const yVariance = (rand(seed + 29) - 0.5) * 0.7;

    const localT = t - startTime;

    let x: number;
    let y: number;
    let z: number;
    let alpha: number;
    let size: number;

    if (localT < 0) {
      x = Math.cos(angle) * startRadius;
      z = Math.sin(angle) * startRadius * 0.7;
      y = -0.45 + yVariance;
      alpha = 0;
      size = 0.55;
    } else if (localT < duration) {
      const progress = localT / duration;
      const radius = startRadius * (1 - progress);
      x = Math.cos(angle) * radius;
      z = Math.sin(angle) * radius * 0.7;
      y = -0.45 + yVariance * (1 - progress * 0.7);

      const fadeIn = Math.min(1, localT / 0.2);
      const fadeOut = Math.min(1, (duration - localT) / 0.35);
      alpha = 0.68 * fadeIn * fadeOut;

      size = 0.55 + progress * 0.55;
    } else {
      x = 0;
      y = -0.45;
      z = 0;
      alpha = 0;
      size = 0.4;
    }

    setTarget(entities[i], x, y, z, {
      size,
      alpha,
      warmth: "amber",
      halo: 0.04,
      pulse: false,
    });
  }

  // Decorations intensify as the hero absorbs more.
  const decorations: DecoEntry[] = [];
  const ringAlphaMul = 0.6 + absorbFactor * 0.5;
  const beamAlphaMul = 0.4 + absorbFactor * 0.8;

  const ringPeriod = 3.4;
  for (let i = 0; i < 4; i++) {
    const offset = i * (ringPeriod / 4);
    const phase = ((t + offset) % ringPeriod) / ringPeriod;
    decorations.push({
      kind: "ring",
      at: { x: 0, y: -0.45, z: 0 },
      radius: 0.25 + phase * 2.0,
      warmth: "amber",
      alpha: (1 - phase) * 0.45 * ringAlphaMul,
      width: 1.0 * (1 - phase * 0.3),
    });
  }

  const beamCount = 6;
  const beamLen = 1.8;
  for (let i = 0; i < beamCount; i++) {
    const a = (i / beamCount) * Math.PI * 2 + t * 0.12;
    decorations.push({
      kind: "line",
      from: { x: 0, y: -0.45, z: 0 },
      to: {
        x: Math.cos(a) * beamLen,
        y: -0.45,
        z: Math.sin(a) * beamLen * 0.7,
      },
      warmth: "amber",
      alpha: 0.09 * beamAlphaMul,
      width: 0.7,
    });
  }

  // Story beat 8 — "life starts bending toward them." Flip to the
  // opposite side, punch in close. Anticipation framing.
  return {
    labels: [],
    decorations,
    cameraZoom: 1.2,
    cameraRotYOffset: -1.6,
    cameraTiltOffset: -0.12,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatOutcomes — "every good thing in your life."
// The harmonic cluster from beatHold — same six entities, same
// positions, same colors, same frequencies — just SWELLING. Over the
// beat every companion's emit ramps up, sizes grow, halos bloom, and
// the mesh fills with rolling harmonic waves. No cascade, no labels,
// no camera jump. Just the circle of people we saw in beatHold, now
// with their waves radiating across the entire field. The image:
// every good thing in your life is the combined wavefield of the
// people who matter. You're surrounded by it.
// ──────────────────────────────────────────────────────────────────────

export const beatOutcomes: BeatLayoutFn = (entities, t) => {
  const DUR = 2.8;
  const p = Math.min(1, t / DUR);
  // ease-in-out-cubic so the swell feels inevitable — slow build
  // continuing beatHold's stillness, middle acceleration, soft
  // landing into the staircase of job/love/life that follows.
  const ease = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;

  const rock = Math.sin(t * 0.5) * 0.05;

  interface Companion {
    angle: number;
    r: number;
    yOff: number;
    color: RGB;
    freq: number;
    baseEmit: number;
    peakEmit: number;
    baseSize: number;
    peakSize: number;
    baseHalo: number;
    peakHalo: number;
    breathRate: number;
    breathPhase: number;
  }

  // Identical positions, colors, frequencies to beatHold — nothing
  // moves. Each companion's emit/size/halo ramps from beatHold's
  // baseline up to a peak as the wavefield fills.
  const companions: ReadonlyArray<Companion> = [
    {
      angle: 0.15, r: 0.52, yOff: 0.02,
      color: WAVE_PALETTE.warmpink, freq: 3.3,
      baseEmit: 0.58, peakEmit: 0.80,
      baseSize: 1.55, peakSize: 1.74,
      baseHalo: 0.30, peakHalo: 0.40,
      breathRate: 1.05, breathPhase: 0.4,
    },
    {
      angle: Math.PI + 0.1, r: 0.50, yOff: -0.03,
      color: WAVE_PALETTE.yellow, freq: 5.5,
      baseEmit: 0.50, peakEmit: 0.72,
      baseSize: 1.45, peakSize: 1.64,
      baseHalo: 0.26, peakHalo: 0.36,
      breathRate: 0.75, breathPhase: 1.4,
    },
    {
      angle: Math.PI / 2 + 0.08, r: 0.55, yOff: 0.03,
      color: WAVE_PALETTE.green, freq: 6.6,
      baseEmit: 0.44, peakEmit: 0.66,
      baseSize: 1.40, peakSize: 1.57,
      baseHalo: 0.24, peakHalo: 0.34,
      breathRate: 1.25, breathPhase: 2.7,
    },
    {
      angle: 5 * Math.PI / 4 - 0.1, r: 0.44, yOff: 0.01,
      color: WAVE_PALETTE.rose, freq: 2.2,
      baseEmit: 0.52, peakEmit: 0.74,
      baseSize: 1.50, peakSize: 1.70,
      baseHalo: 0.28, peakHalo: 0.38,
      breathRate: 0.55, breathPhase: 3.3,
    },
    {
      angle: -Math.PI / 5, r: 0.46, yOff: -0.02,
      color: WAVE_PALETTE.purple, freq: 8.8,
      baseEmit: 0.38, peakEmit: 0.60,
      baseSize: 1.30, peakSize: 1.50,
      baseHalo: 0.22, peakHalo: 0.32,
      breathRate: 1.45, breathPhase: 4.8,
    },
  ];

  // Hero — anchor of the whole chord. Emit AND emitSpeed both grow
  // so the field swell is faster AND bigger — the first visible
  // acceleration of the promise-trio buildup.
  const heroBreath = 0.5 + 0.5 * Math.sin(t * 0.85);
  setTarget(entities[0], rock * 0.35, -0.5, 0, {
    size: 1.90 + ease * 0.22 + heroBreath * 0.12,
    alpha: 1,
    warmth: "amber",
    halo: 0.44 + ease * 0.14 + heroBreath * 0.12,
    pulse: false,
    emit: 0.68 + ease * 0.22,
    emitFreq: 4.4,
    emitSpeed: 2.6 + ease * 0.8, // 2.6 → 3.4 — wave starts rolling faster
    waveColor: WAVE_PALETTE.amber,
  });

  for (let i = 0; i < companions.length; i++) {
    const c = companions[i];
    const x = Math.cos(c.angle) * c.r + rock;
    const z = Math.sin(c.angle) * c.r * 0.78;

    const breath = 0.5 + 0.5 * Math.sin(t * c.breathRate + c.breathPhase);
    const wob = Math.sin(t * 0.6 + i * 1.3) * 0.01;

    const size = c.baseSize + ease * (c.peakSize - c.baseSize) + breath * 0.14;
    const halo = c.baseHalo + ease * (c.peakHalo - c.baseHalo) + breath * 0.12;
    const emit = c.baseEmit + ease * (c.peakEmit - c.baseEmit) + breath * 0.08;

    setTarget(entities[i + 1], x + wob, -0.5 + c.yOff, z, {
      size,
      alpha: 0.95,
      warmth: "amber",
      halo,
      pulse: false,
      emit,
      emitFreq: c.freq,
      emitSpeed: 2.2 + i * 0.12,
      waveColor: c.color,
    });
  }

  parkAsAmbient(entities, companions.length + 1);

  return {
    labels: [],
    decorations: [],
    // Bumped so the woven harmonic mesh dominates — the waves ARE
    // every good thing. They're the message.
    meshAlphaMul: 1.35,
    // Gentle widening from beatHold's 1.55 so the expanding
    // wavefield fills the frame. Engine lerps OPT_LERP-slow so the
    // pullback reads as the field growing, not the camera yanking.
    cameraZoom: 1.30,
    cameraRotYOffset: -0.05 + t * 0.02,
    cameraTiltOffset: 0.18,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 10 — "they walk into any room, and half of it's already theirs."
// Hero walks left-to-right across a literal room. Ten people scattered
// across the floor, alternating allied and neutral. As the hero's x
// position crosses each person's x, allied ones attach (flip amber,
// burst ring, warm line back to hero). Neutrals stay cream. By the
// time the hero exits through the right doorway, five amber lines
// fan back across the room — the half they've picked up on their walk.
// ──────────────────────────────────────────────────────────────────────

export const beatRoom: BeatLayoutFn = (entities, t) => {
  const roomHalfWidth = 1.35;

  // Hero walks from outside the left wall to just past the right wall.
  // Walk completes at walkEnd, then holds at the right edge for the
  // rest of the beat so the final picture can settle.
  const walkEnd = 4.2;
  const walkT = Math.min(1, t / walkEnd);
  const heroStartX = -2.1;
  const heroFinalX = 1.55;
  const heroX = heroStartX + walkT * (heroFinalX - heroStartX);

  setTarget(entities[0], heroX, -0.5, 0, {
    size: 2.4,
    alpha: 1,
    warmth: "amber",
    halo: 0.42,
    pulse: true,
  });

  // People scattered across the full width of the room, alternating
  // allied / neutral so the hero passes the two types interleaved
  // as they walk. z-values varied for 3D depth.
  interface RoomPerson {
    x: number;
    z: number;
    allied: boolean;
  }
  const roomPeople: RoomPerson[] = [
    { x: -1.1, z: -0.5, allied: true },
    { x: -0.85, z: 0.55, allied: false },
    { x: -0.45, z: -0.85, allied: true },
    { x: -0.1, z: 0.7, allied: false },
    { x: 0.15, z: -0.55, allied: true },
    { x: 0.45, z: 0.35, allied: false },
    { x: 0.65, z: -0.75, allied: true },
    { x: 0.9, z: 0.5, allied: false },
    { x: 1.05, z: -0.2, allied: true },
    { x: 0.75, z: 0.75, allied: false },
  ];

  const decorations: DecoEntry[] = [];

  // Square room floor outline
  const corners = [
    { x: -roomHalfWidth, y: 0.01, z: -roomHalfWidth },
    { x: roomHalfWidth, y: 0.01, z: -roomHalfWidth },
    { x: roomHalfWidth, y: 0.01, z: roomHalfWidth },
    { x: -roomHalfWidth, y: 0.01, z: roomHalfWidth },
  ];
  for (let i = 0; i < 4; i++) {
    decorations.push({
      kind: "line",
      from: corners[i],
      to: corners[(i + 1) % 4],
      warmth: "cream",
      alpha: 0.22,
      width: 0.75,
    });
  }

  // Left doorway markers — fade as the hero crosses in.
  const leftDoorAlpha =
    0.4 * Math.max(0, 1 - Math.max(0, (heroX + roomHalfWidth) / 0.5));
  if (leftDoorAlpha > 0.02) {
    decorations.push({
      kind: "line",
      from: { x: -roomHalfWidth - 0.28, y: 0.01, z: -0.35 },
      to: { x: -roomHalfWidth, y: 0.01, z: -0.35 },
      warmth: "amber",
      alpha: leftDoorAlpha,
      width: 0.9,
    });
    decorations.push({
      kind: "line",
      from: { x: -roomHalfWidth - 0.28, y: 0.01, z: 0.35 },
      to: { x: -roomHalfWidth, y: 0.01, z: 0.35 },
      warmth: "amber",
      alpha: leftDoorAlpha,
      width: 0.9,
    });
  }

  // Right doorway markers — fade in as the hero approaches the exit.
  const rightDoorAlpha =
    0.4 * Math.max(0, Math.min(1, (heroX - 0.6) / 0.8));
  if (rightDoorAlpha > 0.02) {
    decorations.push({
      kind: "line",
      from: { x: roomHalfWidth, y: 0.01, z: -0.35 },
      to: { x: roomHalfWidth + 0.28, y: 0.01, z: -0.35 },
      warmth: "amber",
      alpha: rightDoorAlpha,
      width: 0.9,
    });
    decorations.push({
      kind: "line",
      from: { x: roomHalfWidth, y: 0.01, z: 0.35 },
      to: { x: roomHalfWidth + 0.28, y: 0.01, z: 0.35 },
      warmth: "amber",
      alpha: rightDoorAlpha,
      width: 0.9,
    });
  }

  // For each person, compute how far the hero is "past" their x.
  // Allied people attach smoothly as the hero reaches them.
  // Small burst ring at the moment of attachment.
  roomPeople.forEach((person, i) => {
    const entityId = 1 + i;
    if (entityId >= entities.length) return;

    // attachP ramps from 0 to 1 as hero crosses from ~0.1 before the
    // person to ~0.3 past the person. Only triggers for allied.
    const passDelta = heroX - person.x;
    const attachP = person.allied
      ? Math.max(0, Math.min(1, (passDelta + 0.1) / 0.4))
      : 0;
    const aligned = attachP > 0.25;

    setTarget(entities[entityId], person.x, -0.5, person.z, {
      size: 1.0 + attachP * 0.45,
      alpha: 0.55 + attachP * 0.4,
      warmth: aligned ? "amber" : "cream",
      halo: attachP * 0.22,
      pulse: false,
    });

    if (aligned) {
      decorations.push({
        kind: "entityLine",
        fromId: 0,
        toId: entityId,
        warmth: "amber",
        alpha: 0.38 * attachP,
        width: 1,
      });

      // Brief attachment burst at the moment of lock-in
      if (attachP > 0.25 && attachP < 0.7) {
        const burstP = (attachP - 0.25) / 0.45;
        decorations.push({
          kind: "ring",
          at: { x: person.x, y: -0.5, z: person.z },
          radius: 0.08 + burstP * 0.35,
          warmth: "amber",
          alpha: (1 - burstP) * 0.55,
          width: 1,
        });
      }
    }
  });

  parkAsAmbient(entities, 11);

  // Story beat 10 — "half of it's already theirs." Big swing back
  // down from overhead. Almost behind the hero, walking-through-room
  // perspective, close and low.
  return {
    labels: [],
    decorations,
    cameraZoom: 1.25,
    cameraRotYOffset: 2.4,
    cameraTiltOffset: -0.28,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 11 — "the rest of us walk in. and nobody looks up."
// The same walk as beat 10, stripped. A cream diminished figure enters
// the same room but everything is different: no allies, no warming.
// As the hero passes each person, a short cream attempt-line extends
// toward them but never reaches — stops ~65% of the distance and
// fades. Each person repels slightly when the hero gets close (drifts
// back, dims). By the end, the hero exits alone, no trail, visibly
// worn down.
// ──────────────────────────────────────────────────────────────────────

export const beatVoid: BeatLayoutFn = (entities, t) => {
  const roomHalfWidth = 1.35;

  // Hero walks the same trajectory as beat 10, but cream and shrinking.
  const walkEnd = 4.3;
  const walkT = Math.min(1, t / walkEnd);
  const heroStartX = -2.1;
  const heroFinalX = 1.55;
  const heroX = heroStartX + walkT * (heroFinalX - heroStartX);

  // Wears down progressively — smaller, dimmer. Never pulses.
  const wearFactor = Math.min(1, t / 5.5);
  setTarget(entities[0], heroX, -0.5, 0, {
    size: 2.15 - wearFactor * 0.45,
    alpha: 0.82 - wearFactor * 0.15,
    warmth: "cream",
    halo: 0.04,
    pulse: false,
  });

  const decorations: DecoEntry[] = [];

  // Same square room outline as beat 10 for visual continuity, but a
  // touch dimmer — the room itself feels colder.
  const corners = [
    { x: -roomHalfWidth, y: 0.01, z: -roomHalfWidth },
    { x: roomHalfWidth, y: 0.01, z: -roomHalfWidth },
    { x: roomHalfWidth, y: 0.01, z: roomHalfWidth },
    { x: -roomHalfWidth, y: 0.01, z: roomHalfWidth },
  ];
  for (let i = 0; i < 4; i++) {
    decorations.push({
      kind: "line",
      from: corners[i],
      to: corners[(i + 1) % 4],
      warmth: "cream",
      alpha: 0.18,
      width: 0.75,
    });
  }

  // Left doorway — cream (not amber). Fades as hero enters.
  const leftDoorAlpha =
    0.28 * Math.max(0, 1 - Math.max(0, (heroX + roomHalfWidth) / 0.5));
  if (leftDoorAlpha > 0.02) {
    decorations.push({
      kind: "line",
      from: { x: -roomHalfWidth - 0.28, y: 0.01, z: -0.35 },
      to: { x: -roomHalfWidth, y: 0.01, z: -0.35 },
      warmth: "cream",
      alpha: leftDoorAlpha,
      width: 0.9,
    });
    decorations.push({
      kind: "line",
      from: { x: -roomHalfWidth - 0.28, y: 0.01, z: 0.35 },
      to: { x: -roomHalfWidth, y: 0.01, z: 0.35 },
      warmth: "cream",
      alpha: leftDoorAlpha,
      width: 0.9,
    });
  }

  // Right doorway — fades in as hero approaches the exit.
  const rightDoorAlpha =
    0.28 * Math.max(0, Math.min(1, (heroX - 0.6) / 0.8));
  if (rightDoorAlpha > 0.02) {
    decorations.push({
      kind: "line",
      from: { x: roomHalfWidth, y: 0.01, z: -0.35 },
      to: { x: roomHalfWidth + 0.28, y: 0.01, z: -0.35 },
      warmth: "cream",
      alpha: rightDoorAlpha,
      width: 0.9,
    });
    decorations.push({
      kind: "line",
      from: { x: roomHalfWidth, y: 0.01, z: 0.35 },
      to: { x: roomHalfWidth + 0.28, y: 0.01, z: 0.35 },
      warmth: "cream",
      alpha: rightDoorAlpha,
      width: 0.9,
    });
  }

  // Ten people scattered in the room — all cream, all reject.
  interface Person {
    baseX: number;
    baseZ: number;
  }
  const roomPeople: Person[] = [
    { baseX: -1.05, baseZ: -0.55 },
    { baseX: -0.7, baseZ: 0.5 },
    { baseX: -0.3, baseZ: -0.85 },
    { baseX: 0.0, baseZ: 0.65 },
    { baseX: 0.2, baseZ: -0.5 },
    { baseX: 0.45, baseZ: 0.4 },
    { baseX: 0.65, baseZ: -0.75 },
    { baseX: 0.9, baseZ: 0.5 },
    { baseX: 1.05, baseZ: -0.2 },
    { baseX: 0.75, baseZ: 0.78 },
  ];

  roomPeople.forEach((person, i) => {
    const entityId = 1 + i;
    if (entityId >= entities.length) return;

    // Repulsion: person drifts away from the hero when hero gets close.
    // Direction is from hero-to-person (they move in that direction).
    const dx = person.baseX - heroX;
    const dz = person.baseZ - 0;
    const dist = Math.sqrt(dx * dx + dz * dz);
    const repelZone = 0.75;
    const repelStrength = Math.max(0, 1 - dist / repelZone) * 0.18;

    const actualX =
      person.baseX + (dx / Math.max(0.1, dist)) * repelStrength;
    const actualZ =
      person.baseZ + (dz / Math.max(0.1, dist)) * repelStrength;

    // Person dims slightly when the hero is close — turning away.
    const rejectionDim = Math.max(0, 1 - dist / repelZone) * 0.12;

    setTarget(entities[entityId], actualX, -0.5, actualZ, {
      size: 1.0,
      alpha: 0.5 - rejectionDim,
      warmth: "cream",
      halo: 0,
      pulse: false,
    });

    // Failed attempt: short reach-line extends from hero toward the
    // person but only covers ~65% of the distance before fading. Never
    // connects. Each attempt blooms in and out as the hero passes.
    const passDelta = heroX - person.baseX;
    if (passDelta > -0.3 && passDelta < 0.2) {
      const attemptT = (passDelta + 0.3) / 0.5; // 0→1 across the window
      const lineReach = 0.65;
      const reachEndX = heroX + (person.baseX - heroX) * lineReach;
      const reachEndZ = 0 + (person.baseZ - 0) * lineReach;
      // sin curve: 0 → 1 → 0 over the pass, so the attempt blooms then dies.
      const lineAlpha = Math.sin(attemptT * Math.PI) * 0.3;
      if (lineAlpha > 0.02) {
        decorations.push({
          kind: "line",
          from: { x: heroX, y: -0.5, z: 0 },
          to: { x: reachEndX, y: -0.5, z: reachEndZ },
          warmth: "cream",
          alpha: lineAlpha,
          width: 0.7,
        });
      }
    }
  });

  parkAsAmbient(entities, 11);

  // Story beat 11 — "nobody looks up." Swing to the OPPOSITE side
  // from beat 10 — cold, distant, indifferent. ~140° rotation.
  return {
    labels: [],
    decorations,
    cameraZoom: 1.05,
    cameraRotYOffset: -2.2,
    cameraTiltOffset: -0.32,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 12 — "it's a different life. entirely."
// Split composition. Left half: amber hero + 15 amber satellites + a
// web of connections. Right half: cream lone person in near-emptiness,
// a few dim onlookers. Labels: "them" / "everyone else."
// ──────────────────────────────────────────────────────────────────────

export const beatComparison: BeatLayoutFn = (entities, _t) => {
  setTarget(entities[0], -1.15, -0.55, 0, {
    size: 2.8,
    alpha: 1,
    warmth: "amber",
    halo: 0.55,
    pulse: true,
  });

  const decorations: DecoEntry[] = [];

  // 15 amber satellites around left hero
  for (let i = 1; i <= 15 && i < entities.length; i++) {
    const seed = i * 7 + 3;
    const angle = (i / 15) * Math.PI * 2 + rand(seed) * 0.5;
    const radius = 0.4 + rand(seed + 11) * 0.55;
    const x = -1.15 + Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius * 0.65;
    const y = -0.5 + (rand(seed + 17) - 0.5) * 0.5;

    setTarget(entities[i], x, y, z, {
      size: 0.85 + rand(seed + 21) * 0.5,
      alpha: 0.7 + rand(seed + 29) * 0.3,
      warmth: "amber",
      halo: 0.12,
      pulse: false,
    });

    decorations.push({
      kind: "entityLine",
      fromId: 0,
      toId: i,
      warmth: "amber",
      alpha: 0.28,
      width: 1,
    });
  }

  // Right side lone cream figure
  const lonelyId = 16;
  setTarget(entities[lonelyId], 1.25, -0.45, 0, {
    size: 1.9,
    alpha: 0.72,
    warmth: "cream",
    halo: 0.04,
    pulse: false,
  });

  // A few very dim cream dots around the lonely side
  for (let i = 17; i < 22 && i < entities.length; i++) {
    const seed = i * 13 + 5;
    const angle = rand(seed) * Math.PI * 2;
    const radius = 0.55 + rand(seed + 11) * 0.5;
    const x = 1.25 + Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius * 0.65;
    const y = -0.2 + (rand(seed + 17) - 0.5) * 0.4;
    setTarget(entities[i], x, y, z, {
      size: 0.45 + rand(seed + 21) * 0.25,
      alpha: 0.12 + rand(seed + 29) * 0.08,
      warmth: "cream",
      halo: 0,
      pulse: false,
    });
  }

  parkAsAmbient(entities, 22);

  // Text is a question now — labels removed so the visual breathes.
  // Story beat 12 — "what are they seeing?" Pull back high with
  // strong overhead — the shot that transitions into Chapter 2.
  return {
    labels: [],
    decorations,
    cameraZoom: 0.82,
    cameraRotYOffset: 0.4,
    cameraTiltOffset: 0.65,
  };
};

// ══════════════════════════════════════════════════════════════════════
// CHAPTER 2 — THE PHYSICS
// ══════════════════════════════════════════════════════════════════════
// Once the viewer has asked "what are they seeing that we're not?",
// we answer with wave physics. Every scene in this chapter makes the
// invisible machinery of connection visible — concentric waves
// radiating, interfering, aligning, resonating, tuning. No math
// explained, no product shown. Just the beauty of the system.
// ══════════════════════════════════════════════════════════════════════

/**
 * Emit a wave-line between two world points. Phase advances with t
 * (speed controls travel rate). Direction ±1 flips travel. Multiple
 * stacked waves create richer interference patterns.
 */
function addWave(
  decorations: DecoEntry[],
  from: { x: number; y: number; z: number },
  to: { x: number; y: number; z: number },
  t: number,
  opts: {
    speed?: number;
    frequency?: number;
    amplitude?: number;
    direction?: 1 | -1;
    warmth?: Warmth;
    alpha?: number;
    width?: number;
    noise?: number;
    envelope?: boolean;
    samples?: number;
  } = {}
): void {
  const speed = opts.speed ?? 3.2;
  const direction = opts.direction ?? 1;
  decorations.push({
    kind: "waveLine",
    from,
    to,
    phase: t * speed * direction,
    frequency: opts.frequency ?? 4,
    amplitude: opts.amplitude ?? 0.14,
    noise: opts.noise ?? 0,
    envelope: opts.envelope !== false,
    samples: opts.samples ?? 40,
    warmth: opts.warmth ?? "cream",
    alpha: opts.alpha ?? 0.55,
    width: opts.width ?? 1.1,
  });
}

// ──────────────────────────────────────────────────────────────────────
// Beat 13 — "when two people meet, something travels between them."
// Two figures face each other. Both emit waves into the ground plane
// with matching frequency. The whole mesh ripples and the waves
// interfere in the space between them.
// ──────────────────────────────────────────────────────────────────────

export const beatMeet: BeatLayoutFn = (entities, t) => {
  setTarget(entities[0], -0.9, -0.5, 0, {
    size: 2.2,
    alpha: 1,
    warmth: "cream",
    halo: 0.2,
    pulse: false,
    emit: 0.7,
    emitFreq: 5,
    emitSpeed: 3.5,
    waveColor: WAVE_PALETTE.amber,
  });
  setTarget(entities[1], 0.9, -0.5, 0, {
    size: 2.2,
    alpha: 1,
    warmth: "cream",
    halo: 0.2,
    pulse: false,
    emit: 0.7,
    emitFreq: 5,
    emitSpeed: 3.5,
    waveColor: WAVE_PALETTE.cyan,
  });
  parkAsAmbient(entities, 2);
  void t;
  // Beat 13 — close, eye-level, head-on. The two face each other,
  // we sit between them at their altitude.
  return {
    labels: [],
    decorations: [],
    cameraZoom: 1.3,
    cameraRotYOffset: 0.0,
    cameraTiltOffset: -0.4,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 14 — "something goes out. something comes back."
// Two counter-traveling waves between the same two figures — one
// moving left→right, one moving right→left. The continuous exchange
// between people, visible as two opposing undulations superimposed.
// ──────────────────────────────────────────────────────────────────────

export const beatExchange: BeatLayoutFn = (entities, t) => {
  const oscillation = Math.sin(t * 1.4);
  const leftAmp = 0.5 + oscillation * 0.3;
  const rightAmp = 0.5 - oscillation * 0.3;

  setTarget(entities[0], -0.9, -0.5, 0, {
    size: 2.2,
    alpha: 1,
    warmth: "cream",
    halo: 0.22,
    pulse: false,
    emit: leftAmp,
    emitFreq: 4.5,
    emitSpeed: 3.5,
    waveColor: WAVE_PALETTE.amber,
  });
  setTarget(entities[1], 0.9, -0.5, 0, {
    size: 2.2,
    alpha: 1,
    warmth: "cream",
    halo: 0.22,
    pulse: false,
    emit: rightAmp,
    emitFreq: 4.5,
    emitSpeed: 3.5,
    waveColor: WAVE_PALETTE.cyan,
  });
  parkAsAmbient(entities, 2);
  // Beat 14 — big swing to the right, close in. Reveals the
  // exchange as two distinct flows.
  return {
    labels: [],
    decorations: [],
    cameraZoom: 1.25,
    cameraRotYOffset: 1.8,
    cameraTiltOffset: -0.1,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 15 — "the repair."
// Inherits drift's end state: two emitters at ±0.9, partner at 6.0 Hz
// (decoupled). Over 1.65s, the partner's wavelengths LOCK BACK IN to
// the hero's 4.5 Hz — the interference pattern on the mesh resolves
// from dissonant beating into clean constructive harmony. Final third
// adds a lock-in emit bump (the "click" moment) and colors warm from
// cool cyan back to amber. emitFreq is set directly to bypass the
// engine's slow freq lerp so the alignment is VISIBLE across the
// short beat duration. Camera holds the same angle as drift — one
// continuous shot watching the rhythm repair itself.
// ──────────────────────────────────────────────────────────────────────

export const beatAlign: BeatLayoutFn = (entities, t) => {
  const DUR = 1.65;
  const p = Math.min(1, t / DUR);
  // smoothstep — gentle in, settling resolve at the end.
  const ease = p * p * (3 - 2 * p);

  // Partner frequency 6.0 → 4.5 (matching hero). Direct assignment
  // overrides the engine's gentle lerp so viewers SEE the wavelengths
  // settle into matched rhythm within 1.65s.
  const partnerFreq = 6.0 - ease * 1.5;

  // Lock-in flash — in the final 35% of the beat, emit amplitude
  // bumps on both orbs. Two matched, in-phase sources firing harder
  // together = one unified constructive swell: the repair MOMENT.
  const lockProgress = Math.max(0, (p - 0.55) / 0.45);
  const lockFlash = Math.sin(lockProgress * Math.PI) * 0.3;
  const emitLevel = 0.72 + lockFlash;

  // Hero holds amber throughout. Partner warms from cool cyan → amber
  // as the frequencies snap together.
  const [cR, cG, cB] = WAVE_PALETTE.cyan;
  const [aR, aG, aB] = WAVE_PALETTE.amber;
  const partnerColor: RGB = [
    cR + (aR - cR) * ease,
    cG + (aG - cG) * ease,
    cB + (aB - cB) * ease,
  ];

  setTarget(entities[0], -0.9, -0.5, 0, {
    size: 2.2 + ease * 0.15,
    alpha: 1,
    warmth: "amber",
    halo: 0.2 + ease * 0.15,
    pulse: lockProgress > 0.3,
    emit: emitLevel,
    emitFreq: 4.5,
    emitSpeed: 3.0,
    waveColor: WAVE_PALETTE.amber,
  });
  setTarget(entities[1], 0.9, -0.5, 0, {
    size: 2.2 + ease * 0.15,
    alpha: 1,
    warmth: ease > 0.5 ? "amber" : "cream",
    halo: 0.2 + ease * 0.15,
    pulse: lockProgress > 0.3,
    emit: emitLevel,
    emitFreq: partnerFreq,
    emitSpeed: 3.0,
    waveColor: partnerColor,
  });

  // Bypass the engine's freq lerp — set directly so the repair reads
  // in 1.65s instead of ~60% muted. Target already matches so engine
  // won't push it back.
  entities[1].emitFreq = partnerFreq;

  parkAsAmbient(entities, 2);
  // Hold the drift/quiet camera angle — one continuous shot watching
  // rhythms break, rebuild, rest.
  return {
    labels: [],
    decorations: [],
    cameraZoom: 1.4,
    cameraRotYOffset: 0,
    cameraTiltOffset: -0.32,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 14 — "the drift."
// Physics of rhythmic decoupling. Two emitters HOLD their positions
// at ±0.9 (inherited from beat 13's exchange), but the partner's
// wavelengths shorten: frequency 4.5 → 6.0 over 1.5s. The viewer
// sees the unified interference pattern between them visibly
// fragment — the hero's waves stay broad and steady, the partner's
// get shorter and faster. Same strength, same positions — only the
// RHYTHM drifts. This is what "the drift" actually is: not distance,
// not fading, but going out of sync. emitFreq set directly so the
// decoupling is legible in the short 1.5s beat. Partner colors lerp
// amber → cyan to signal the coolness of the drift.
// ──────────────────────────────────────────────────────────────────────

export const beatScatter: BeatLayoutFn = (entities, t) => {
  const DUR = 1.5;
  const p = Math.min(1, t / DUR);
  // smoothstep — the drift comes in gently, settles at the end.
  const ease = p * p * (3 - 2 * p);

  // Partner frequency 4.5 → 6.0. Direct assignment below overrides
  // the engine's slow freq lerp so the decoupling is visible.
  const partnerFreq = 4.5 + ease * 1.5;

  // Hero — steady amber anchor, holds at -0.9. Its wavelengths and
  // amplitude DON'T change. It's what the drift is drifting FROM.
  setTarget(entities[0], -0.9, -0.5, 0, {
    size: 2.2,
    alpha: 1,
    warmth: "amber",
    halo: 0.22,
    pulse: false,
    emit: 0.72,
    emitFreq: 4.5,
    emitSpeed: 3.0,
    waveColor: WAVE_PALETTE.amber,
  });

  // Partner — holds position at +0.9 but its rhythm drifts away.
  // Amber → cool cyan lerp, amplitude holds (this isn't fading,
  // it's decoupling).
  const [aR, aG, aB] = WAVE_PALETTE.amber;
  const [cR, cG, cB] = WAVE_PALETTE.cyan;
  const driftColor: RGB = [
    aR + (cR - aR) * ease,
    aG + (cG - aG) * ease,
    aB + (cB - aB) * ease,
  ];

  setTarget(entities[1], 0.9, -0.5, 0, {
    size: 2.2,
    alpha: 1,
    warmth: ease > 0.5 ? "cream" : "amber",
    halo: 0.22,
    pulse: false,
    emit: 0.72,
    emitFreq: partnerFreq,
    emitSpeed: 3.0,
    waveColor: driftColor,
  });

  // Direct freq override so the 1.5-unit drift plays out within 1.5s
  // instead of being muted by the engine's slow lerp (FREQ_LERP=0.01).
  entities[1].emitFreq = partnerFreq;

  parkAsAmbient(entities, 2);
  // Eye-level, centered. Camera holds this angle through beats 14/15/16
  // so the drift → repair → quiet story reads as one continuous shot.
  return {
    labels: [],
    decorations: [],
    cameraZoom: 1.4,
    cameraRotYOffset: 0,
    cameraTiltOffset: -0.32,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 17 — "every friendship. every partnership. every near-miss."
// Wide view. Six pairs of figures scattered across the field, each
// with their own waveform between them. Variety: different frequencies,
// amplitudes, some amber-resonant, some cream-scattering. The world
// is alive with these interactions.
// ──────────────────────────────────────────────────────────────────────

export const beatAllPairs: BeatLayoutFn = (entities, t) => {
  // Beat 17 — "some find their people. others never do."
  // Four clusters pushed to four corners, each a distinct color family.
  // Between them: a few lonely outliers, faint and stranded. The gulf
  // between the clusters and the outliers is the whole visual.
  interface Member {
    id: number;
    x: number;
    z: number;
    color: RGB;
    freq: number;
    speed: number;
    emit: number;
    resonant: boolean;
  }
  const members: Member[] = [
    // Cluster A — warm, back-left
    { id: 0, x: -1.35, z: -0.85, color: WAVE_PALETTE.amber, freq: 5, speed: 3, emit: 0.6, resonant: true },
    { id: 1, x: -1.15, z: -0.6, color: WAVE_PALETTE.orange, freq: 5, speed: 3, emit: 0.55, resonant: true },
    { id: 2, x: -1.5, z: -0.55, color: WAVE_PALETTE.yellow, freq: 5, speed: 3, emit: 0.5, resonant: true },
    // Cluster B — cool, back-right
    { id: 3, x: 1.35, z: -0.85, color: WAVE_PALETTE.cyan, freq: 4.5, speed: 3.2, emit: 0.6, resonant: true },
    { id: 4, x: 1.15, z: -0.6, color: WAVE_PALETTE.teal, freq: 4.5, speed: 3.2, emit: 0.55, resonant: true },
    { id: 5, x: 1.5, z: -0.55, color: WAVE_PALETTE.blue, freq: 4.5, speed: 3.2, emit: 0.5, resonant: true },
    // Cluster C — pink, front-left
    { id: 6, x: -1.35, z: 0.9, color: WAVE_PALETTE.rose, freq: 5.5, speed: 3, emit: 0.6, resonant: true },
    { id: 7, x: -1.15, z: 0.65, color: WAVE_PALETTE.magenta, freq: 5.5, speed: 3, emit: 0.55, resonant: true },
    { id: 8, x: -1.5, z: 0.6, color: WAVE_PALETTE.warmpink, freq: 5.5, speed: 3, emit: 0.5, resonant: true },
    // Cluster D — green/purple, front-right
    { id: 9, x: 1.35, z: 0.9, color: WAVE_PALETTE.green, freq: 4.8, speed: 3.1, emit: 0.6, resonant: true },
    { id: 10, x: 1.15, z: 0.65, color: WAVE_PALETTE.purple, freq: 4.8, speed: 3.1, emit: 0.55, resonant: true },
    { id: 11, x: 1.5, z: 0.6, color: WAVE_PALETTE.teal, freq: 4.8, speed: 3.1, emit: 0.5, resonant: true },
    // Outliers — stranded in the middle gap, dim, not emitting
    { id: 12, x: -0.2, z: 0, color: WAVE_PALETTE.cream, freq: 6.5, speed: 4.2, emit: 0.04, resonant: false },
    { id: 13, x: 0.25, z: -0.15, color: WAVE_PALETTE.cream, freq: 3.5, speed: 2.6, emit: 0.04, resonant: false },
    { id: 14, x: 0.0, z: 0.2, color: WAVE_PALETTE.cream, freq: 7.2, speed: 4.5, emit: 0.04, resonant: false },
  ];

  for (const m of members) {
    setTarget(entities[m.id], m.x, -0.5, m.z, {
      size: m.resonant ? 1.4 : 0.85,
      alpha: m.resonant ? 0.9 : 0.35,
      warmth: m.resonant ? "amber" : "cream",
      halo: m.resonant ? 0.18 : 0.02,
      pulse: false,
      emit: m.emit,
      emitFreq: m.freq,
      emitSpeed: m.speed,
      waveColor: m.color,
    });
  }

  parkAsAmbient(entities, 15);
  void t;
  // Beat 17 — pure top-down survey. Four corner clusters read
  // cleanest when you see the whole floor at once.
  return {
    labels: [],
    decorations: [],
    cameraZoom: 0.95,
    cameraRotYOffset: 0.1,
    cameraTiltOffset: 0.78,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 18 — "most of us have been moving through it blind."
// One figure at center surrounded by pairs of other figures with
// visible waves between them. The central figure has no waves —
// closed off, unable to participate. The viewer sees the machinery
// they themselves have been missing.
// ──────────────────────────────────────────────────────────────────────

interface BlindImpact {
  /** Ripple center — offset toward the emitter so the patch blooms
   *  on the side of the hero where the wave came from. */
  x: number;
  z: number;
  startT: number;
  emitterId: number;
}
let blindImpacts: BlindImpact[] = [];
let blindHeroX = -1.5;
let blindHeroZ = 0;
let blindHeroVX = 0.6;
let blindHeroVZ = 0.2;
let blindPrevT = -1;
/** Cooldown per emitter so a single wave crest doesn't spam the
 *  pool with redundant ripples. */
const blindLastImpactAt: Record<number, number> = {};

export const beatBlind: BeatLayoutFn = (entities, t) => {
  // Beat 18 — "moving through it blind." Waves are invisible. The
  // hero ping-pongs through an invisible interference field: each
  // wave shoves them in a different direction, and at each shove a
  // 3D ripple of that wave's color blooms where it hit. The hero
  // never stops getting bumped — every color keeps taking turns.

  // Reset on beat restart.
  if (t < blindPrevT || t < 0.05) {
    blindImpacts = [];
    blindHeroX = -1.5;
    blindHeroZ = 0;
    blindHeroVX = 0.6;
    blindHeroVZ = 0.2;
    for (const k of Object.keys(blindLastImpactAt)) delete blindLastImpactAt[Number(k)];
  }
  const dt = Math.max(0, Math.min(0.05, t - blindPrevT));
  blindPrevT = t;
  const y = -0.5;

  // Dense invisible emitter field — more sources, spread evenly so
  // the hero never escapes into a dead zone. Distinct colors &
  // frequencies so the ripple color keeps changing as the hero
  // bounces.
  interface Em {
    id: number;
    x: number;
    z: number;
    freq: number;
    color: RGB;
  }
  const ems: Em[] = [
    { id: 1, x: -1.3, z: -0.95, freq: 4.2, color: WAVE_PALETTE.amber },
    { id: 2, x: -0.3, z: -1.1, freq: 5.2, color: WAVE_PALETTE.cyan },
    { id: 3, x: 0.7, z: -1.05, freq: 4.0, color: WAVE_PALETTE.warmpink },
    { id: 4, x: 1.5, z: -0.6, freq: 5.8, color: WAVE_PALETTE.yellow },
    { id: 5, x: 1.5, z: 0.35, freq: 4.5, color: WAVE_PALETTE.purple },
    { id: 6, x: 0.55, z: 1.0, freq: 5.0, color: WAVE_PALETTE.green },
    { id: 7, x: -0.5, z: 1.05, freq: 4.3, color: WAVE_PALETTE.rose },
    { id: 8, x: -1.4, z: 0.45, freq: 5.5, color: WAVE_PALETTE.blue },
    { id: 9, x: -0.2, z: 0.1, freq: 4.8, color: WAVE_PALETTE.magenta },
    { id: 10, x: 0.35, z: -0.15, freq: 5.3, color: WAVE_PALETTE.teal },
  ];
  for (const em of ems) {
    setTarget(entities[em.id], em.x, y, em.z, {
      size: 1.0, alpha: 0, warmth: "cream", halo: 0, pulse: false,
      emit: 0.6, emitFreq: em.freq, emitSpeed: 3, waveColor: em.color,
    });
  }

  // Ping-pong physics for the hero. Sample wave gradient at current
  // hero position, treat -grad as accel (pushed away from crests).
  // Integrate velocity, bounce off the box walls.
  const step = 0.15;
  const h_xp = sampleWaveHeight(entities, blindHeroX + step, blindHeroZ);
  const h_xm = sampleWaveHeight(entities, blindHeroX - step, blindHeroZ);
  const h_zp = sampleWaveHeight(entities, blindHeroX, blindHeroZ + step);
  const h_zm = sampleWaveHeight(entities, blindHeroX, blindHeroZ - step);
  const gradX = (h_xp - h_xm) / (2 * step);
  const gradZ = (h_zp - h_zm) / (2 * step);

  // Settle phase — over the last 2.5s of the beat, quiet the
  // ping-pong and guide the hero to center so beat 19 (lone orange
  // hero, centered) doesn't snap. Force drops off, velocity damps
  // out, and the position is pulled toward (0, 0).
  const SETTLE_START = 8.0;
  const SETTLE_DUR = 2.5;
  const rawSettle = Math.max(0, Math.min(1, (t - SETTLE_START) / SETTLE_DUR));
  const settle = rawSettle * rawSettle * (3 - 2 * rawSettle); // smoothstep

  const FORCE = 4.5 * (1 - settle);
  blindHeroVX += -gradX * FORCE * dt;
  blindHeroVZ += -gradZ * FORCE * dt;

  // Drag grows as we settle — velocity trails off to zero.
  const drag = 0.985 - settle * 0.06; // 0.985 → 0.925
  blindHeroVX *= drag;
  blindHeroVZ *= drag;
  const speed = Math.sqrt(blindHeroVX * blindHeroVX + blindHeroVZ * blindHeroVZ);
  const MAX_SPEED = 1.6 * (1 - settle * 0.6);
  if (speed > MAX_SPEED) {
    blindHeroVX = (blindHeroVX / speed) * MAX_SPEED;
    blindHeroVZ = (blindHeroVZ / speed) * MAX_SPEED;
  }

  blindHeroX += blindHeroVX * dt;
  blindHeroZ += blindHeroVZ * dt;

  // Wall bounce — slowly shrink bounds during settle so the hero
  // can't still be against a wall when it's time to center.
  const BOUND_X = 1.7 * (1 - settle * 0.6);
  const BOUND_Z = 1.15 * (1 - settle * 0.6);
  if (blindHeroX > BOUND_X) { blindHeroX = BOUND_X; blindHeroVX = -Math.abs(blindHeroVX); }
  if (blindHeroX < -BOUND_X) { blindHeroX = -BOUND_X; blindHeroVX = Math.abs(blindHeroVX); }
  if (blindHeroZ > BOUND_Z) { blindHeroZ = BOUND_Z; blindHeroVZ = -Math.abs(blindHeroVZ); }
  if (blindHeroZ < -BOUND_Z) { blindHeroZ = -BOUND_Z; blindHeroVZ = Math.abs(blindHeroVZ); }

  // Final pull toward center — kicks in during settle. Position is
  // blended toward (0, 0) by settle^2 so it stays natural early and
  // becomes dominant at the end.
  const centerPull = settle * settle;
  const targetX = blindHeroX + (0 - blindHeroX) * centerPull;
  const targetZ = blindHeroZ + (0 - blindHeroZ) * centerPull;

  setTarget(entities[0], targetX, y, targetZ, {
    size: 2.0, alpha: 0.92, warmth: "cream", halo: 0.15,
    pulse: false, emit: 0, waveColor: WAVE_PALETTE.cream,
  });

  // Impact detection — per emitter, fires when that emitter's
  // contribution at the hero's position peaks. Cooldown keeps one
  // wavefront from re-triggering. Since emitters are always different
  // distances from the hero, at least one is usually near its crest,
  // so ripples overlap continuously.
  const IMPACT_COOLDOWN = 0.35;
  const IMPACT_THRESHOLD = 0.28;
  for (const em of ems) {
    const e = entities[em.id];
    if (!e) continue;
    const dx = blindHeroX - e.x;
    const dz = blindHeroZ - e.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    const decay = Math.exp(-dist * 0.9);
    const wave = Math.sin(dist * e.emitFreq - e.phase);
    const contrib = e.emit * decay * Math.max(0, wave);
    if (contrib < IMPACT_THRESHOLD) continue;
    const last = blindLastImpactAt[em.id] ?? -10;
    if (t - last < IMPACT_COOLDOWN) continue;
    // Place the ripple between hero and the emitter — the wave hit
    // that side of the hero, so the bloom should be *between* them.
    const dirLen = Math.max(0.001, dist);
    const dirX = -dx / dirLen; // toward emitter from hero
    const dirZ = -dz / dirLen;
    const OFFSET = 0.22;
    blindImpacts.push({
      x: blindHeroX + dirX * OFFSET,
      z: blindHeroZ + dirZ * OFFSET,
      startT: t,
      emitterId: em.id,
    });
    blindLastImpactAt[em.id] = t;
  }

  // Render live impacts — each patch reveals THAT emitter's real
  // traveling wave briefly, then fades.
  const LIFETIME = 1.0;
  blindImpacts = blindImpacts.filter((im) => t - im.startT < LIFETIME);
  const decorations: DecoEntry[] = [];
  for (const im of blindImpacts) {
    const emitter = entities[im.emitterId];
    if (!emitter) continue;
    const age = t - im.startT;
    const p = age / LIFETIME;
    const fade =
      p < 0.12 ? p / 0.12 : 1 - Math.pow((p - 0.12) / 0.88, 1.4);
    const radius = 0.32 + p * 0.35;
    decorations.push({
      kind: "wavePatch",
      at: { x: im.x, z: im.z },
      radius,
      emitterX: emitter.x,
      emitterZ: emitter.z,
      frequency: emitter.emitFreq,
      phase: emitter.phase,
      amplitude: 0.45,
      rgb: [emitter.waveR, emitter.waveG, emitter.waveB] as const,
      alpha: Math.max(0, fade) * 0.9,
    });
  }

  parkAsAmbient(entities, 11);
  // Beat 18 — close, ground-level, big angle swing. The hero
  // ricochets sideways; the camera gives a dramatic side-on view.
  return {
    labels: [],
    decorations,
    meshAlphaMul: 0,
    cameraZoom: 1.25,
    cameraRotYOffset: 1.0,
    cameraTiltOffset: -0.42,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 19 — "but once you feel it, you can shape it."
// The central figure's perception activates over ~1.5s. Halo brightens
// cream → amber. Then — crucially — waves begin emanating from the
// figure to four surrounding points, visibly shaped and intentional.
// ──────────────────────────────────────────────────────────────────────

export const beatShape: BeatLayoutFn = (entities, t) => {
  // Beat 19 — "but once you feel it, you can shape it."
  // Just the orange hero. Alone in the frame. Their emission ramps
  // from nothing to a strong, shaped wave — the first moment anyone
  // is the one making the field, not just standing in it.
  const awakeP = Math.min(1, t / 2.0);
  const shapeP = Math.max(0, Math.min(1, (t - 2.0) / 3.0));

  setTarget(entities[0], 0, -0.5, 0, {
    size: 2.6 + shapeP * 0.4,
    alpha: 1,
    warmth: "amber",
    halo: 0.2 + awakeP * 0.3 + shapeP * 0.4,
    pulse: shapeP > 0.3,
    emit: awakeP * 0.5 + shapeP * 0.5,
    emitFreq: 4.5,
    emitSpeed: 3.2,
    waveColor: WAVE_PALETTE.orange,
  });

  parkAsAmbient(entities, 1);
  // Beat 19 — closest shot of the sequence. Hero ignites alone,
  // camera punches in intimate, low. This is the empowerment beat.
  return {
    labels: [],
    decorations: [],
    cameraZoom: 1.4,
    cameraRotYOffset: -0.6,
    cameraTiltOffset: -0.18,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 20 — "anyone can. it's been under your skin the whole time."
// Field of twelve figures. Each one "wakes" in sequence and starts
// shaping waves to its nearest neighbor. By the end, the whole field
// is a mesh of amber wave lines connecting everyone to everyone.
// ──────────────────────────────────────────────────────────────────────

/** Unsigned spherical wave strength at a unit direction — same
 *  formula as the globe renderer's sampler but here we only need
 *  the magnitude so entities can brighten when crests reach them. */
function sphereWaveStrengthLocal(
  ux: number, uy: number, uz: number,
  center: { x: number; y: number; z: number },
  entities: Entity[]
): number {
  let s = 0;
  for (const e of entities) {
    if (e.emit < 0.005) continue;
    const edx = e.x - center.x;
    const edy = e.y - center.y;
    const edz = e.z - center.z;
    const elen = Math.sqrt(edx * edx + edy * edy + edz * edz);
    if (elen < 0.001) continue;
    const inv = 1 / elen;
    const eux = edx * inv;
    const euy = edy * inv;
    const euz = edz * inv;
    const cosA = Math.max(-1, Math.min(1, ux * eux + uy * euy + uz * euz));
    const angDist = Math.acos(cosA);
    const decay = Math.exp(-angDist * 0.9);
    const wave = Math.sin(angDist * e.emitFreq - e.phase);
    s += e.emit * decay * Math.abs(wave);
  }
  return s;
}


// ──────────────────────────────────────────────────────────────────────
// Beat 21 — "once you can read a room, the right rooms find you."
// Hero at center emitting a strong wave signature. Distant figures
// initially out of sync. As the beat progresses, their positions
// shift toward the hero along their wave connection — pulled in by
// the resonance. Wave lines visibly tune to match.
// ──────────────────────────────────────────────────────────────────────

export const beatFound: BeatLayoutFn = (entities, t) => {
  // Beat 44 — "you just need to know how to reach them."
  // Second-to-last scene. The rooms pull inward along the hero's
  // wavefront and settle into the final ring. Previously tuned to
  // complete at t=9.0 (full-scene-hold) which meant the actual beat
  // (now 4.8s) cut AT ~50% of the motion — the viewer saw the pull
  // start but never the satisfying resolution, making the scene
  // feel rushed/unresolved. Retuned to complete at t=3.6 so the
  // ring IS formed by the 4-second mark, then holds for the last
  // ~800ms under the settling voice. Motion resolves visually
  // before the music finishes fading to silence, landing the
  // viewer in a completed, quiet frame.
  const pullP = Math.min(1, t / 3.6);
  const eased =
    pullP < 0.5 ? 2 * pullP * pullP : 1 - Math.pow(-2 * pullP + 2, 2) / 2;

  // Hero — the one wave in the room. Long wavelength (low freq) for
  // a tsunami feel. Emit surges past normal so the swell is big.
  setTarget(entities[0], 0, -0.55, 0, {
    size: 2.6 + pullP * 0.5,
    alpha: 1,
    warmth: "amber",
    halo: 0.5 + pullP * 0.4,
    pulse: true,
    emit: 1.1 + pullP * 0.5,
    emitFreq: 3.2, // longer wavelength — reads as a swell, not a ripple
    emitSpeed: 2.6,
    waveColor: WAVE_PALETTE.orange,
  });

  // Same rooms as beat 20, same starting positions. All emission
  // drops to zero so the hero's wave owns the mesh. Colors rapidly
  // tune to orange — one unified color, one unified wave.
  interface Room {
    cx: number;
    cz: number;
  }
  const rooms: Room[] = [
    { cx: -3.4, cz: -2.6 },
    { cx: 3.4, cz: -2.6 },
    { cx: -3.4, cz: 2.6 },
    { cx: 3.4, cz: 2.6 },
    { cx: -2.2, cz: -3.6 },
    { cx: 2.2, cz: -3.6 },
    { cx: -2.2, cz: 3.6 },
    { cx: 2.2, cz: 3.6 },
    { cx: -4.3, cz: 0 },
    { cx: 4.3, cz: 0 },
    { cx: 0, cz: -4.3 },
    { cx: 0, cz: 4.3 },
    { cx: -4.5, cz: -3.8 },
    { cx: 4.5, cz: 3.8 },
    { cx: -4.5, cz: 3.8 },
    { cx: 4.5, cz: -3.8 },
  ];

  let idOffset = 1;
  rooms.forEach((r, ri) => {
    // Final ring target — evenly spaced around the hero.
    const angle = (ri / rooms.length) * Math.PI * 2;
    const ringR = 1.1;
    const finalX = Math.cos(angle) * ringR;
    const finalZ = Math.sin(angle) * ringR;

    // Outer rooms start moving later — the pull sweeps inward like
    // a tide reaching the far shores last.
    const dist = Math.sqrt(r.cx * r.cx + r.cz * r.cz);
    const startOffset = Math.min(0.55, dist * 0.08);
    const myEased = Math.max(0, Math.min(1,
      (eased - startOffset) / (1 - startOffset)
    ));
    const curCX = r.cx + (finalX - r.cx) * myEased;
    const curCZ = r.cz + (finalZ - r.cz) * myEased;

    const offsets: Array<{ dx: number; dz: number }> = [
      { dx: 0, dz: 0 },
      { dx: 0.16, dz: 0.14 },
      { dx: -0.14, dz: 0.16 },
    ];
    offsets.forEach((o, k) => {
      const eid = idOffset + k;
      if (eid >= entities.length) return;
      setTarget(entities[eid], curCX + o.dx, -0.5, curCZ + o.dz, {
        size: 0.85 + myEased * 0.35,
        alpha: 0.55 + myEased * 0.4,
        warmth: "amber",
        halo: 0.06 + myEased * 0.18,
        pulse: false,
        // Clusters do NOT emit — only the hero does. They ride the
        // hero's wave (engine's waveHeightAt picks up the hero and
        // offsets their rendered y to bob on it).
        emit: 0,
        emitFreq: 3.2,
        emitSpeed: 2.6,
        waveColor: WAVE_PALETTE.orange,
      });
    });
    idOffset += offsets.length;
  });

  parkAsAmbient(entities, idOffset);
  // Beat 21 — hold god view but rotate — camera slides around as
  // the tsunami pulls everything in. Keeps the shot active.
  return {
    labels: [],
    decorations: [],
    cameraZoom: 0.42,
    meshExtentMul: 1 / 0.42,
    cameraRotYOffset: -0.8,
    cameraTiltOffset: 0.9,
  };
};

// ──────────────────────────────────────────────────────────────────────
// Beat 22 — "you were never supposed to walk alone."
// The warm close: hero at center, eight supporters appearing around
// them in sequence. Each one connects to the hero via a clean amber
// wave line. Waves between all pairs create a humming web of
// resonance. Holds in the final 2 seconds.
// ──────────────────────────────────────────────────────────────────────

export const beatNotAlone: BeatLayoutFn = (entities, t) => {
  // Beat 22 — "learn how to feel it." The quiet brand beat.
  // Phase 1 (0–3s): hero breathes, ring holds. Cue text visible.
  // Phase 2 (3–4.5s): scene fades to black — entities dim, mesh
  //   fades out. Leaves a clean canvas.
  // Phase 3 (3.8s+): BrandReveal DOM overlay (suron + 5 services)
  //   fades in on top of nothing.
  const zoom = 0.6;
  const tiltOffset = 0.95;
  const rotOffset = 0;

  // Scene-out fade from t=3 to t=4.5.
  const FADE_START = 3.0;
  const FADE_DUR = 1.5;
  const rawFade = Math.max(0, Math.min(1, (t - FADE_START) / FADE_DUR));
  const sceneAlive = 1 - rawFade * rawFade * (3 - 2 * rawFade); // 1 → 0

  const breath = 0.5 + 0.5 * Math.sin(t * 0.45);

  setTarget(entities[0], 0, -0.55, 0, {
    size: (2.4 + breath * 0.25) * sceneAlive,
    alpha: sceneAlive,
    warmth: "amber",
    halo: (0.45 + breath * 0.2) * sceneAlive,
    pulse: sceneAlive > 0.5,
    emit: (0.9 + breath * 0.15) * sceneAlive,
    emitFreq: 3.6,
    emitSpeed: 2.4,
    waveColor: WAVE_PALETTE.orange,
  });

  const seats = 8;
  const ringR = 1.25;
  for (let i = 0; i < seats; i++) {
    const eid = 1 + i;
    if (eid >= entities.length) break;
    const angle = (i / seats) * Math.PI * 2;
    const x = Math.cos(angle) * ringR;
    const z = Math.sin(angle) * ringR;
    setTarget(entities[eid], x, -0.5, z, {
      size: (1.25 + breath * 0.12) * sceneAlive,
      alpha: 0.9 * sceneAlive,
      warmth: "amber",
      halo: (0.15 + breath * 0.1) * sceneAlive,
      pulse: false,
      emit: 0,
      emitFreq: 3.6,
      emitSpeed: 2.4,
      waveColor: WAVE_PALETTE.orange,
    });
  }

  parkAsAmbient(entities, 1 + seats);
  return {
    labels: [],
    decorations: [],
    cameraZoom: zoom,
    meshExtentMul: 1 / zoom,
    cameraRotYOffset: rotOffset,
    cameraTiltOffset: tiltOffset,
    meshAlphaMul: sceneAlive,
  };
};

// ══════════════════════════════════════════════════════════════════════
// v13 ADDITIONS — new layouts built for the "Investigation" script.
// Each maps to a specific moment in the Johnny Harris-voice monologue.
// Existing layouts above are preserved and remixed into the new order.
// ══════════════════════════════════════════════════════════════════════

// ──────────────────────────────────────────────────────────────────────
// beatGlobeArcs — "we've built the most connected generation in history."
// Wireframe globe with people scattered on the surface. Signal arcs
// (waveLines) flash between random pairs, implying a planet-wide network.
// ──────────────────────────────────────────────────────────────────────

export const beatGlobeArcs: BeatLayoutFn = (entities, t) => {
  const R = 1.05;
  const center = { x: 0, y: -0.45, z: 0 };
  const AXIS_TILT = 0.41;
  const rotation = t * 0.22;

  const cosTilt = Math.cos(AXIS_TILT);
  const sinTilt = Math.sin(AXIS_TILT);

  const peopleCount = Math.min(18, entities.length);
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));

  interface Pos {
    x: number;
    y: number;
    z: number;
    front: boolean;
  }
  const positions: Pos[] = [];

  for (let i = 0; i < peopleCount; i++) {
    const yFrac = 1 - (i / (peopleCount - 1)) * 2;
    const ringR = Math.sqrt(Math.max(0, 1 - yFrac * yFrac));
    const theta = goldenAngle * i + rotation;
    const lx = Math.cos(theta) * ringR;
    const ly = -yFrac;
    const lz = Math.sin(theta) * ringR;
    const ux = lx * cosTilt - ly * sinTilt;
    const uy = lx * sinTilt + ly * cosTilt;
    const uz = lz;
    const px = center.x + ux * R;
    const py = center.y + uy * R;
    const pz = center.z + uz * R;
    const front = uz > -0.05;
    positions.push({ x: px, y: py, z: pz, front });

    const appearAt = (i / peopleCount) * 1.4;
    const appear = Math.max(0, Math.min(1, (t - appearAt) / 0.6));
    setTarget(entities[i], px, py, pz, {
      size: 0.9 * appear,
      alpha: (front ? 0.88 : 0.15) * appear,
      warmth: "cream",
      halo: 0.08 * appear,
      pulse: false,
      emit: 0,
    });
  }
  parkAsAmbient(entities, peopleCount);

  const decorations: DecoEntry[] = [
    {
      kind: "globe",
      at: center,
      radius: R,
      rotation,
      axisTilt: AXIS_TILT,
      waveAmplitude: 0.02,
      warmth: "cream",
      alpha: 0.3,
    },
  ];

  // Signal arcs — pseudo-random pairs, each flashes briefly and fades.
  const ARC_COUNT = 18;
  for (let i = 0; i < ARC_COUNT; i++) {
    const aIdx = Math.floor(rand(i * 7.1) * peopleCount);
    const bIdx = Math.floor(rand(i * 11.7 + 3) * peopleCount);
    if (aIdx === bIdx) continue;
    const a = positions[aIdx];
    const b = positions[bIdx];
    if (!a.front && !b.front) continue;
    const appearT = 0.6 + i * 0.18;
    const life = 1.4;
    const localT = t - appearT;
    if (localT < 0 || localT > life) continue;
    const p = localT / life;
    const alpha = Math.sin(p * Math.PI) * 0.75;
    decorations.push({
      kind: "waveLine",
      from: { x: a.x, y: a.y, z: a.z },
      to: { x: b.x, y: b.y, z: b.z },
      phase: t * 5,
      frequency: 3.5,
      amplitude: 0.06,
      samples: 28,
      warmth: "cream",
      alpha,
      width: 0.9,
    });
  }

  return {
    labels: [],
    decorations,
    meshAlphaMul: 0,
    cameraZoom: 1.1,
    cameraRotYOffset: 0.2,
    cameraTiltOffset: -0.05,
    occluderSphere: { x: center.x, y: center.y, z: center.z, radius: R },
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatAlone — "we've never felt more alone." LONE STAR IN THE VOID.
// Physics lens: a single luminous star burning in cosmic space. Distant
// points of light exist but are unreachable — not because the star is
// faint (it isn't), but because the space around it is vast and cold.
// To be alive is to burn alone among other fires. Camera pulls back so
// the hero feels small against the depth.
// ──────────────────────────────────────────────────────────────────────

// Cosmological starfield — points distributed on a spherical shell
// around the hero at radius 4–7 units. Shell distribution (not box)
// so stars feel like they're surrounding the viewer in 3D depth, not
// pasted on a backdrop. Kept as ALONE_BG_DOTS name for beatSilence
// reuse; the content is now the deep-void starfield.
const ALONE_BG_DOTS: ReadonlyArray<{ x: number; y: number; z: number }> = (() => {
  const dots: { x: number; y: number; z: number }[] = [];
  for (let i = 0; i < 340; i++) {
    const s = i * 17.3 + 901;
    const theta = rand(s) * Math.PI * 2;
    const phi = Math.acos(2 * rand(s + 11) - 1);
    const r = 4 + rand(s + 23) * 3;
    const x = r * Math.sin(phi) * Math.cos(theta);
    const y = r * Math.cos(phi) * 0.4 - 0.5;
    const z = r * Math.sin(phi) * Math.sin(theta);
    dots.push({ x, y, z });
  }
  return dots;
})();

const ALONE_STAR_ALPHAS: ReadonlyArray<number> = (() => {
  const arr: number[] = [];
  for (let i = 0; i < 340; i++) {
    const s = i * 17.3 + 901;
    arr.push(0.12 + rand(s + 77) * 0.55);
  }
  return arr;
})();

export const beatAlone: BeatLayoutFn = (entities, t) => {
  // Hard reset of emission state on beat start — kill leftover colored
  // mesh ripples from beat 3's messaging recipients.
  if (t < 0.04) {
    for (const e of entities) {
      e.emit = 0;
      e.targetEmit = 0;
      e.waveR = 242;
      e.waveG = 238;
      e.waveB = 228;
      e.targetWaveR = 242;
      e.targetWaveG = 238;
      e.targetWaveB = 228;
    }
  }

  const emerge = Math.min(1, t / 0.6);
  const breath = 0.5 + 0.5 * Math.sin(t * 0.42);

  // The lone star. Warm amber, glowing, alive — NOT faint. The
  // loneliness is in the surround, not the subject. A star burns
  // even when no one is watching — and it radiates. A slow, low-
  // frequency wave pulses outward from the hero into the void, the
  // ONLY wave in the scene. The mesh carries it a short distance
  // before it decays into nothing. This is the first hint that
  // wave physics is underneath every moment of the piece.
  setTarget(entities[0], 0, -0.5, 0, {
    size: 2.1 + breath * 0.12,
    alpha: emerge,
    warmth: "amber",
    halo: 0.48 + breath * 0.08,
    pulse: false,
    emit: 0.32,
    emitFreq: 1.6,
    emitSpeed: 1.4,
    waveColor: WAVE_PALETTE.amber,
  });

  // Every other entity pushed OUT to the far reaches — becomes part of
  // the ghost-starfield. Continuity from beat 3's messaging people
  // dissolving into the void.
  for (let i = 1; i < entities.length; i++) {
    const s = i * 11 + 201;
    const theta = rand(s) * Math.PI * 2;
    const phi = Math.acos(2 * rand(s + 11) - 1);
    const r = 3 + rand(s + 23) * 2.5;
    const x = r * Math.sin(phi) * Math.cos(theta);
    const y = r * Math.cos(phi) * 0.35 - 0.5;
    const z = r * Math.sin(phi) * Math.sin(theta);
    setTarget(entities[i], x, y, z, {
      size: 0.45 + rand(s + 37) * 0.35,
      alpha: (0.08 + rand(s + 43) * 0.14) * emerge,
      warmth: "cream",
      halo: rand(s + 53) < 0.15 ? 0.05 : 0,
      pulse: false,
      emit: 0,
    });
  }

  return {
    labels: [],
    decorations: [
      {
        kind: "dotCloud",
        points: ALONE_BG_DOTS,
        pixelRadius: 0.55,
        alpha: 0.78 * emerge,
        warmth: "cream",
        rgb: [212, 216, 226],
        alphas: ALONE_STAR_ALPHAS,
      },
    ],
    // Faint mesh now so the hero's lone emission is visible as it
    // ripples outward into the void. The mesh is the fabric of
    // space itself — barely there, but there.
    meshAlphaMul: 0.22,
    cameraZoom: 0.95,
    cameraRotYOffset: 0.2,
    cameraTiltOffset: -0.22,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatChart — "loneliness is at the highest level ever recorded."
// Physics lens: COSMOLOGICAL REDSHIFT. Pairs of people drift apart as
// space itself expands between them. Hubble's law: the farther a pair
// is from the hero, the faster it recedes. Connecting lines redshift
// from warm to deep blue as the pair separates — real Doppler shift
// visualized. "Loneliness rising" IS cosmic expansion.
// ──────────────────────────────────────────────────────────────────────

const CHART_BLUE: RGB = [100, 135, 195];
const CHART_WARM: RGB = [240, 190, 130];

// Galactic pairs — each a cosmic "dipole" with a bright core at its
// midpoint that emits powerful waves across the fabric of space.
// Spread through a radially varied volume so pairs near the viewer
// drift slowly and pairs far out recede fast (Hubble's law).
const EXPANSION_PAIRS: ReadonlyArray<{
  cx: number;
  cz: number;
  dirX: number;
  dirZ: number;
  d0: number;
  hue: number; // 0..1 color seed
}> = (() => {
  const arr: {
    cx: number;
    cz: number;
    dirX: number;
    dirZ: number;
    d0: number;
    hue: number;
  }[] = [];
  const COUNT = 22;
  for (let i = 0; i < COUNT; i++) {
    const s = i * 19 + 51;
    // Radially distributed — ring with jitter, so the expansion reads
    // as concentric motion outward from the viewer.
    const angle = (i / COUNT) * Math.PI * 2 + rand(s) * 0.5;
    const cr = 0.5 + rand(s + 11) * 1.4;
    const cx = Math.cos(angle) * cr;
    const cz = Math.sin(angle) * cr * 0.85;
    const pairAngle = rand(s + 17) * Math.PI * 2;
    arr.push({
      cx,
      cz,
      dirX: Math.cos(pairAngle),
      dirZ: Math.sin(pairAngle),
      d0: 0.15 + rand(s + 23) * 0.1,
      hue: rand(s + 29),
    });
  }
  return arr;
})();

export const beatChart: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    for (const e of entities) {
      e.emit = 0;
      e.targetEmit = 0;
    }
  }

  const decorations: DecoEntry[] = [];
  // Consistent cross-beat emerge ramp — everything fades in over
  // 0.5s so the transition FROM beat 4 into this beat is smooth.
  // Same pattern in every Act II beat so the visual pulse is
  // coherent: the decorations morph continuously, not as separate
  // clip cuts.
  const emergeT = Math.min(1, t / 0.5);
  const emerge = emergeT * emergeT * (3 - 2 * emergeT);
  // Expansion progress — pairs separate and recede. Monotonic; the
  // universe doesn't contract in this beat.
  const expand = Math.min(1, t / 1.6);

  // Persistent cosmic backdrop — the same 340-star void that
  // appeared in beat 4 is STILL THERE underneath the galaxies.
  // This is what continuity is built from: one starfield that
  // never moves, and foreground phenomena that morph on top.
  decorations.push({
    kind: "dotCloud",
    points: ALONE_BG_DOTS,
    pixelRadius: 0.5,
    alpha: 0.35 * emerge,
    warmth: "cream",
    rgb: [200, 208, 220],
    alphas: ALONE_STAR_ALPHAS,
  });

  const galaxyCores: { x: number; y: number; z: number }[] = [];
  const coreAlphas: number[] = [];

  // Each pair = one emitter at its midpoint (a galaxy core). The
  // midpoint carries the dominant wave for the pair; as the pair
  // recedes, both the emit amplitude (1/r² distance falloff) and
  // the frequency drop — true redshift. The mesh is the cosmic
  // fabric, and the waves ARE what you watch.
  const MAX_EMITTERS = Math.min(EXPANSION_PAIRS.length, entities.length - 1);
  for (let i = 0; i < EXPANSION_PAIRS.length; i++) {
    const pair = EXPANSION_PAIRS[i];
    const originDist = Math.sqrt(pair.cx * pair.cx + pair.cz * pair.cz);
    const recession = 1 + expand * (0.4 + originDist * 0.55);
    const ccx = pair.cx * recession;
    const ccz = pair.cz * recession;

    const shift = Math.min(1, expand * (0.3 + originDist * 0.45));
    const coreR = Math.round(CHART_WARM[0] * (1 - shift) + CHART_BLUE[0] * shift);
    const coreG = Math.round(CHART_WARM[1] * (1 - shift) + CHART_BLUE[1] * shift);
    const coreB = Math.round(CHART_WARM[2] * (1 - shift) + CHART_BLUE[2] * shift);

    // Bright glowing galaxy core at midpoint — visible anchor so
    // the eye sees WHERE the waves are coming from.
    galaxyCores.push({ x: ccx, y: -0.5, z: ccz });
    coreAlphas.push((1 - shift * 0.55) * emerge);

    // Redshifted wave emitter — the MIDPOINT, not the endpoints,
    // carries the wave. Frequency drops with expansion (redshift),
    // amplitude stays strong near the source and attenuates
    // naturally via the engine's distance damping. Entities are
    // invisible (alpha 0), but each drives a VISIBLE ripple on
    // the mesh at the galaxy core's position.
    if (i < MAX_EMITTERS) {
      const emitFreq = 5.5 * Math.pow(1 - shift * 0.75, 1.2);
      const emitLevel = 0.32 * (1 - shift * 0.4) * emerge;
      const e = entities[1 + i];
      if (e) {
        setTarget(e, ccx, -0.5, ccz, {
          size: 0,
          alpha: 0,
          warmth: "cool",
          halo: 0,
          pulse: false,
          emit: emitLevel,
          emitFreq,
          emitSpeed: 2.6 + pair.hue * 0.8,
          waveColor: [coreR, coreG, coreB],
        });
      }
    }
  }

  // Galaxy cores — bright, with halos, so they read as light-
  // sources sending out the waves we see in the mesh.
  decorations.push({
    kind: "dotCloud",
    points: galaxyCores,
    pixelRadius: 2.2,
    alpha: 0.95,
    warmth: "cool",
    rgb: [210, 220, 240],
    alphas: coreAlphas,
  });

  // Hero at the comoving origin — cooling toward blue as the universe
  // around them expands. This is the "you are here" dot watching the
  // galaxies recede.
  const heroBreath = 0.5 + 0.5 * Math.sin(t * 0.9);
  setTarget(entities[0], 0, -0.5, 0, {
    size: (1.8 + heroBreath * 0.12) * emerge,
    alpha: emerge,
    warmth: "cool",
    halo: 0.35 * emerge,
    pulse: true,
    emit: 0,
    waveColor: CHART_BLUE,
  });

  parkAsAmbient(entities, 1 + EXPANSION_PAIRS.length);

  return {
    labels: [],
    decorations,
    // Mesh turned way up — it's the hero visual now. You're watching
    // the interference pattern of 22 receding galaxies rippling
    // across the fabric of space, their wavelengths visibly growing
    // as they redshift.
    meshAlphaMul: 1.0,
    // WADING THROUGH THE WAVE FIELD. Low tilt so the mesh projects
    // edge-on and waves sweep past the viewer's face as coherent
    // ripples. Close zoom so the nearest galaxies and their
    // wavefronts fill the foreground. Mesh extent expanded so the
    // wave field reaches into peripheral vision.
    cameraZoom: 1.75,
    cameraRotYOffset: -0.22,
    cameraTiltOffset: -0.16,
    meshExtentMul: 1.5,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatCounter600 — "a phone full of names."
// Physics lens: SIGNAL WITHOUT RECEIVER. Many nodes each fire tiny
// spherical wavefronts that expand outward — then collapse into
// nothing because there is no RESONANT receiver tuned to catch
// them. Signals emitted into the void, returning nothing. The
// contacts-list reality: every name is a signal that goes out, and
// goes silent. The mesh briefly ripples with each emission and
// settles back to flat — never a sustained wave, never a reply.
// ──────────────────────────────────────────────────────────────────────

interface GasParticle {
  x: number;
  y: number;
  z: number;
  baseR: number;   // initial distance from center (for wave-ride weighting)
  size: number;    // per-particle size variation
  tint: number;    // 0..1 color index (for nebula palette)
  brightness: number;
  f1: number;
  f2: number;
  p1: number;
  p2: number;
  p3: number;
}

// 600 gas particles distributed through a 3D NEBULA with 1/r
// density concentration toward the center. This is how real gas
// clouds look — dense at the core where gravity has pulled matter
// together, sparse at the edges. Not uniform noise. Plus per-
// particle size + tint variation so the cloud reads as structured
// matter with embedded stars, not noise.
const GAS_BASE: ReadonlyArray<GasParticle> = (() => {
  const COUNT = 620;
  const arr: GasParticle[] = [];
  for (let i = 0; i < COUNT; i++) {
    const s = i * 13.1 + 7;
    // Power-law sampling biases density toward the center — real
    // nebula density profile. u^1.6 gives moderate central
    // concentration without hiding all mass in a ball at center.
    const u = rand(s + 101);
    const r = 0.15 + Math.pow(u, 1.6) * 1.7;
    // Random direction on unit sphere.
    const theta = rand(s + 103) * Math.PI * 2;
    const phi = Math.acos(2 * rand(s + 107) - 1);
    const sphX = Math.sin(phi) * Math.cos(theta);
    const sphY = Math.cos(phi);
    const sphZ = Math.sin(phi) * Math.sin(theta);
    // Size variation — 10% of particles are "proto-stars," 3x bigger
    // and brighter, scattered through the cloud. Gives the nebula
    // visible structure instead of uniform dust.
    const isStar = rand(s + 131) < 0.1;
    const size = isStar ? 1.6 + rand(s + 137) * 1.1 : 0.5 + rand(s + 139) * 0.35;
    const brightness = isStar ? 0.95 : 0.55 + rand(s + 141) * 0.25;
    arr.push({
      x: sphX * r,
      y: -0.5 + sphY * r * 0.65,
      z: sphZ * r,
      baseR: r,
      size,
      tint: rand(s + 151),
      brightness,
      f1: 0.85 + rand(s) * 2.3,
      f2: 1.4 + rand(s + 11) * 2.7,
      p1: rand(s + 17) * Math.PI * 2,
      p2: rand(s + 23) * Math.PI * 2,
      p3: rand(s + 29) * Math.PI * 2,
    });
  }
  return arr;
})();

// Nebula palette — pink/violet/teal hot interstellar medium + warm
// stellar cores. Mixing 3 tones across the cloud makes it look like
// a real ionized gas region, not a grey swarm.
const NEBULA_COLORS: ReadonlyArray<RGB> = [
  [248, 200, 160], // warm stellar
  [220, 160, 200], // hydrogen alpha pink
  [160, 180, 240], // oxygen blue
];

// Contact-node registry — 30 names in your phone shown as dim static
// dots (the visual density of "a phone full of"), but only 5 of them
// fire across the beat. This avoids the chunky-cluster render problem
// from having 20+ overlapping shells. Fires are spaced 440ms apart;
// with SHELL_LIFE 0.75s, at most TWO shells are in flight at a time
// (one fading, one emerging) — mathematically clean, visually smooth.
interface NameNode {
  x: number;
  z: number;
  /** Time within the beat when this node fires. `Infinity` = never fires. */
  fireTime: number;
  color: RGB;
}
const NAME_NODES: ReadonlyArray<NameNode> = (() => {
  const arr: NameNode[] = [];
  const N = 30;
  const FIRE_COUNT = 5;
  const FIRE_FIRST = 0.18;
  const FIRE_SPACING = 0.44;
  const golden = Math.PI * (3 - Math.sqrt(5));
  // Pick 5 evenly-spaced indices (0..29) to be the firing ones.
  const firingSet = new Set<number>();
  for (let f = 0; f < FIRE_COUNT; f++) {
    firingSet.add(Math.floor((f + 0.5) * (N / FIRE_COUNT)));
  }
  let fireOrder = 0;
  for (let i = 0; i < N; i++) {
    const s = i * 19 + 7;
    const angle = golden * i;
    const r = 0.55 + rand(s) * 1.35;
    const x = Math.cos(angle) * r;
    const z = Math.sin(angle) * r * 0.85;
    const fireTime = firingSet.has(i)
      ? FIRE_FIRST + fireOrder * FIRE_SPACING
      : Number.POSITIVE_INFINITY;
    if (firingSet.has(i)) fireOrder++;
    // Muted, desaturated tones — colors of connections that exist
    // but never reach.
    const hue = rand(s + 17);
    const color: RGB =
      hue < 0.33
        ? [195, 205, 220]
        : hue < 0.66
        ? [205, 200, 195]
        : [190, 195, 205];
    arr.push({ x, z, fireTime, color });
  }
  return arr;
})();

export const beatCounter600: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    for (const e of entities) {
      e.emit = 0;
      e.targetEmit = 0;
    }
    // Hero carries the cool-blue loneliness from beatChart into
    // this beat — same emotional color, no warm interruption.
    const hero = entities[0];
    hero.warmth = "cool";
    hero.targetWarmth = "cool";
    hero.waveR = CHART_BLUE[0];
    hero.waveG = CHART_BLUE[1];
    hero.waveB = CHART_BLUE[2];
    hero.targetWaveR = CHART_BLUE[0];
    hero.targetWaveG = CHART_BLUE[1];
    hero.targetWaveB = CHART_BLUE[2];
  }

  const emergeT = Math.min(1, t / 0.5);
  const emerge = emergeT * emergeT * (3 - 2 * emergeT);

  const decorations: DecoEntry[] = [];

  // Persistent cosmic backdrop — same starfield as all Act II beats.
  decorations.push({
    kind: "dotCloud",
    points: ALONE_BG_DOTS,
    pixelRadius: 0.5,
    alpha: 0.32 * emerge,
    warmth: "cream",
    rgb: [200, 208, 220],
    alphas: ALONE_STAR_ALPHAS,
  });

  // Hero — cool, aching, still. Breathes but doesn't reach.
  const heroBreath = 0.5 + 0.5 * Math.sin(t * 0.85);
  setTarget(entities[0], 0, -0.5, 0, {
    size: (1.75 + heroBreath * 0.1) * emerge,
    alpha: emerge,
    warmth: "cool",
    halo: 0.28 * emerge,
    pulse: true,
    emit: 0,
    waveColor: CHART_BLUE,
  });

  // Walk through all 30 name-nodes. All are VISIBLE as dim points —
  // steady-state "contacts in your phone." Only 5 of them fire during
  // the beat (fireTime !== Infinity). When one fires, a shell expands,
  // fades, collapses. Shells are short-lived (0.75s) and spaced 440ms
  // apart, so at most TWO shells are in flight at a time — keeps the
  // render smooth and each signal legible as its own event.
  const SHELL_LIFE = 0.75;
  const SHELL_SPEED = 0.92;

  const nodePoints: { x: number; y: number; z: number }[] = [];
  const nodeAlphas: number[] = [];

  const maxEmitters = Math.min(NAME_NODES.length, entities.length - 1);
  for (let i = 0; i < NAME_NODES.length; i++) {
    const node = NAME_NODES[i];
    nodePoints.push({ x: node.x, y: -0.5, z: node.z });

    const sinceFire = t - node.fireTime;
    const firing = sinceFire >= 0 && sinceFire < SHELL_LIFE;

    if (firing) {
      const lifeT = sinceFire / SHELL_LIFE;
      const pulseEnv = Math.sin(lifeT * Math.PI);
      nodeAlphas.push((0.24 + pulseEnv * 0.7) * emerge);

      // Shell expands outward, 1/r² falloff with a hard lifetime
      // taper so it clearly COLLAPSES at the end instead of just
      // fading — the emotional read is "the signal dies."
      const radius = sinceFire * SHELL_SPEED;
      const r0 = 0.22;
      const rr = Math.max(radius, r0);
      const invSq = Math.min(0.38, (r0 * r0) / (rr * rr));
      const collapse = Math.pow(1 - lifeT, 1.4);
      const shellIntensity = invSq * collapse * emerge;
      if (shellIntensity > 0.015) {
        decorations.push({
          kind: "wavefront",
          origin: { x: node.x, y: -0.5, z: node.z },
          radius,
          intensity: shellIntensity,
          rgb: node.color,
          thickness: 0.18,
          phaseT: sinceFire,
        });
      }

      // Drive a brief mesh ripple from the firing node.
      if (i < maxEmitters) {
        const e = entities[1 + i];
        if (e) {
          setTarget(e, node.x, -0.5, node.z, {
            size: 0,
            alpha: 0,
            warmth: "cool",
            halo: 0,
            pulse: false,
            emit: 0.22 * pulseEnv * emerge,
            emitFreq: 4.8,
            emitSpeed: 3.0,
            waveColor: node.color,
          });
          // Direct-stomp so the emission is felt in the short window.
          e.emit = 0.22 * pulseEnv * emerge;
          e.emitFreq = 4.8;
          e.emitSpeed = 3.0;
          e.waveR = node.color[0];
          e.waveG = node.color[1];
          e.waveB = node.color[2];
        }
      }
    } else {
      nodeAlphas.push(0.22 * emerge);
      if (i < maxEmitters) {
        const e = entities[1 + i];
        if (e) {
          setTarget(e, node.x, -0.5, node.z, {
            size: 0,
            alpha: 0,
            warmth: "cool",
            halo: 0,
            pulse: false,
            emit: 0,
          });
        }
      }
    }
  }

  // Name nodes as a dim dotCloud — "contacts" always visible.
  decorations.push({
    kind: "dotCloud",
    points: nodePoints,
    pixelRadius: 1.15,
    alpha: 1,
    warmth: "cream",
    rgb: [205, 205, 205],
    alphas: nodeAlphas,
  });

  parkAsAmbient(entities, 1 + NAME_NODES.length);

  return {
    labels: [],
    decorations,
    // Mesh on — each firing node briefly ripples the fabric, and
    // the ripple decays to flat between fires. You see the field's
    // attempts to carry signals, none of them reaching home.
    meshAlphaMul: 0.68,
    meshExtentMul: 1.3,
    // Zoomed in so each of the 5 sparks dominates the frame when it
    // fires. With one shell in flight at a time, pushing closer lets
    // the viewer FEEL the single signal die mid-flight rather than
    // watching a distant chaotic cluster.
    cameraZoom: 1.65,
    cameraRotYOffset: 0.22,
    cameraTiltOffset: -0.04,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatClusterDozens — "a room full of strangers."
// Physics lens: PRESENCE WITHOUT SIGNAL. 38 figures fill the space
// around the hero — faces in a crowded room. None of them emit a
// wave. The mesh is DEAD FLAT. They're here. They're visible. They
// are not reaching each other, and no one is reaching you. This is
// the silence AT SOCIAL DENSITY — what it actually looks like when
// you're "surrounded by people" and it doesn't help.
// ──────────────────────────────────────────────────────────────────────

// Strangers — positioned in a loose disc around the hero at varied
// distances and heights, so the camera reads "a room of faces" not
// "a clean geometry."
interface StrangerSlot {
  x: number;
  y: number;
  z: number;
  size: number;
  coolness: number; // 0..1, how cold their tone
}
const STRANGER_SLOTS: ReadonlyArray<StrangerSlot> = (() => {
  const arr: StrangerSlot[] = [];
  const N = 38;
  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < N; i++) {
    const s = i * 23 + 11;
    const theta = golden * i + rand(s) * 0.6;
    const rNorm = Math.sqrt((i + 0.5) / N);
    const r = 0.45 + rNorm * 1.55;
    const x = Math.cos(theta) * r;
    const z = Math.sin(theta) * r * 0.9;
    const y = -0.5 + (rand(s + 11) - 0.5) * 0.45;
    arr.push({
      x,
      y,
      z,
      size: 0.75 + rand(s + 17) * 0.35,
      coolness: 0.3 + rand(s + 23) * 0.6,
    });
  }
  return arr;
})();

export const beatClusterDozens: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    for (const e of entities) {
      e.emit = 0;
      e.targetEmit = 0;
    }
  }

  const emergeT = Math.min(1, t / 0.6);
  const emerge = emergeT * emergeT * (3 - 2 * emergeT);
  const decorations: DecoEntry[] = [];

  // Persistent cosmic backdrop — same starfield as every other Act
  // II beat. Continuity anchor.
  decorations.push({
    kind: "dotCloud",
    points: ALONE_BG_DOTS,
    pixelRadius: 0.5,
    alpha: 0.26 * emerge,
    warmth: "cream",
    rgb: [200, 208, 220],
    alphas: ALONE_STAR_ALPHAS,
  });

  // Hero — cool, still, hollow. Pulses barely. This is the "you"
  // surrounded by faces, none of whom register. No wave emission.
  const heroBreath = 0.5 + 0.5 * Math.sin(t * 0.6);
  setTarget(entities[0], 0, -0.5, 0, {
    size: (1.7 + heroBreath * 0.08) * emerge,
    alpha: emerge,
    warmth: "cool",
    halo: 0.22 * emerge,
    pulse: false,
    emit: 0,
    waveColor: CHART_BLUE,
  });

  // Place strangers as entities so each gets real dot rendering
  // (halo, twinkle, sphere shading from drawDot). They are PRESENT
  // — visible, breathing microscopically — but none are emitting.
  const maxStrangers = Math.min(STRANGER_SLOTS.length, entities.length - 1);
  for (let i = 0; i < maxStrangers; i++) {
    const slot = STRANGER_SLOTS[i];
    // Tiny per-stranger breath — barely visible, reads as "alive
    // but absent." No motion, no reaching.
    const breath = 0.5 + 0.5 * Math.sin(t * 0.4 + i * 0.7);
    // Color mix — mostly cool, slight cream variance. Desaturated.
    // No warm tones anywhere: no human warmth in this room.
    const tone: RGB = [
      Math.round(200 - slot.coolness * 25),
      Math.round(205 - slot.coolness * 20),
      Math.round(215 - slot.coolness * 8),
    ];
    setTarget(entities[1 + i], slot.x, slot.y, slot.z, {
      size: slot.size * (0.85 + breath * 0.1) * emerge,
      alpha: (0.38 + slot.coolness * 0.15) * emerge,
      warmth: "cool",
      halo: 0.02 * emerge,
      pulse: false,
      emit: 0,
      waveColor: tone,
    });
  }
  parkAsAmbient(entities, 1 + maxStrangers);

  return {
    labels: [],
    decorations,
    // Mesh NEARLY DEAD — no one is emitting. What little you see
    // is residual from previous beat's fading wave trails. The
    // silence is visible as a flat cosmic fabric.
    meshAlphaMul: 0.12,
    meshExtentMul: 1.4,
    // Standing in the middle of the room — strangers all around
    // you at head height, none close enough to read as individuals,
    // all too close to ignore. Immersive isolation.
    cameraZoom: 1.5,
    cameraRotYOffset: -0.15,
    cameraTiltOffset: -0.12,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatScroll — "a life full of... almost."
// Physics lens: DESTRUCTIVE INTERFERENCE. Two figures close enough
// to almost touch. Each emits waves at the SAME frequency but 180°
// OUT OF PHASE. In the space BETWEEN them the waves cancel — dead
// flat mesh where they should meet. AROUND them the waves add. This
// is "almost" expressed as physics: two people reaching into the
// same field on opposite phases, so no matter how much each one
// emits, at the point of contact there is NOTHING. The entire beat
// is the ache of near-miss — the emissions are there, the space is
// right, but the phase is wrong. You can see the dead zone between
// them where connection should have happened.
// ──────────────────────────────────────────────────────────────────────

export const beatScroll: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    for (const e of entities) {
      e.emit = 0;
      e.targetEmit = 0;
    }
  }

  const emergeT = Math.min(1, t / 0.6);
  const emerge = emergeT * emergeT * (3 - 2 * emergeT);

  // Two figures positioned close but never touching. Each sits on
  // the mesh plane. As time progresses the beat, they drift
  // slightly APART — the "almost" widens. Separation starts at 0.5
  // and grows to 0.8 over the beat.
  const separation = 0.5 + Math.min(1, t / 2.5) * 0.3;
  const posAx = -separation * 0.5;
  const posBx = separation * 0.5;

  // Wave drivers — entities 1 and 2 are the two figures' emitters.
  // They run at the SAME emitFreq and SAME emitSpeed, but their
  // PHASES are offset by π — enforced by direct stomp each frame,
  // bypassing the engine's natural integration.
  const SHARED_FREQ = 3.6;
  const SHARED_SPEED = 2.4;
  // Phase A = ωt. Phase B = ωt + π.
  const phaseA = t * SHARED_SPEED;
  const phaseB = phaseA + Math.PI;

  // Figure A — cool tone, emitting its half of the wave.
  const figAColor: RGB = [185, 195, 220];
  setTarget(entities[1], posAx, -0.5, 0, {
    size: 1.55 * emerge,
    alpha: 0.92 * emerge,
    warmth: "cool",
    halo: 0.28 * emerge,
    pulse: false,
    emit: 0.35 * emerge,
    emitFreq: SHARED_FREQ,
    emitSpeed: SHARED_SPEED,
    waveColor: figAColor,
  });
  // Direct-stomp phase so interference is exact.
  entities[1].emit = 0.35 * emerge;
  entities[1].emitFreq = SHARED_FREQ;
  entities[1].emitSpeed = SHARED_SPEED;
  entities[1].phase = phaseA;
  entities[1].waveR = figAColor[0];
  entities[1].waveG = figAColor[1];
  entities[1].waveB = figAColor[2];

  // Figure B — slightly warmer cool, emitting the anti-phase half.
  const figBColor: RGB = [205, 190, 185];
  setTarget(entities[2], posBx, -0.5, 0, {
    size: 1.55 * emerge,
    alpha: 0.92 * emerge,
    warmth: "cool",
    halo: 0.28 * emerge,
    pulse: false,
    emit: 0.35 * emerge,
    emitFreq: SHARED_FREQ,
    emitSpeed: SHARED_SPEED,
    waveColor: figBColor,
  });
  entities[2].emit = 0.35 * emerge;
  entities[2].emitFreq = SHARED_FREQ;
  entities[2].emitSpeed = SHARED_SPEED;
  entities[2].phase = phaseB;
  entities[2].waveR = figBColor[0];
  entities[2].waveG = figBColor[1];
  entities[2].waveB = figBColor[2];

  // Hero — offstage in beat 8. The two figures are "you and
  // everyone." Park the hero small and dim behind, barely there.
  setTarget(entities[0], 0, -0.5, 0.8, {
    size: 0.8 * emerge,
    alpha: 0.35 * emerge,
    warmth: "cool",
    halo: 0.1 * emerge,
    pulse: false,
    emit: 0,
    waveColor: CHART_BLUE,
  });
  parkAsAmbient(entities, 3);

  const decorations: DecoEntry[] = [];

  // Persistent cosmic backdrop.
  decorations.push({
    kind: "dotCloud",
    points: ALONE_BG_DOTS,
    pixelRadius: 0.5,
    alpha: 0.28 * emerge,
    warmth: "cream",
    rgb: [200, 208, 220],
    alphas: ALONE_STAR_ALPHAS,
  });

  // Faint "reaching line" between the two figures — the gesture of
  // near-connection. Drawn UNDER the figures so it reads as the
  // shared intention, even as the wave field between them stays
  // flat. Pulses softly.
  const reachAlpha = (0.12 + 0.06 * Math.sin(t * 1.3)) * emerge;
  decorations.push({
    kind: "line",
    from: { x: posAx + 0.15, y: -0.5, z: 0 },
    to: { x: posBx - 0.15, y: -0.5, z: 0 },
    warmth: "cool",
    rgb: [200, 195, 205],
    alpha: reachAlpha,
    width: 0.6,
  });

  return {
    labels: [],
    decorations,
    // Mesh CRANKED — you NEED to see the dead zone between the
    // figures. Around them, waves ripple. BETWEEN them, flatness.
    // That's the "almost" — visible as cancelled physics.
    meshAlphaMul: 1.0,
    meshExtentMul: 1.4,
    // CLOSE. Side-on so you see both figures in the frame and the
    // dead zone between them. Near-eye tilt so the cancellation
    // reads as a trough of silence in the wave field at your level.
    cameraZoom: 2.0,
    cameraRotYOffset: 0.12,
    cameraTiltOffset: -0.1,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatSilence — "and still, most nights, silence."
// Physics lens: GROUND STATE / COSMIC MICROWAVE BACKGROUND. The
// lattice has dissolved. All structure is gone. The hero — a single
// warm point — cools toward thermal equilibrium with the void. Around
// them, the distant starfield remains (the CMB — undifferentiated
// cosmic background hiss you can't remove). Heat death for one.
// Quantum ground-state flickers in the tail as the filament struggles
// at the zero-energy limit.
// ──────────────────────────────────────────────────────────────────────

export const beatSilence: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    for (const e of entities) {
      e.emit = 0;
      e.targetEmit = 0;
    }
  }

  const decorations: DecoEntry[] = [];

  // ── Cooling envelope ──────────────────────────────────────────
  // Two phases. PHASE 1 (0 → 4.0s): exponential heat dissipation,
  // filament cools from full-bright to a red-ember floor (~0.08).
  // Not all the way to black — real filaments don't — so the room
  // keeps a dying amber presence that tells us there's still
  // SOMEONE in it, just barely. PHASE 2 (4.0 → 5.4s): the faintest
  // re-warming begins. This is "a thought forming." The first
  // stirring of the question that opens Act III. Feeds directly
  // into beat 10's awakening.
  const entrance = Math.min(1, t / 0.35);
  const coolingDim = entrance * (0.08 + Math.exp(-t * 0.7) * 0.92);
  // Re-ignition starts ~1.4s before beat end. Smooth cubic so it
  // feels like thought arising, not a sudden flicker.
  const BEAT_DUR = 5.4;
  const ignitionStart = BEAT_DUR - 1.4;
  const ignitionT = Math.max(0, (t - ignitionStart) / 1.4);
  const ignition = ignitionT * ignitionT * (3 - 2 * ignitionT); // smoothstep
  const lit = Math.min(1, coolingDim + ignition * 0.22);

  // Quantum ground-state flicker — a near-zero-energy filament
  // fluctuates. Only active in the COOLING valley (2.6s – 4.0s)
  // before the re-ignition phase, so it's felt as "the last
  // thermal noise before stillness."
  const flickerActive = t > 2.6 && t < 4.0;
  const flickerDip = flickerActive
    ? (Math.sin(t * 22) > 0.7 || Math.sin(t * 17) > 0.8 ? 0.65 : 1)
    : 1;
  const heroBrightness = lit * flickerDip;

  // Barely-moving breath — the hero still has a pulse but it's
  // slow, a body on the edge of sleep.
  const breathe = 0.5 + 0.5 * Math.sin(t * 0.45);

  // The hero's wave emission also follows the cooling curve — dying
  // to near-zero through the valley, then faintly returning in the
  // re-ignition phase. The mesh carries the final whisper of a wave
  // and then (in the last 1.4s) the first breath of a new one. Wave
  // physics never stops — it just gets quiet enough that you have
  // to look for it.
  const emitCooling = 0.14 * heroBrightness;
  const emitIgnition = 0.09 * ignition;
  setTarget(entities[0], 0, -0.5, 0, {
    size: (2.4 + breathe * 0.12) * heroBrightness + 0.32,
    alpha: Math.max(0.06, heroBrightness),
    warmth: "amber",
    halo: 0.5 * heroBrightness + ignition * 0.08,
    pulse: false,
    emit: emitCooling + emitIgnition,
    emitFreq: 1.4,
    emitSpeed: 1.2,
    waveColor: WAVE_PALETTE.amber,
  });

  // ── The last photon ───────────────────────────────────────────
  // Single expanding shell of warmth released at t=0.25 — the
  // filament's final emission before equilibrium. Travels slowly
  // (c=0.55 units/s), fades as 1/r² like any real radiating body.
  // Exists only for the first ~3s so it's gone before the quiet
  // valley. This is what makes beat 9 feel PHYSICAL rather than
  // just "a dot dimming on a static backdrop."
  const photonT = t - 0.25;
  if (photonT > 0 && photonT < 3.5) {
    const shellR = photonT * 0.55;
    // 1/r² intensity, clamped at peak so it never flashes. Multiply
    // by a slow envelope that ramps in / fades out.
    const r0 = 0.32;
    const rr = Math.max(shellR, r0);
    const inv = (r0 * r0) / (rr * rr);
    const envIn = Math.min(1, photonT / 0.6);
    const envOut = Math.max(0, 1 - photonT / 3.5);
    const shellIntensity = Math.min(0.22, inv) * envIn * envOut;
    if (shellIntensity > 0.01) {
      decorations.push({
        kind: "wavefront",
        origin: { x: 0, y: -0.5, z: 0 },
        radius: shellR,
        intensity: shellIntensity,
        rgb: [230, 170, 110], // warm amber echo
        thickness: 0.25,
        phaseT: photonT,
      });
    }
  }

  // ── Ground-state fluctuations ────────────────────────────────
  // The distant stars (entities 1..47) occasionally sparkle with
  // tiny halo bumps at staggered, seeded times. Each star has its
  // own flicker clock so the field as a whole twinkles without any
  // single star drawing attention. Quantum vacuum energy made
  // visible.
  for (let i = 1; i < entities.length; i++) {
    const seed = i * 11 + 3;
    const rx = (rand(seed) - 0.5) * 3.6;
    const rz = (rand(seed + 7) - 0.5) * 3;
    // Per-star flicker: a narrow pulse around a random phase within
    // the beat. Only a subset ever activates (the <0.45 gate).
    const flickerActivity = rand(seed + 41);
    let starHalo = 0;
    let starAlphaMul = 1;
    if (flickerActivity < 0.35) {
      const flickerT0 = rand(seed + 47) * 4.2 + 0.4;
      const flickerDur = 0.25 + rand(seed + 53) * 0.4;
      const ft = (t - flickerT0) / flickerDur;
      if (ft > 0 && ft < 1) {
        const pulse = Math.sin(ft * Math.PI);
        starHalo = pulse * 0.08;
        starAlphaMul = 1 + pulse * 1.6;
      }
    }
    setTarget(entities[i], rx, -0.5 + rand(seed + 13) * 0.05, rz, {
      size: 0.45,
      alpha: (0.05 + rand(seed + 29) * 0.05) * starAlphaMul,
      warmth: "cream",
      halo: starHalo,
      pulse: false,
      emit: 0,
    });
  }

  // Distant backdrop stars — kept dim, slight ignition-phase lift
  // so the cosmic hiss feels like it's listening when the hero's
  // thought begins to form.
  decorations.push({
    kind: "dotCloud",
    points: ALONE_BG_DOTS.slice(0, 180),
    pixelRadius: 0.8,
    alpha: 0.06 + ignition * 0.025,
    warmth: "cream",
    rgb: [175, 177, 182],
  });

  // Subtle camera drift — viewer slowly rotates through the void
  // over the beat so it never feels locked to a still frame.
  const driftProgress = Math.min(1, t / BEAT_DUR);
  return {
    labels: [],
    decorations,
    // Barely-there mesh carries the hero's dying wave + the nascent
    // re-ignition wave at the end. Almost nothing — but something.
    // The physics never fully dies.
    meshAlphaMul: 0.15,
    // Close to the dying filament at near-eye level. Camera drifts
    // gently through the beat — the void moves past us like we're
    // floating, not standing in a fixed shot.
    cameraZoom: 1.3 + driftProgress * 0.05,
    cameraRotYOffset: 0.12 + driftProgress * 0.12,
    cameraTiltOffset: -0.1,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatQuiet — "the quiet moments that decide everything."
// Inherits the repair's end state — two emitters at ±0.9, matched 4.5 Hz,
// in-phase, aligned amber. Now the waves SETTLE: over 3.3s the amplitude
// decays from 0.85 → 0.32 (ease-out — fast at first, then lingering) and
// the wave propagation slows (emitSpeed 3.0 → 2.0) so each crest rolls
// more meditatively. Frequencies stay locked. Phase stays aligned. What
// viewers see: the interference pattern from the repair gently relaxes
// into a calm, steady, rhythmic breathing — the pond after the stone
// stops moving. Not silence (waves still propagate), but rest.
// ──────────────────────────────────────────────────────────────────────

export const beatQuiet: BeatLayoutFn = (entities, t) => {
  const DUR = 3.3;
  const p = Math.min(1, t / DUR);
  // ease-out cubic — settles FAST at first (the air lets out), then
  // trails off quietly. Mirrors how a real pond calms after activity.
  const ease = 1 - Math.pow(1 - p, 3);

  // Emit decays 0.85 → 0.32 — waves keep propagating but the amplitude
  // visibly relaxes. Never zero: the physics never fully dies.
  const settleEmit = 0.85 - ease * 0.53;
  // Propagation slows — crests roll through the mesh more meditatively
  // as the beat progresses.
  const settleSpeed = 3.0 - ease * 1.0;

  setTarget(entities[0], -0.9, -0.5, 0, {
    size: 2.2 - ease * 0.2,
    alpha: 1,
    warmth: "amber",
    halo: 0.22 - ease * 0.08,
    pulse: false,
    emit: settleEmit,
    emitFreq: 4.5,
    emitSpeed: settleSpeed,
    waveColor: WAVE_PALETTE.amber,
  });
  setTarget(entities[1], 0.9, -0.5, 0, {
    size: 2.2 - ease * 0.2,
    alpha: 1,
    warmth: "amber",
    halo: 0.22 - ease * 0.08,
    pulse: false,
    emit: settleEmit,
    emitFreq: 4.5,
    emitSpeed: settleSpeed,
    waveColor: WAVE_PALETTE.amber,
  });
  parkAsAmbient(entities, 2);
  // Hold the same eye-level centered frame from drift and repair —
  // the trio reads as one continuous shot watching rhythms break,
  // rebuild, rest.
  return {
    labels: [],
    decorations: [],
    cameraZoom: 1.4,
    cameraRotYOffset: 0,
    cameraTiltOffset: -0.32,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatRead — "for the first time, the invisible becomes visible."
// True top-down radar. Camera tilt = π/2 (straight down at the plane).
// Everything grounded on the mesh surface at y = 0.02 so the dots and
// their wave signatures are perfectly centered from overhead.
//
// The graphics upgrade: instead of localized wavePatch decorations
// (tiled squares) stamped at each person, every scanned person
// EMITS onto the main wave mesh. The mesh IS the radar display —
// when the sweep arm passes over a person, their colored wave
// radiates outward across the entire field in their emotion color.
// Overlapping waves blend colors the way paint does in water.
//
// The sweep wedge is 24 tightly-spaced radial lines with exponential
// alpha decay — reads as a smooth cone of amber afterglow chasing
// the leading edge instead of 5 discrete trail arms.
// ──────────────────────────────────────────────────────────────────────

// Total tiltX = π/2 (true top-down). Subtract DEFAULT_TILT because the
// engine adds it automatically. Tiny 0.04 shave leaves a whisper of
// depth so the mesh lines don't collapse into a flat 2D grid.
const RADAR_TOP_DOWN_TILT = Math.PI / 2 - DEFAULT_TILT - 0.04;

export const beatRead: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    for (const e of entities) {
      e.emit = 0;
      e.targetEmit = 0;
    }
    // Center reticle — the radar operator's position, on the plane.
    const c = entities[0];
    c.x = 0; c.y = 0.02; c.z = 0;
    c.size = 0.5; c.alpha = 0; c.halo = 0;
    c.waveR = RADAR_PHOSPHOR[0]; c.waveG = RADAR_PHOSPHOR[1]; c.waveB = RADAR_PHOSPHOR[2];
    c.targetWaveR = RADAR_PHOSPHOR[0]; c.targetWaveG = RADAR_PHOSPHOR[1]; c.targetWaveB = RADAR_PHOSPHOR[2];
    // People — placed on the plane at y=0.02 (not floating). Pre-seed
    // wave params so their signatures are ready the instant they're lit.
    for (const p of RADAR_PEOPLE) {
      const n = entities[p.id];
      n.x = Math.cos(p.angle) * p.radius;
      n.y = 0.02;
      n.z = Math.sin(p.angle) * p.radius;
      n.size = 0.6; n.alpha = 0.04; n.halo = 0;
      n.emit = 0; n.targetEmit = 0;
      n.emitFreq = p.freq; n.targetEmitFreq = p.freq;
      n.emitSpeed = 3.0; n.targetEmitSpeed = 3.0;
      n.waveR = p.color[0]; n.waveG = p.color[1]; n.waveB = p.color[2];
      n.targetWaveR = p.color[0]; n.targetWaveG = p.color[1]; n.targetWaveB = p.color[2];
    }
  }

  // Boot — grid fades in, sweep accelerates from rest.
  const boot = Math.min(1, t / 0.7);
  const bootEased = boot * boot * (3 - 2 * boot);
  const SWEEP_SPEED = 1.8; // rad/s — slower than before for readability
  const bootArea = 0.5 * Math.min(t, 0.7) * bootEased;
  const postBoot = Math.max(0, t - 0.7);
  const sweepAngle = bootArea * SWEEP_SPEED + postBoot * SWEEP_SPEED;

  // Center reticle — subtle amber "you are here".
  setTarget(entities[0], 0, 0.02, 0, {
    size: 0.5,
    alpha: 0.55 * bootEased,
    warmth: "amber",
    halo: 0.22 * bootEased,
    pulse: false,
    emit: 0,
    waveColor: RADAR_PHOSPHOR,
  });

  // People — each lights up when the sweep arm passes over them.
  // Their emit turns on directly (bypasses engine lerp) so the wave
  // blooms across the mesh the instant the beam hits.
  for (const p of RADAR_PEOPLE) {
    const px = Math.cos(p.angle) * p.radius;
    const pz = Math.sin(p.angle) * p.radius;

    let delta = sweepAngle - p.angle;
    delta = ((delta % (Math.PI * 2)) + Math.PI * 3) % (Math.PI * 2) - Math.PI;

    // Heat profile: sharp rise on the leading edge, long phosphor tail
    // (~2.4 rad ≈ 137°) so waves from earlier contacts are still
    // glowing when new ones are scanned — multiple emotions visible at
    // once across the field. Ahead of the arm: invisible.
    let heat = 0;
    if (delta >= -0.08 && delta <= 2.4) {
      if (delta < 0) {
        heat = 1 - (-delta) / 0.08; // sharp ignition
      } else {
        const trail = 1 - delta / 2.4;
        heat = trail * trail; // quadratic decay — slow at first, faster at edge
      }
    }

    const n = entities[p.id];
    const emitLevel = heat * 0.85;
    // Snap emit so the wave doesn't slowly ramp via WAVE_LERP.
    n.emit = emitLevel;
    n.targetEmit = emitLevel;

    setTarget(n, px, 0.02, pz, {
      size: 0.6 + heat * 0.4,
      alpha: 0.04 + heat * 0.92,
      warmth: "cream",
      halo: heat * 0.38,
      pulse: false,
      emit: emitLevel,
      emitFreq: p.freq,
      emitSpeed: 3.0,
      waveColor: p.color,
    });
  }
  parkAsAmbient(entities, RADAR_PEOPLE.length + 1);

  const decorations: DecoEntry[] = [];

  // Range rings — 3 major concentric circles. Subtle, not fighting the
  // colored waves for attention.
  for (const r of [0.55, 1.05, 1.55]) {
    decorations.push({
      kind: "ring",
      at: { x: 0, y: 0.02, z: 0 },
      radius: r,
      warmth: "amber",
      rgb: RADAR_PHOSPHOR,
      alpha: 0.22 * bootEased,
      width: 0.7,
    });
  }
  // Inner bullseye ring around the operator.
  decorations.push({
    kind: "ring",
    at: { x: 0, y: 0.02, z: 0 },
    radius: 0.18,
    warmth: "amber",
    rgb: RADAR_PHOSPHOR,
    alpha: 0.4 * bootEased,
    width: 1.0,
  });

  // Crosshairs — dim north/south and east/west axes.
  const extent = 1.7;
  for (const [fx, fz, tx, tz] of [
    [-extent, 0, extent, 0],
    [0, -extent, 0, extent],
  ] as const) {
    decorations.push({
      kind: "line",
      from: { x: fx, y: 0.02, z: fz },
      to:   { x: tx, y: 0.02, z: tz },
      warmth: "amber",
      rgb: RADAR_PHOSPHOR,
      alpha: 0.13 * bootEased,
      width: 0.5,
    });
  }

  // Sweep wedge — 24 tight radial lines forming a smooth cone of
  // phosphor afterglow behind the leading edge. Exponential alpha
  // decay makes it read as continuous light, not discrete stripes.
  const WEDGE_SPAN = 1.3; // radians of visible sweep trail (~75°)
  const WEDGE_LINES = 24;
  const WEDGE_LENGTH = 1.7;
  for (let i = 0; i < WEDGE_LINES; i++) {
    const f = i / (WEDGE_LINES - 1); // 0 = leading edge, 1 = tail
    const angle = sweepAngle - f * WEDGE_SPAN;
    const lx = Math.cos(angle) * WEDGE_LENGTH;
    const lz = Math.sin(angle) * WEDGE_LENGTH;
    const decay = Math.exp(-f * 3.4);
    // Leading edge: thicker + brighter. Trail: thin phosphor.
    const isLeading = i === 0;
    decorations.push({
      kind: "line",
      from: { x: 0, y: 0.02, z: 0 },
      to:   { x: lx, y: 0.02, z: lz },
      warmth: "amber",
      rgb: RADAR_PHOSPHOR,
      alpha: (isLeading ? 0.95 : 0.7) * decay * bootEased,
      width: isLeading ? 1.6 : 0.7,
    });
  }

  return {
    labels: [],
    decorations,
    // Mesh carries the revealed waves — boosted alpha so the colored
    // signatures from each scanned person glow clearly across the
    // top-down view.
    meshAlphaMul: 1.3,
    cameraZoom: 1.2,
    cameraRotYOffset: 0,
    cameraTiltOffset: RADAR_TOP_DOWN_TILT,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatNoise — "not the posts. not the clicks."
// Chaotic flicker of unrelated dots across the field — the visual
// equivalent of "noise" — that dims away as the beat ends.
// ──────────────────────────────────────────────────────────────────────

// beatNoise — "not the posts. not the clicks."
// Quick transitional palate cleanse between the radar reveal and the
// product reveal. Camera drops to eye-level off-axis (NOT the radar
// top-down) so it reads as a fresh scene. Chaotic red flicker scatters
// across the field, then clears out fast. No grid, no sweep, no radar
// vocabulary — just a brief "here's the meaningless noise other
// products measure" then gone.
const NOISE_RED: RGB = [230, 85, 95];
export const beatNoise: BeatLayoutFn = (entities, t) => {
  const fadeP = Math.min(1, t / 1.8);
  const noiseLevel = Math.max(0, 1 - fadeP * 1.2);

  // Hero hidden — this is a noise moment, not a subject moment.
  setTarget(entities[0], 0, -0.5, 0, {
    size: 0.4,
    alpha: 0.15,
    warmth: "cream",
    halo: 0,
    pulse: false,
    emit: 0,
  });

  // Junk flicker across the whole field.
  for (let i = 1; i < entities.length; i++) {
    const seed = i * 17 + 5;
    const rx = (rand(seed) - 0.5) * 3.4;
    const ry = -0.55 + rand(seed + 3) * 0.25;
    const rz = (rand(seed + 7) - 0.5) * 2.6;
    const flashFreq = 6 + rand(seed + 13) * 8;
    const flashPhase = rand(seed + 19) * Math.PI * 2;
    const flash = Math.max(0, Math.sin(t * flashFreq + flashPhase));
    const amp = noiseLevel * flash;
    const rgb: RGB = [
      NOISE_RED[0] + rand(seed + 31) * 20,
      NOISE_RED[1] + rand(seed + 37) * 15,
      NOISE_RED[2] + rand(seed + 41) * 15,
    ];
    setTarget(entities[i], rx, ry, rz, {
      size: 0.4 + amp * 0.35,
      alpha: 0.04 + amp * 0.55,
      warmth: "amber",
      halo: amp * 0.1,
      pulse: false,
      emit: 0,
      waveColor: rgb,
    });
  }

  return {
    labels: [],
    decorations: [],
    meshAlphaMul: 0,
    // Eye-level, off-axis — a clean break from the radar top-down.
    cameraZoom: 1.0,
    cameraRotYOffset: -0.4,
    cameraTiltOffset: -0.15,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatEmblem — "that's what we built."
// Entities pull inward from scatter to form a tight amber ring. The
// moment before Suron is named — the shape of the product appearing.
// ──────────────────────────────────────────────────────────────────────

// beatEmblem — "that's what we built."
// The instrument. Central "you" dot at center; the 5 emotion people
// settle into a slowly-rotating ring around you. Each person is
// connected to you by a LIVE wave line oscillating at that emotion's
// frequency in that emotion's color — the actual signals the product
// reads. The whole thing rotates so you see it as an instrument,
// dimensional, alive. Camera holds at a 3/4 angle.
const EMBLEM_RADIUS = 1.0;
// 16 vertices of the unit tesseract — coordinates in (x, y, z, w).
const TESSERACT_VERTICES: ReadonlyArray<readonly [number, number, number, number]> =
  (() => {
    const v: [number, number, number, number][] = [];
    for (let i = 0; i < 16; i++) {
      v.push([
        (i & 1) ? 1 : -1,
        (i & 2) ? 1 : -1,
        (i & 4) ? 1 : -1,
        (i & 8) ? 1 : -1,
      ]);
    }
    return v;
  })();

// 32 edges — pairs of vertices whose indices differ in exactly one bit.
const TESSERACT_EDGES: ReadonlyArray<readonly [number, number]> = (() => {
  const edges: [number, number][] = [];
  for (let i = 0; i < 16; i++) {
    for (let j = i + 1; j < 16; j++) {
      const d = i ^ j;
      if ((d & (d - 1)) === 0) edges.push([i, j]);
    }
  }
  return edges;
})();

// beatEmblem — "that's what we built."
//
// A 4-simplex (pentatope) — the 4D analog of a tetrahedron, and the
// simplest regular 4D polytope. It has exactly 5 vertices and 10
// edges, with every vertex equidistant from every other. We assign
// one vertex per emotion (yellow happiness, blue sadness, violet
// anxiety, red anger, sage calm). The resulting shape IS the system:
// five emotions in relation, connected through 4-dimensional space,
// all equivalent. The product isn't a container OR a radar — it's
// the hyperdimensional geometry that holds five emotional frequencies
// in one coherent structure.
//
// Regular 4-simplex coordinates (edge length √8):
//   (±1, ±1, ±1, -1/√5) × 4 vertices — parity-even
//   (0, 0, 0, 4/√5)                  — apex
const SIMPLEX_VERTICES: ReadonlyArray<readonly [number, number, number, number]> = [
  [ 1,  1,  1, -1 / Math.sqrt(5)],
  [ 1, -1, -1, -1 / Math.sqrt(5)],
  [-1,  1, -1, -1 / Math.sqrt(5)],
  [-1, -1,  1, -1 / Math.sqrt(5)],
  [ 0,  0,  0,  4 / Math.sqrt(5)],
];
const SIMPLEX_EMOTION_COLORS: ReadonlyArray<RGB> = [
  [255, 215, 100],  // happiness — yellow
  [110, 145, 200],  // sadness — blue
  [180, 130, 210],  // anxiety — violet
  [240,  80,  95],  // anger — red
  [180, 230, 200],  // calm — sage
];
const SIMPLEX_EDGES: ReadonlyArray<readonly [number, number]> = (() => {
  const e: [number, number][] = [];
  for (let i = 0; i < 5; i++) for (let j = i + 1; j < 5; j++) e.push([i, j]);
  return e;
})();
export const beatEmblem: BeatLayoutFn = (entities, t) => {
  const formP = Math.min(1, t / 1.1);
  const eased = formP < 0.5 ? 2 * formP * formP : 1 - Math.pow(-2 * formP + 2, 2) / 2;

  // Central "you" — the observer at the centroid of the simplex.
  const centerBreath = 0.5 + 0.5 * Math.sin(t * 1.4);
  setTarget(entities[0], 0, -0.5, 0, {
    size: 0.65 + centerBreath * 0.04,
    alpha: 1,
    warmth: "cream",
    halo: 0.1 + centerBreath * 0.03,
    pulse: false,
    emit: 0,
    waveColor: [245, 245, 250],
  });
  parkAsAmbient(entities, 1);

  // Rotate the simplex in two 4D planes (XW, YW) plus a gentle 3D
  // parallax around Y. Every vertex moves through 4-space together
  // — no "inside" or "outside," just five equidistant points orbiting.
  const angleXW = t * 0.55;
  const angleYW = t * 0.37;
  const angle3DY = t * 0.3;
  const cxw = Math.cos(angleXW), sxw = Math.sin(angleXW);
  const cyw = Math.cos(angleYW), syw = Math.sin(angleYW);
  const cy3 = Math.cos(angle3DY), sy3 = Math.sin(angle3DY);

  const SCALE = 0.95;
  const VIEW_DIST = 3.5; // > 4/√5 ≈ 1.79 so denominator stays positive
  const Y_BASE = -0.5;

  const projected: { x: number; y: number; z: number; w: number }[] = [];
  for (const v of SIMPLEX_VERTICES) {
    let [x, y, z, w] = v;
    // 4D rotation in XW plane.
    { const nx = x * cxw - w * sxw; const nw = x * sxw + w * cxw; x = nx; w = nw; }
    // 4D rotation in YW plane.
    { const ny = y * cyw - w * syw; const nw = y * syw + w * cyw; y = ny; w = nw; }
    // Perspective project 4D → 3D along W.
    const factor = 1 / (VIEW_DIST - w);
    let px = x * factor * SCALE * VIEW_DIST;
    const py = y * factor * SCALE * VIEW_DIST * 0.55;
    let pz = z * factor * SCALE * VIEW_DIST;
    // 3D parallax rotation around Y.
    { const nx = px * cy3 - pz * sy3; const nz = px * sy3 + pz * cy3; px = nx; pz = nz; }
    projected.push({ x: px, y: Y_BASE + py, z: pz, w });
  }

  // Frame-relative W-depth for brightness cues.
  let wMin = Infinity, wMax = -Infinity;
  for (const p of projected) {
    if (p.w < wMin) wMin = p.w;
    if (p.w > wMax) wMax = p.w;
  }
  const wSpan = Math.max(0.001, wMax - wMin);

  const decorations: DecoEntry[] = [];

  // 10 edges, one per pair of emotions. Each edge's color is the blend
  // of its two endpoint emotions — literally the shade that appears
  // when those two feelings meet. Wave physics rides through every
  // edge so the whole simplex shimmers as a connected field.
  const WAVE_FREQ = 2.2;
  const WAVE_SPEED = 4.5;
  for (let e = 0; e < SIMPLEX_EDGES.length; e++) {
    const [i, j] = SIMPLEX_EDGES[e];
    const a = projected[i];
    const b = projected[j];
    const wAvg = (a.w + b.w) * 0.5;
    const depth = (wAvg - wMin) / wSpan;
    const ca = SIMPLEX_EMOTION_COLORS[i];
    const cb = SIMPLEX_EMOTION_COLORS[j];
    const blend: RGB = [
      (ca[0] + cb[0]) * 0.5,
      (ca[1] + cb[1]) * 0.5,
      (ca[2] + cb[2]) * 0.5,
    ];
    const edgePhase = e * 0.4 + wAvg * Math.PI + t * WAVE_SPEED;
    decorations.push({
      kind: "waveLine",
      from: { x: a.x, y: a.y, z: a.z },
      to:   { x: b.x, y: b.y, z: b.z },
      phase: edgePhase,
      frequency: WAVE_FREQ,
      amplitude: 0.028,
      envelope: true,
      samples: 20,
      warmth: "cream",
      rgb: blend,
      alpha: (0.55 + depth * 0.4) * eased,
      width: 0.9 + depth * 0.35,
    });
  }

  // Vertex dots in full emotion color. Each glows slightly as the
  // wavefront sweeps through its W coordinate — the feeling being
  // "heard" at that moment.
  for (let k = 0; k < projected.length; k++) {
    const p = projected[k];
    const depth = (p.w - wMin) / wSpan;
    const pulse = 0.5 + 0.5 * Math.sin(p.w * Math.PI + t * WAVE_SPEED);
    const color = SIMPLEX_EMOTION_COLORS[k];
    decorations.push({
      kind: "ring",
      at: { x: p.x, y: p.y, z: p.z },
      radius: 0.035 + depth * 0.02 + pulse * 0.01,
      warmth: "cream",
      rgb: color,
      alpha: (0.65 + pulse * 0.3 + depth * 0.05) * eased,
      width: 1.3,
    });
  }

  // Soft neutral heartbeat ring from center — cream/white so it holds
  // the palette together without fighting any one emotion.
  const RING_PERIOD = 1.9;
  const ringPhase = (t % RING_PERIOD) / RING_PERIOD;
  decorations.push({
    kind: "ring",
    at: { x: 0, y: Y_BASE, z: 0 },
    radius: 0.05 + ringPhase * 1.0,
    warmth: "cream",
    rgb: [245, 245, 250],
    alpha: (1 - ringPhase) * 0.45 * eased,
    width: 1.0,
  });

  return {
    labels: [],
    decorations,
    meshAlphaMul: 0,
    cameraZoom: 1.2,
    cameraRotYOffset: 0.15,
    cameraTiltOffset: 0.22,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatDrift — "shows you what's drifting."
// The unraveling of what used to be one. Opens on a single enormous
// 2A unified wave — both sources nearly overlapping, phase-locked at
// 4.4 Hz, amber. We HOLD that unity for ~35% of the beat so the
// viewer LOCKS onto "this is one big beautiful wave." Only then does
// the partner drift up in frequency (4.4 → 7.0) and pull apart, the
// unified peak visibly fracturing into dissonant beating and finally
// two separate A waves. Keeps separation small enough (0.9) that the
// collision zone stays on screen — the beauty is watching the one
// wave become two, not watching two things move apart.
// ──────────────────────────────────────────────────────────────────────

export const beatDrift: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    for (const e of entities) {
      e.emit = 0;
      e.targetEmit = 0;
    }
    // Phase-lock the pair so the open frame is a clean 2A peak, not a
    // random interference pattern. Without this, integrated-phase
    // drift desyncs them immediately.
    entities[0].phase = 0;
    entities[1].phase = 0;
  }

  const DUR = 2.6;
  const p = Math.min(1, t / DUR);

  // Hold the unity for the first 35% of the beat. The unravel curve
  // is zero until p=HOLD, then accelerates — giving viewer time to
  // register the big wave before anything changes.
  const HOLD = 0.35;
  const unravel = Math.max(0, (p - HOLD) / (1 - HOLD));
  const ease =
    unravel < 0.5
      ? 4 * unravel * unravel * unravel
      : 1 - Math.pow(-2 * unravel + 2, 3) / 2;

  // Near-overlap during HOLD, then separate to 0.9 (kept small so the
  // interference zone stays on-screen as the waves pull apart).
  const sep = 0.08 + ease * 0.82;

  // Hero anchors the original wave — stays amber, stays 4.4 Hz.
  setTarget(entities[0], -sep, -0.5, 0, {
    size: 2.0,
    alpha: 0.95,
    warmth: "amber",
    halo: 0.32 - ease * 0.08,
    pulse: false,
    emit: 0.82,
    emitFreq: 4.4,
    emitSpeed: 3.0,
    waveColor: WAVE_PALETTE.amber,
  });

  // Partner drifts: amber → cool cyan, 4.4 → 7.0 Hz, emit softens
  // 0.82 → 0.48. Its contribution to the combined peak shrinks both
  // in amplitude and in phase-alignment, so the unified 2A wave
  // disintegrates into beating and then into two A waves.
  const coolRgb: RGB = [
    WAVE_PALETTE.amber[0] + (WAVE_PALETTE.cyan[0] - WAVE_PALETTE.amber[0]) * ease,
    WAVE_PALETTE.amber[1] + (WAVE_PALETTE.cyan[1] - WAVE_PALETTE.amber[1]) * ease,
    WAVE_PALETTE.amber[2] + (WAVE_PALETTE.cyan[2] - WAVE_PALETTE.amber[2]) * ease,
  ];
  setTarget(entities[1], sep, -0.5, 0, {
    size: 2.0 - ease * 0.25,
    alpha: 0.95 - ease * 0.18,
    warmth: ease > 0.55 ? "cool" : "amber",
    halo: 0.32 - ease * 0.16,
    pulse: false,
    emit: 0.82 - ease * 0.34,
    emitFreq: 4.4 + ease * 2.6,
    emitSpeed: 3.0,
    waveColor: coolRgb,
  });

  // Seed partners B and C at their future path positions as very
  // faint background pinpoints. When "what to do next" beat arrives
  // they're already in the frame — they just wake up in place rather
  // than teleporting in from random ambient positions.
  setTarget(entities[2], -1.1, -0.5, 0.1, {
    size: 0.7,
    alpha: 0.18,
    warmth: "cream",
    halo: 0,
    pulse: false,
    emit: 0,
  });
  setTarget(entities[3], 0, -0.5, 0.85, {
    size: 0.7,
    alpha: 0.18,
    warmth: "cream",
    halo: 0,
    pulse: false,
    emit: 0,
  });
  parkAsAmbient(entities, 4);

  return {
    labels: [],
    decorations: [],
    meshAlphaMul: 1.1,
    // Zoom out slightly from EMOTION_ZOOM so both sources + the
    // beating-interference zone between them stay fully in frame as
    // they pull apart.
    cameraZoom: 1.4,
    cameraRotYOffset: 0,
    cameraTiltOffset: EMOTION_TILT,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatGrowing — "what's growing."
// A continuation of the drift scene in reverse — the pair that was
// fracturing now re-finds itself. Picks up EXACTLY where drift ended
// (sep 0.9, partner cyan at 7.0 Hz, hero amber at 4.4 Hz) so the
// viewer never sees a cut: the same two entities that just pulled
// apart now pull back in. Over the front half the partner warms from
// cyan back to warmpink, frequency locks from 7.0 down to 4.4, and
// the separation closes to 0.2. At the midpoint THE LOCK — matched
// frequency + tight overlap + amplitude bell-curve boost = enormous
// 2A+ unified peak rolling outward. Back half HOLDS the merged wave
// so viewer gets a full second to bathe in the unity. No reset at
// t=0 — emits and phases flow continuously from drift.
// ──────────────────────────────────────────────────────────────────────

export const beatGrowing: BeatLayoutFn = (entities, t) => {
  const DUR = 2.3;
  const p = Math.min(1, t / DUR);

  // Two-phase timeline: converge across the front half, HOLD unity
  // for the back half. `conv` is the 0→1 convergence progress, eased;
  // it caps at 1 at p=CONVERGE so all ease-driven params finalize
  // into their locked-in state for the hold.
  const CONVERGE = 0.5;
  const conv = Math.min(1, p / CONVERGE);
  const ease =
    conv < 0.5 ? 4 * conv * conv * conv : 1 - Math.pow(-2 * conv + 2, 3) / 2;

  // Pull in from drift's ending separation (0.9) to near-overlap
  // (0.2) where the combined field sums to a clean 2A peak.
  const sep = 0.9 - ease * 0.7;

  // Lock-flash amplitude boost through the held-unity back half.
  // Bell curve peaks mid-hold (p=0.75) so the unified swell blooms
  // and settles inside the viewer's attention window.
  const holdProgress = Math.max(0, (p - CONVERGE) / (1 - CONVERGE));
  const lockFlash = Math.sin(Math.min(1, holdProgress) * Math.PI) * 0.4;

  // Partner recovers color: drift's end cyan → warmpink as the
  // frequency re-locks. Feels like warmth returning after the fracture.
  const rgbB: RGB = [
    WAVE_PALETTE.cyan[0] + (WAVE_PALETTE.warmpink[0] - WAVE_PALETTE.cyan[0]) * ease,
    WAVE_PALETTE.cyan[1] + (WAVE_PALETTE.warmpink[1] - WAVE_PALETTE.cyan[1]) * ease,
    WAVE_PALETTE.cyan[2] + (WAVE_PALETTE.warmpink[2] - WAVE_PALETTE.cyan[2]) * ease,
  ];

  // Hero is already full amber + size 2.0 + emit 0.82 from drift.
  // Nothing to grow — just add the lock-flash on unity and a slight
  // halo bloom as the partner re-locks.
  setTarget(entities[0], -sep, -0.5, 0, {
    size: 2.0,
    alpha: 0.95,
    warmth: "amber",
    halo: 0.32 + ease * 0.1,
    pulse: false,
    emit: 0.82 + lockFlash,
    emitFreq: 4.4,
    emitSpeed: 3.0,
    waveColor: WAVE_PALETTE.amber,
  });

  // Partner starts exactly where drift left it (size 1.75, alpha 0.77,
  // emit 0.48, freq 7.0, cyan) and recovers: size grows back, emit
  // ramps up, frequency locks to hero's 4.4, color warms to warmpink.
  setTarget(entities[1], sep, -0.5, 0, {
    size: 1.75 + ease * 0.25,
    alpha: 0.77 + ease * 0.18,
    warmth: "amber",
    halo: 0.16 + ease * 0.28,
    pulse: false,
    emit: 0.48 + ease * 0.34 + lockFlash,
    emitFreq: 7.0 - ease * 2.6,
    emitSpeed: 3.0,
    waveColor: rgbB,
  });

  // Keep partners B and C staged in place as faint background pinpoints
  // (same positions as drift staged them) so "what to do next" has
  // nothing to teleport in — they're already there, just dim.
  setTarget(entities[2], -1.1, -0.5, 0.1, {
    size: 0.7,
    alpha: 0.18,
    warmth: "cream",
    halo: 0,
    pulse: false,
    emit: 0,
  });
  setTarget(entities[3], 0, -0.5, 0.85, {
    size: 0.7,
    alpha: 0.18,
    warmth: "cream",
    halo: 0,
    pulse: false,
    emit: 0,
  });
  parkAsAmbient(entities, 4);

  return {
    labels: [],
    decorations: [],
    meshAlphaMul: 1.1,
    cameraZoom: EMOTION_ZOOM,
    // Gentle continuation of drift's rotY=0 — a small rightward
    // shoulder instead of the previous 0.7 hard swing.
    cameraRotYOffset: 0.15,
    cameraTiltOffset: EMOTION_TILT,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatPath — "and what to do next."
// The act of ATTUNEMENT. The hero (amber, center) is YOU — one
// constant source. Three partners sit around the hero at fixed
// positions, each with their own natural frequency and color. Over
// the beat, the hero's emitFreq smoothly morphs through all three:
// 4.4 → 3.2 (slow warm partner) → 5.6 (medium bright) → 7.1 (fast
// sharp). As the hero's wave locks onto each partner's frequency,
// that partner wakes — halo brightens, emit swells into resonance,
// hero's wave color inherits the partner's hue while tuned to them.
// When the hero moves on, the previous partner keeps a residual
// afterglow — the relationship leaves a trace. The physics says:
// reading the room is changing your wavelength to meet each person
// where they are. You don't ask them to change. You attune.
// ──────────────────────────────────────────────────────────────────────

export const beatPath: BeatLayoutFn = (entities, t) => {
  // No emit/phase reset — flow continuously from growing's unified
  // state. Hero gently slides from -0.2 to center, partner (entity 1)
  // continues further right into position A, and entities 2 & 3
  // (already staged as faint pinpoints during drift + growing) just
  // brighten in place. Nothing teleports.

  const DUR = 2.8;
  const p = Math.min(1, t / DUR);

  interface Partner {
    x: number;
    z: number;
    color: RGB;
    freq: number;
  }
  // Partner A sits on the RIGHT so it inherits the drift/growing
  // partner's position naturally. B is on the left, C is in the
  // back — a soft triangle. Each has a distinct natural frequency
  // (slow warm / medium bright / fast sharp) so the hero's
  // attunement to each reads as an audibly different wave shape.
  const partners: ReadonlyArray<Partner> = [
    { x:  1.1,  z: -0.15, color: WAVE_PALETTE.warmpink, freq: 3.2 },
    { x: -1.1,  z:  0.1,  color: WAVE_PALETTE.yellow,   freq: 5.6 },
    { x:  0.0,  z:  0.85, color: WAVE_PALETTE.green,    freq: 7.1 },
  ];

  // Hero's target frequency — a piecewise smooth glide through each
  // partner's natural freq. Starts at hero's own 4.4 Hz, drifts into
  // partner A's 3.2, then to B's 5.6, then to C's 7.1 and settles.
  const HERO_FREQ = 4.4;
  const targetFreq = (() => {
    if (p < 0.20) {
      const k = p / 0.20;
      return HERO_FREQ + (partners[0].freq - HERO_FREQ) * k;
    }
    if (p < 0.50) {
      const k = (p - 0.20) / 0.30;
      return partners[0].freq + (partners[1].freq - partners[0].freq) * k;
    }
    if (p < 0.80) {
      const k = (p - 0.50) / 0.30;
      return partners[1].freq + (partners[2].freq - partners[1].freq) * k;
    }
    return partners[2].freq;
  })();

  // Resonance match strength per partner — Gaussian around each
  // partner's natural freq. matchWidth 1.1 Hz means a partner lights
  // sharply only when the hero's wave is close to theirs.
  const matchWidth = 1.1;
  const matches = partners.map((pr) => {
    const d = (targetFreq - pr.freq) / matchWidth;
    return Math.exp(-d * d);
  });

  // Hero wave color: base amber blended with partner colors weighted
  // by live match strength. When hero is tuned to a partner, that
  // partner's color floods the hero's wave — you literally take on
  // their hue while you're meeting them.
  const baseWeight = 0.45;
  let r = WAVE_PALETTE.amber[0] * baseWeight;
  let g = WAVE_PALETTE.amber[1] * baseWeight;
  let b = WAVE_PALETTE.amber[2] * baseWeight;
  let totalWeight = baseWeight;
  for (let i = 0; i < partners.length; i++) {
    r += partners[i].color[0] * matches[i];
    g += partners[i].color[1] * matches[i];
    b += partners[i].color[2] * matches[i];
    totalWeight += matches[i];
  }
  const heroColor: RGB = [r / totalWeight, g / totalWeight, b / totalWeight];

  setTarget(entities[0], 0, -0.5, 0, {
    size: 2.0,
    alpha: 1,
    warmth: "amber",
    halo: 0.38,
    pulse: false,
    emit: 0.78,
    emitFreq: targetFreq,
    emitSpeed: 3.0,
    waveColor: heroColor,
  });

  // Each partner responds to live match AND keeps a residual glow
  // once visited, so by the end all three trails are lit across the
  // field — three relationships held in the same moment.
  const VISIT_P = [0.15, 0.45, 0.75];
  for (let i = 0; i < partners.length; i++) {
    const pr = partners[i];
    const match = matches[i];
    const visited = p >= VISIT_P[i];
    const residual = visited ? 0.35 : 0.08;
    const level = Math.max(residual, match);

    setTarget(entities[i + 1], pr.x, -0.5, pr.z, {
      size: 1.45 + level * 0.55,
      alpha: 0.45 + level * 0.5,
      warmth: "amber",
      halo: 0.12 + level * 0.32,
      pulse: match > 0.75, // brief flicker at the moment of lock
      emit: 0.22 + match * 0.55,
      emitFreq: pr.freq,
      emitSpeed: 3.0,
      waveColor: pr.color,
    });
  }

  // Seed the two remaining hold-scene companions (rose front-left
  // and purple front-right) as faint pinpoints at their future hold
  // positions. When "hold the people who matter" arrives they just
  // brighten into color in place — no teleport from ambient.
  setTarget(entities[4], Math.cos(5 * Math.PI / 4 - 0.1) * 0.44, -0.5,
    Math.sin(5 * Math.PI / 4 - 0.1) * 0.44 * 0.78, {
    size: 0.6,
    alpha: 0.15,
    warmth: "cream",
    halo: 0,
    pulse: false,
    emit: 0,
  });
  setTarget(entities[5], Math.cos(-Math.PI / 5) * 0.46, -0.5,
    Math.sin(-Math.PI / 5) * 0.46 * 0.78, {
    size: 0.6,
    alpha: 0.15,
    warmth: "cream",
    halo: 0,
    pulse: false,
    emit: 0,
  });
  parkAsAmbient(entities, 6);

  return {
    labels: [],
    decorations: [],
    meshAlphaMul: 1.15,
    // Zoom slightly tighter than emotion — hero + 3 partners need
    // to fit the frame without the resonance bands getting cropped.
    cameraZoom: 1.45,
    // Small leftward lean, continuous with growing's rightward 0.15
    // — the camera drifts back through center across the beat change
    // instead of cutting a hard 60° shoulder swing.
    cameraRotYOffset: -0.1,
    cameraTiltOffset: EMOTION_TILT,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatHold — "so you can actually hold the people who matter."
// The harmony scene. Not unison — harmony. Hero at center, 5
// companions around in a soft organic cluster. Each companion has
// their OWN frequency (harmonically related to the hero's 4.4 Hz in
// clean ratios — 3:4, 5:4, 3:2, 1:2, 2:1), their OWN color, their
// OWN intensity, their OWN breath rhythm. When their waves overlap
// on the mesh they form a rich CONSONANT interference pattern — not
// a single unison wave, but a woven chord where every note is
// distinct and every note is needed. The thesis: holding people
// doesn't mean they all vibrate the same. It means their different
// wavelengths fit together beautifully.
//
// Entities 1–3 continue from beatPath (warmpink right / yellow left /
// green back) and pull IN close to the hero, inheriting their path
// colors and finding new harmonic frequencies. Entities 4–5 emerge
// from their staged positions to complete the cluster.
// ──────────────────────────────────────────────────────────────────────

export const beatHold: BeatLayoutFn = (entities, t) => {
  // "so you can actually hold the people who matter."
  //
  // The emotional rest before the promise staircase. Must feel like
  // being held — unified, safe, symmetric, slow. The previous design
  // gave each of five companions its own frequency (3.3 / 5.5 / 6.6
  // / 2.2 / 8.8 Hz — nearly three octaves) AND its own emitSpeed,
  // and placed them at asymmetric angles and radii. Six emitters
  // running out of phase on one mesh = heavy destructive/constructive
  // interference: spiky peaks on one side, deep valleys on the other,
  // no symmetry, no rest. It looked anxious, not tender.
  //
  // New design is single-source radial:
  //
  //   · HERO is the only emitter. Slow 3.0 Hz long-wavelength swell
  //     at emit 0.55 (was 0.68 at 4.4 Hz). Wide wavelength + low
  //     amplitude + slow travel (emitSpeed 1.6) = gentle concentric
  //     ripple. Single-source emission is automatically symmetric.
  //
  //   · FIVE COMPANIONS sit in a perfect pentagon around the hero at
  //     equal radius and y. Their emit is 0 — they ride the hero's
  //     swell instead of pushing their own waves. Identity lives in
  //     colour, size, and a gentle halo-breath that ripples around
  //     the pentagon with a small per-person phase shift, so the
  //     cluster reads as alive individuals without fracturing the
  //     mesh.
  //
  //   · NO rocking sway on the cluster — the hold is STILL. A sway
  //     breaks the hard-won radial symmetry over time.

  const ease = Math.min(1, t / 1.0);

  // One breath for everyone. Per-person phase offsets only wobble
  // halo intensity, never position.
  const unifiedBreath = 0.5 + 0.5 * Math.sin(t * 0.55);

  setTarget(entities[0], 0, -0.5, 0, {
    size: (1.95 + unifiedBreath * 0.10) * ease,
    alpha: ease,
    warmth: "amber",
    halo: (0.46 + unifiedBreath * 0.10) * ease,
    pulse: false,
    emit: 0.55 * ease,
    emitFreq: 3.0,
    emitSpeed: 1.6,
    waveColor: WAVE_PALETTE.amber,
  });

  interface Companion {
    color: RGB;
    baseSize: number;
    baseHalo: number;
    breathPhase: number;
  }

  const RADIUS = 0.62;
  const companions: ReadonlyArray<Companion> = [
    { color: WAVE_PALETTE.warmpink, baseSize: 1.44, baseHalo: 0.26, breathPhase: 0.0 },
    { color: WAVE_PALETTE.yellow,   baseSize: 1.40, baseHalo: 0.24, breathPhase: 1.26 },
    { color: WAVE_PALETTE.green,    baseSize: 1.38, baseHalo: 0.22, breathPhase: 2.51 },
    { color: WAVE_PALETTE.rose,     baseSize: 1.42, baseHalo: 0.24, breathPhase: 3.77 },
    { color: WAVE_PALETTE.purple,   baseSize: 1.36, baseHalo: 0.22, breathPhase: 5.03 },
  ];

  for (let i = 0; i < companions.length; i++) {
    const c = companions[i];
    // Perfect pentagon — equal angular spacing, top vertex first.
    const angle = -Math.PI / 2 + (i * Math.PI * 2) / companions.length;
    const x = Math.cos(angle) * RADIUS;
    const z = Math.sin(angle) * RADIUS * 0.78;

    // Halo breath only — positional stillness is the whole point.
    const breath = 0.5 + 0.5 * Math.sin(t * 0.55 + c.breathPhase);

    setTarget(entities[i + 1], x, -0.5, z, {
      size: (c.baseSize + breath * 0.08) * ease,
      alpha: 0.95 * ease,
      warmth: "amber",
      halo: (c.baseHalo + breath * 0.10) * ease,
      pulse: false,
      // Zero emission — companions do NOT add competing waves.
      // Matched freq/speed kept only so residual lerp from prior
      // beat tapers to the hero's settings cleanly.
      emit: 0,
      emitFreq: 3.0,
      emitSpeed: 1.6,
      waveColor: c.color,
    });
  }

  parkAsAmbient(entities, companions.length + 1);

  return {
    labels: [],
    decorations: [],
    meshAlphaMul: 1.0,
    cameraZoom: 1.55,
    // Barely-there orbit. Slower than before (0.025 → 0.012) so
    // motion reads as stillness — the camera is present, not moving.
    cameraRotYOffset: -0.1 + t * 0.012,
    cameraTiltOffset: 0.2,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatConnectedWorld — "we've built the most connected generation in history."
// Earth built from ~800 amber dots sampled densely on a sphere and
// filtered through a continent mask — ocean is void, land emerges as
// recognizable shapes (Americas, Europe, Africa, Middle East, Asia,
// Oceania, Antarctica). No solid sphere body — the globe is felt
// through the dot distribution. Five curved 3D arcs fly between major
// cities with parabolic lift. Slow rotation reveals the whole world.
// ──────────────────────────────────────────────────────────────────────

// Land mask — union of rectangular lat/lng bounding regions. Any sample
// point falling in at least one box is considered land.
// Each entry: [minLat, maxLat, minLng, maxLng] in degrees.
const CW_LAND_BOXES: ReadonlyArray<readonly [number, number, number, number]> = [
  // NORTH AMERICA
  [28, 50, -125, -90],    // US west/midwest + S Canada
  [28, 50, -90, -66],     // US east
  [34, 45, -85, -66],     // US northeast
  [48, 60, -130, -55],    // Canada main
  [55, 72, -140, -60],    // Canada north
  [55, 72, -168, -140],   // Alaska
  [17, 32, -117, -94],    // Mexico north
  [14, 20, -104, -87],    // Mexico south
  [8, 18, -92, -77],      // Central America
  [17, 24, -85, -74],     // Cuba/Caribbean
  [18, 21, -74, -66],     // Hispaniola/PR
  // GREENLAND
  [60, 83, -70, -15],
  // SOUTH AMERICA
  [2, 13, -78, -58],      // Venezuela/Colombia
  [-8, 2, -80, -45],      // Northern Brazil + Peru
  [-23, -8, -78, -36],    // Central Brazil + Bolivia
  [-33, -23, -72, -42],   // S Brazil + Paraguay
  [-40, -33, -72, -56],   // Argentina
  [-55, -40, -74, -62],   // Patagonia
  // EUROPE
  [50, 60, -11, 2],       // UK/Ireland
  [43, 51, -5, 8],        // France
  [36, 44, -10, 4],       // Spain
  [36, 46, 6, 18],        // Italy
  [47, 55, 2, 15],        // Germany/Benelux
  [45, 55, 15, 40],       // Eastern Europe
  [55, 60, 10, 30],       // S Scandinavia
  [58, 71, 5, 32],        // N Scandinavia
  [35, 42, 15, 30],       // Balkans
  // RUSSIA
  [55, 72, 28, 60],       // Russia west
  [55, 72, 60, 100],      // Russia central
  [55, 72, 100, 180],     // Siberia east
  // AFRICA
  [18, 37, -18, 12],      // NW Africa (Morocco/Algeria)
  [18, 32, 12, 36],       // N Africa (Libya/Egypt)
  [5, 18, -17, 42],       // Sahel band
  [-5, 5, -10, 42],       // Central Africa
  [-22, -5, 10, 42],      // Southern middle
  [-35, -22, 12, 36],     // S Africa
  [2, 12, 40, 52],        // Horn of Africa
  // MIDDLE EAST
  [15, 30, 34, 55],       // Arabia
  [30, 40, 35, 50],       // Levant/Iraq
  [25, 42, 44, 65],       // Iran
  // ASIA
  [35, 50, 60, 90],       // Central Asia
  [40, 55, 90, 120],      // Mongolia
  [28, 42, 72, 100],      // Tibet/W China
  [20, 40, 100, 123],     // E China
  [42, 55, 118, 140],     // Manchuria
  [6, 28, 68, 92],        // India
  [20, 32, 90, 100],      // Myanmar
  [8, 23, 95, 110],       // Indochina
  [-10, 8, 95, 120],      // Malaysia/Sumatra/Borneo
  [-10, -2, 115, 142],    // Indonesia east
  [4, 20, 118, 128],      // Philippines
  [30, 46, 128, 146],     // Japan
  [33, 43, 124, 131],     // Korea
  [22, 27, 120, 122],     // Taiwan
  // OCEANIA
  [-40, -10, 112, 154],   // Australia
  [-47, -34, 165, 178],   // New Zealand
  [-11, -3, 140, 152],    // Papua New Guinea
  // ANTARCTICA
  [-90, -65, -180, 180],
];

function cwIsLand(lat: number, lng: number): boolean {
  for (const box of CW_LAND_BOXES) {
    if (lat >= box[0] && lat <= box[1] && lng >= box[2] && lng <= box[3]) {
      return true;
    }
  }
  return false;
}

// Precomputed land dot positions — once, at module load. Fibonacci
// lattice on sphere, filtered by land mask.
const CW_LAND_DOTS: ReadonlyArray<{ lat: number; lng: number }> = (() => {
  const SAMPLES = 5600;
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const result: { lat: number; lng: number }[] = [];
  for (let i = 0; i < SAMPLES; i++) {
    const yFrac = 1 - (i / (SAMPLES - 1)) * 2;
    const lat = (Math.asin(yFrac) * 180) / Math.PI;
    const thetaRaw = goldenAngle * i;
    let lng = ((thetaRaw * 180) / Math.PI) % 360;
    if (lng > 180) lng -= 360;
    if (lng < -180) lng += 360;
    if (cwIsLand(lat, lng)) {
      result.push({ lat, lng });
    }
  }
  return result;
})();

// Major city pairs for traveling arcs with unique color per route.
// Ten global connections, staggered 200ms apart, so 3–4 arcs are
// always flying at once — reads as continuous planetary traffic.
interface CwArc {
  from: [number, number];
  to: [number, number];
  delay: number;
  color: RGB;
}
const CW_ARCS: ReadonlyArray<CwArc> = [
  { from: [40.7, -74.0], to: [51.5, -0.1], delay: 0.1, color: WAVE_PALETTE.amber },    // NYC → London
  { from: [35.7, 139.7], to: [22.3, 114.2], delay: 0.3, color: WAVE_PALETTE.cyan },    // Tokyo → HK
  { from: [55.8, 37.6], to: [-33.9, 151.2], delay: 0.5, color: WAVE_PALETTE.rose },    // Moscow → Sydney
  { from: [-1.3, 36.8], to: [29.8, -95.4], delay: 0.7, color: WAVE_PALETTE.green },    // Nairobi → Houston
  { from: [-33.9, 18.4], to: [39.9, 116.4], delay: 0.9, color: WAVE_PALETTE.warmpink }, // Cape Town → Beijing
  { from: [48.9, 2.3], to: [-23.5, -46.6], delay: 1.1, color: WAVE_PALETTE.yellow },   // Paris → São Paulo
  { from: [19.1, 72.9], to: [6.5, 3.4], delay: 1.3, color: WAVE_PALETTE.purple },      // Mumbai → Lagos
  { from: [37.6, 127.0], to: [34.0, -118.2], delay: 1.5, color: WAVE_PALETTE.teal },   // Seoul → LA
  { from: [25.3, 55.3], to: [1.3, 103.8], delay: 1.7, color: WAVE_PALETTE.orange },    // Dubai → Singapore
  { from: [-34.6, -58.4], to: [41.0, 28.9], delay: 1.9, color: WAVE_PALETTE.blue },    // Buenos Aires → Istanbul
];

// Ivory/moonlight tone for land dots — reads as cool neutral so the
// colored arcs pop against it.
const CW_LAND_RGB: RGB = [230, 235, 242];

export const beatConnectedWorld: BeatLayoutFn = (entities, t) => {
  const R = 1.05;
  const center = { x: 0, y: -0.45, z: 0 };
  const AXIS_TILT = 0.41;
  const rotation = t * 0.1;
  const cosTilt = Math.cos(AXIS_TILT);
  const sinTilt = Math.sin(AXIS_TILT);

  // Beat 1 left the hero as a bright amber "memory" in the foreground.
  // Instead of snapping it into ambient scatter the moment this beat
  // starts (which reads as a hard cut), keep it visible at its
  // previous spot while it fades to zero over the first 0.8s — the
  // same window the Earth's dotCloud uses to fade in. So the memory
  // dissolves AS the globe materialises in its place.
  const heroFade = Math.max(0, 1 - t / 0.8);

  // lat/lng → rotated unit vector on sphere.
  function place(lat: number, lng: number): { x: number; y: number; z: number } {
    const latR = (lat * Math.PI) / 180;
    const lngR = (lng * Math.PI) / 180;
    const cLat = Math.cos(latR);
    const ux = cLat * Math.cos(lngR);
    const uy = -Math.sin(latR);
    const uz = cLat * Math.sin(lngR);
    const r1x = ux * Math.cos(rotation) + uz * Math.sin(rotation);
    const r1z = -ux * Math.sin(rotation) + uz * Math.cos(rotation);
    const r2y = uy * cosTilt - r1z * sinTilt;
    const r2z = uy * sinTilt + r1z * cosTilt;
    return { x: r1x, y: r2y, z: r2z };
  }

  // All 48 entities parked — the land cloud + arcs do the visual work.
  // Then override entity 0 (the hero) to hold its foreground position
  // briefly and fade out, crossfading with the Earth that's fading in.
  parkAsAmbient(entities, 0);
  if (heroFade > 0) {
    setTarget(entities[0], 0, -0.4, -0.2, {
      size: 2.4 * heroFade,
      alpha: 0.97 * heroFade,
      warmth: "amber",
      halo: 0.67 * heroFade,
      pulse: false,
      emit: 0,
    });
  }

  // Rotate all land dots into world space.
  const landPoints: { x: number; y: number; z: number }[] = new Array(
    CW_LAND_DOTS.length
  );
  for (let i = 0; i < CW_LAND_DOTS.length; i++) {
    const { lat, lng } = CW_LAND_DOTS[i];
    const r = place(lat, lng);
    landPoints[i] = {
      x: center.x + r.x * R,
      y: center.y + r.y * R,
      z: center.z + r.z * R,
    };
  }

  const fadeIn = Math.min(1, t / 0.8);

  const decorations: DecoEntry[] = [
    // Atmospheric glow — rendered BEFORE land dots so the continents
    // sit on top of the planetary halo. Warm horizon inner tint, cool
    // ionosphere outer bloom, drifting wisps above the limb.
    {
      kind: "atmosphere",
      at: center,
      radius: R,
      innerRgb: [150, 200, 240],
      outerRgb: [80, 150, 220],
      alpha: 0.95 * fadeIn,
      time: t,
    },
    {
      kind: "dotCloud",
      points: landPoints,
      // Tinier dots + denser sample count + tighter alpha makes the
      // continents read as real terrain texture instead of a loose
      // dot-paint. Finer pixelRadius also lets the atmospheric rim
      // glow shine through between dots.
      pixelRadius: 0.62,
      alpha: 0.96 * fadeIn,
      warmth: "cream",
      rgb: CW_LAND_RGB,
      occluder: { x: center.x, y: center.y, z: center.z, radius: R },
    },
  ];

  // Curved 3D arcs + pin drops at destinations.
  // Animation model: progressive draw with trailing fade.
  //   - Head position = arcP along the great circle (continuous, no seg quantization)
  //   - Trail extends p_head - TRAIL_LEN back; head glows bright, trail fades
  //   - No bright flicker on seg boundaries because alpha is a smooth
  //     function of each segment's midpoint distance from head
  //   - Pin drop triggers exactly when head reaches destination (arcT ≥ ARC_LIFE)
  const ARC_LIFE = 1.1;
  const TRAIL_LEN = 0.55;
  const DROP_LIFE = 0.8;

  for (const arc of CW_ARCS) {
    const arcT = t - arc.delay;
    if (arcT < 0) continue;

    const fu = place(arc.from[0], arc.from[1]);
    const tu = place(arc.to[0], arc.to[1]);
    const dot = Math.max(-1, Math.min(1, fu.x * tu.x + fu.y * tu.y + fu.z * tu.z));
    const omega = Math.acos(dot);
    const sinOmega = Math.sin(omega);
    if (omega < 0.01 || sinOmega < 0.001) continue;

    // Arc segments — progressive draw up to head, with trailing fade.
    if (arcT <= ARC_LIFE) {
      const arcP = arcT / ARC_LIFE;
      const pHead = arcP;
      const pTail = Math.max(0, pHead - TRAIL_LEN);

      const LIFT = 0.22;
      const SEGS = 40;
      let prev: { x: number; y: number; z: number; uz: number; sP: number } | null = null;

      for (let seg = 0; seg <= SEGS; seg++) {
        const sP = seg / SEGS;
        if (sP > pHead + 0.002) break;
        const a = Math.sin((1 - sP) * omega) / sinOmega;
        const b = Math.sin(sP * omega) / sinOmega;
        const ux = a * fu.x + b * tu.x;
        const uy = a * fu.y + b * tu.y;
        const uz = a * fu.z + b * tu.z;
        const lift = LIFT * Math.sin(sP * Math.PI);
        const rr = R + lift;
        const pt = {
          x: center.x + ux * rr,
          y: center.y + uy * rr,
          z: center.z + uz * rr,
          uz,
          sP,
        };

        if (prev && prev.sP >= pTail - 0.001) {
          // Back-hemisphere cull.
          const bothBack = prev.uz > 0.14 && pt.uz > 0.14;
          if (!bothBack) {
            // Depth dim for partially-back segments.
            const fMul = Math.max(
              0.25,
              1 - Math.max(0, (prev.uz + pt.uz) * 0.5 + 0.05) * 1.8
            );
            // Trail fade — segment midpoint distance from head.
            const midP = (prev.sP + pt.sP) * 0.5;
            const distFromHead = pHead - midP;
            // Smooth trail alpha — exponential brighter near head.
            const trailT = Math.max(0, Math.min(1, 1 - distFromHead / TRAIL_LEN));
            const trailAlpha = Math.pow(trailT, 0.6);
            // Head brightness boost — tiny window right at the head.
            const headBoost =
              distFromHead < 0.06 ? 1 + (1 - distFromHead / 0.06) * 0.6 : 1;
            decorations.push({
              kind: "line",
              from: { x: prev.x, y: prev.y, z: prev.z },
              to: { x: pt.x, y: pt.y, z: pt.z },
              warmth: "amber",
              rgb: arc.color,
              alpha: 0.82 * trailAlpha * headBoost * fMul,
              width: 1.4,
            });
          }
        }
        prev = pt;
      }
    }

    // Pin drop — starts exactly when the arc head arrives at the destination.
    const dropT = arcT - ARC_LIFE;
    if (dropT >= 0 && dropT <= DROP_LIFE) {
      // Skip drops on the back hemisphere.
      if (tu.z < 0.14) {
        for (let r = 0; r < 3; r++) {
          const ringStagger = r * 0.14;
          const rT = dropT - ringStagger;
          if (rT < 0 || rT > DROP_LIFE - ringStagger) continue;
          const rP = rT / (DROP_LIFE - ringStagger);
          const ringRadius = 0.015 + rP * 0.17;
          const fade = Math.max(0, 1 - rP);
          const ringAlpha = fade * (r === 0 ? 0.95 : r === 1 ? 0.55 : 0.3);
          if (ringAlpha < 0.02) continue;
          decorations.push({
            kind: "sphereRing",
            center,
            normal: { x: tu.x, y: tu.y, z: tu.z },
            distance: R + 0.015,
            radius: ringRadius,
            warmth: "amber",
            rgb: arc.color,
            alpha: ringAlpha,
            width: 1.6,
          });
        }
        // Bright stationary dot at the destination — "pin landed" feel.
        const pinFade = Math.max(0, 1 - dropT / 0.5);
        if (pinFade > 0.05) {
          decorations.push({
            kind: "sphereRing",
            center,
            normal: { x: tu.x, y: tu.y, z: tu.z },
            distance: R + 0.02,
            radius: 0.012,
            warmth: "amber",
            rgb: arc.color,
            alpha: pinFade * 0.9,
            width: 2.2,
          });
        }
      }
    }
  }

  return {
    labels: [],
    decorations,
    meshAlphaMul: 0,
    cameraZoom: 1.1,
    cameraRotYOffset: 0.2,
    cameraTiltOffset: -0.05,
    // No occluderSphere — entities are all parked (not rendered on the
    // sphere), so no per-entity occlusion is needed. The dot cloud
    // handles its own front/back dimming via `occluder`.
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatMessageSent — "a message to anyone. any time. any place on earth."
// Flat wave-mesh scene (no globe). ~15 people scattered across the
// field. Six colored message packets fly from sender to recipient,
// arcing up off the surface like airplanes. Each arrival triggers a
// pin-drop ring + a brief wave ripple on the mesh in that person's
// color — the recipient's emit surges so the whole mesh hears it.
// ──────────────────────────────────────────────────────────────────────

// 30 "messaging" people scattered across the mesh with varied warm colors.
// Positions and colors are seeded so the layout is deterministic.
interface MsPerson {
  x: number;
  z: number;
  color: RGB;
}
const MS_PEOPLE: ReadonlyArray<MsPerson> = (() => {
  const warmColors: RGB[] = [
    WAVE_PALETTE.amber,
    WAVE_PALETTE.yellow,
    WAVE_PALETTE.warmpink,
    WAVE_PALETTE.cream,
    WAVE_PALETTE.orange,
    WAVE_PALETTE.rose,
    [252, 220, 180],
    [240, 190, 130],
  ];
  const result: MsPerson[] = [];
  // Poisson-ish scatter: jittered grid across mesh extent ±1.75 × ±1.45.
  const cols = 7;
  const rows = 5;
  let idx = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (idx >= 30) break;
      const seed = idx * 7.13 + 3;
      const gx = -1.75 + (c + 0.5) * (3.5 / cols);
      const gz = -1.3 + (r + 0.5) * (2.6 / rows);
      const jx = (rand(seed) - 0.5) * 0.3;
      const jz = (rand(seed + 11) - 0.5) * 0.22;
      const ci = Math.floor(rand(seed + 17) * warmColors.length);
      result.push({ x: gx + jx, z: gz + jz, color: warmColors[ci] });
      idx++;
    }
  }
  return result;
})();

// Background dot cloud — 200 additional faint people across a wider
// extent. Gives the scene the feel of a populated world around the
// messaging network.
const MS_BG_DOTS: ReadonlyArray<{ x: number; y: number; z: number }> = (() => {
  const dots: { x: number; y: number; z: number }[] = [];
  for (let i = 0; i < 220; i++) {
    const s = i * 13.7 + 100;
    const x = (rand(s) - 0.5) * 4.2;
    const z = (rand(s + 37) - 0.5) * 3.4;
    // Push dots that might overlap a "main" person slightly further
    // out so the main dots read as foreground.
    dots.push({ x, y: -0.5, z });
  }
  return dots;
})();

interface MsMsg {
  from: number;
  to: number;
  delay: number;
  color: RGB;
}
// 12 messages, staggered tight (150ms) so 3–4 are always flying at once.
// 5 messages spaced ~550ms apart so AT MOST one shell is in flight at
// a time. Each spark lands with clear weight; the scene reads as
// "individual wavefronts visible as distinct events" instead of a
// chaotic cluster of overlapping shells. Nearby sender/recipient pairs
// chosen so each shell has a clear origin + absorption the viewer can
// track without cognitive overload.
const MS_MESSAGES: ReadonlyArray<MsMsg> = [
  { from: 12, to: 18, delay: 0.15, color: WAVE_PALETTE.amber },
  { from: 8,  to: 2,  delay: 0.75, color: WAVE_PALETTE.cyan },
  { from: 21, to: 15, delay: 1.30, color: WAVE_PALETTE.rose },
  { from: 4,  to: 24, delay: 1.90, color: WAVE_PALETTE.warmpink },
  { from: 17, to: 9,  delay: 2.50, color: WAVE_PALETTE.teal },
];

// Wave propagation speed (world units / second). At c=2.8 a shell
// crosses ~2 world units in ~0.7s — matches the pacing of the beat
// while still feeling like finite-speed propagation, not instant.
const MS_WAVE_C = 2.8;
// Shell shimmer width — how close (radial distance from shell front)
// a person has to be before they "hear" the passing wavefront. Uses
// world units, not a fraction of radius, so close-range shells behave
// identically to far-range ones.
const MS_SHELL_WIDTH = 0.22;

/**
 * 1/r² energy conservation — shell intensity falls as its surface
 * area grows. `r0` is the "emission radius" at which intensity = 1;
 * below that we clamp to avoid singularity at the origin.
 *
 * INTENSITY_CEIL clamps the peak so the emission moment doesn't
 * register as a flashbang — we want a *felt* shock, not a bright
 * spike that blows out the eye. The shell still reads as strong at
 * close range; the visual magnitude is carried by shell thickness +
 * bass-frequency mesh pulse, not by raw luminance.
 */
const INTENSITY_CEIL = 0.48;
function wavefrontIntensity(r: number): number {
  const r0 = 0.28;
  const rr = Math.max(r, r0);
  return Math.min(INTENSITY_CEIL, (r0 * r0) / (rr * rr));
}

export const beatMessageSent: BeatLayoutFn = (entities, t) => {
  const peopleCount = Math.min(MS_PEOPLE.length, entities.length);

  // Track per-entity accumulated wave effects (resonant bloom from
  // absorption + non-resonant shimmer as shells pass through). We
  // walk every active wavefront once, stamp effects onto each person,
  // then write their final targets. This avoids the old pattern of
  // "last message wins" clobbering earlier messages' pulses.
  interface PersonFx {
    /** Absorption pop envelope — sharp attack + exponential decay.
     *  This is what drives the bass THUMP, not a sustained bloom. */
    pop: number;
    popColor: RGB | null;
    shimmer: number;
  }
  const fx: PersonFx[] = new Array(peopleCount);
  for (let i = 0; i < peopleCount; i++) {
    fx[i] = { pop: 0, popColor: null, shimmer: 0 };
  }

  const decorations: DecoEntry[] = [];

  // Background population — 200+ faint dots extending beyond the
  // messaging network.
  const bgFadeIn = Math.min(1, t / 0.5);
  decorations.push({
    kind: "dotCloud",
    points: MS_BG_DOTS,
    pixelRadius: 0.9,
    alpha: 0.18 * bgFadeIn,
    warmth: "cream",
    rgb: [230, 224, 210],
  });

  // Each message = an expanding spherical wavefront. Lifetime = the
  // time it takes for 1/r² intensity to fall below visibility.
  // intensity(R_MAX) = 0.015 → (0.28/R_MAX)² = 0.015 → R_MAX ≈ 2.28
  // R_MAX / c ≈ 0.82s → we give a small buffer and use 0.95s.
  const WAVE_LIFE = 0.95;

  for (const msg of MS_MESSAGES) {
    const from = MS_PEOPLE[msg.from];
    const to = MS_PEOPLE[msg.to];
    if (!from || !to) continue;
    const msgT = t - msg.delay;
    if (msgT < 0 || msgT > WAVE_LIFE) continue;

    const radius = msgT * MS_WAVE_C;
    const intensity = wavefrontIntensity(radius);
    if (intensity < 0.01) continue;

    // Emit the 3D shell decoration — renderer samples a Fibonacci
    // lattice on the sphere at this radius.
    decorations.push({
      kind: "wavefront",
      origin: { x: from.x, y: -0.5, z: from.z },
      radius,
      intensity,
      rgb: msg.color,
      thickness: MS_SHELL_WIDTH,
      phaseT: msgT,
    });

    // Ground-plane intersection ring — where the expanding 3D shell
    // meets the mesh (y=0 is where the mesh lives, y=-0.5 is where
    // people live, so the shell intersects the mesh whenever its
    // radius > 0.5). The intersection radius on the plane is
    // sqrt(R² - 0.5²). This reads as a ground-hugging ripple
    // expanding from directly below the sender.
    if (radius > 0.5) {
      const groundR = Math.sqrt(radius * radius - 0.25);
      decorations.push({
        kind: "ring",
        at: { x: from.x, y: -0.02, z: from.z },
        radius: groundR,
        warmth: "amber",
        rgb: msg.color,
        alpha: Math.min(0.65, intensity * 1.4),
        width: 1.25,
      });
    }

    // Exact absorption time for the resonant recipient: sender-to-
    // recipient great-circle distance divided by wave speed. This is
    // the moment the shell front crosses the recipient. We build a
    // sharp attack/decay envelope around this exact instant rather
    // than relying on shell-proximity detection (which gets smeared
    // by the engine's emit-lerp and never registers audibly).
    const rdx = to.x - from.x;
    const rdz = to.z - from.z;
    const rDist = Math.sqrt(rdx * rdx + rdz * rdz);
    const absorbT = rDist / MS_WAVE_C;
    const sinceAbs = msgT - absorbT;
    if (sinceAbs > 0 && sinceAbs < 1.1) {
      // 35ms attack for a sharp hit + ~560ms tail (e^(-t·2.2)). The
      // lick is still felt as a punch, but the tail sustains long
      // enough for the mesh wave to VISIBLY propagate outward from
      // the recipient before decaying. Under the old 280ms tail the
      // mesh ripple never had time to read before emit fell below
      // visibility — every spark fired its bass but only a few
      // produced a visible wave.
      const attack = Math.min(1, sinceAbs / 0.035);
      const decay = Math.exp(-sinceAbs * 2.2);
      const popEnv = attack * decay;
      if (popEnv > fx[msg.to].pop) {
        fx[msg.to].pop = popEnv;
        fx[msg.to].popColor = msg.color;
      }
    }

    // Non-resonant shimmer — everyone else the shell passes through
    // gets a tiny brightness bump as the wave washes over them.
    for (let i = 0; i < peopleCount; i++) {
      if (i === msg.to) continue;
      const p = MS_PEOPLE[i];
      const dx = p.x - from.x;
      const dz = p.z - from.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      const offset = Math.abs(dist - radius);
      if (offset > MS_SHELL_WIDTH) continue;
      const env = 1 - offset / MS_SHELL_WIDTH;
      fx[i].shimmer = Math.max(fx[i].shimmer, env * intensity * 0.45);
    }
  }

  // Place people — defaults + bloom/shimmer overlays.
  for (let i = 0; i < peopleCount; i++) {
    const p = MS_PEOPLE[i];
    const appear = Math.max(0, Math.min(1, (t - i * 0.025) / 0.28));
    const breathFreq = 0.55 + (i % 7) * 0.22;
    const breathPhase = (i * 1.37) % (Math.PI * 2);
    const breath = 0.82 + 0.18 * Math.sin(t * breathFreq + breathPhase);

    const pop = fx[i].pop;
    const shimmer = fx[i].shimmer;
    const popColor = fx[i].popColor ?? p.color;

    setTarget(entities[i], p.x, -0.5, p.z, {
      size: (1.15 + pop * 0.35) * appear * breath,
      alpha: (0.92 + shimmer * 0.05) * appear,
      warmth: "amber",
      halo: (0.1 + shimmer * 0.18 + pop * 0.18) * appear,
      pulse: false,
      emit: 0,
      emitFreq: 2.6,
      emitSpeed: 2.6,
      waveColor: popColor,
    });

    // Direct-stomp the absorbing entity's emit/freq/color so the pop
    // lick fires AT its sample rather than lerping toward it. The
    // engine lerps at WAVE_LERP=0.02/frame (~1.5s time constant); a
    // 280ms pop would never survive the lerp if we went through the
    // target field. Writing e.emit directly bypasses that and makes
    // the bass hum audibly THUMP at the instant of absorption.
    if (pop > 0.02) {
      const e = entities[i];
      e.emit = pop * 0.95;
      // emitFreq 1.8 → engine maps to ~107 Hz (A2) — the deepest
      // clean bass consumer speakers can reproduce.
      e.emitFreq = 1.8;
      // Faster oscillation during the pop = that "buzz in the air"
      // lick before it settles.
      e.emitSpeed = 5.2;
      e.waveR = popColor[0];
      e.waveG = popColor[1];
      e.waveB = popColor[2];
    }
  }
  parkAsAmbient(entities, peopleCount);

  return {
    labels: [],
    decorations,
    // Mesh turned up so the wave ripples from every spark absorption
    // are clearly felt. The mesh IS the medium the photons travel
    // through — making it visible sells the physics.
    meshAlphaMul: 0.82,
    // ZOOMED WAY IN — viewer is standing among 3-4 nearby nodes with
    // fewer sparks in flight (5 total across the beat, spaced ~650ms
    // apart so at most ONE shell is flying). Each wavefront becomes a
    // distinct event you can track from emission to absorption.
    // Unique angle via stronger rotY + positive tilt so we're slightly
    // looking down into the field — "observing the choreography of
    // individual messages" rather than "watching a crowd."
    cameraZoom: 1.65,
    cameraRotYOffset: 0.55,
    cameraTiltOffset: 0.08,
    meshExtentMul: 1.3,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatMessageFly — "a message to anyone. any time. any place on earth."
// Three bright message entities traveling along great-circle arcs
// (proper 3D spherical slerp). Each carries its own color and emits
// a wave that the globe's surface wave physics renders as a real
// traveling ripple. Background dots stay faint. Messages loop
// continuously — planetary traffic.
// ──────────────────────────────────────────────────────────────────────

export const beatMessageFly: BeatLayoutFn = (entities, t) => {
  const R = 1.05;
  const center = { x: 0, y: -0.45, z: 0 };
  const AXIS_TILT = 0.38;
  const rotation = t * 0.25;

  const cosTilt = Math.cos(AXIS_TILT);
  const sinTilt = Math.sin(AXIS_TILT);

  // Background city dots — faint, stable context.
  const bgCount = Math.min(28, entities.length - 3);
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < bgCount; i++) {
    const yFrac = 1 - (i / (bgCount - 1)) * 2;
    const ringR = Math.sqrt(Math.max(0, 1 - yFrac * yFrac));
    const theta = goldenAngle * i + rotation * 0.8;
    const lx = Math.cos(theta) * ringR;
    const ly = -yFrac;
    const lz = Math.sin(theta) * ringR;
    const ux = lx * cosTilt - ly * sinTilt;
    const uy = lx * sinTilt + ly * cosTilt;
    const uz = lz;
    const front = uz > -0.05;
    setTarget(
      entities[i + 3],
      center.x + ux * R,
      center.y + uy * R,
      center.z + uz * R,
      {
        size: 0.55,
        alpha: front ? 0.4 : 0.08,
        warmth: "cream",
        halo: 0.02,
        pulse: false,
        emit: 0,
      }
    );
  }
  parkAsAmbient(entities, 3 + bgCount);

  // Three messages on looping great-circle arcs.
  interface Msg {
    seed: number;
    color: RGB;
    offset: number;
    duration: number;
  }
  const messages: Msg[] = [
    { seed: 0.17, color: WAVE_PALETTE.amber, offset: 0.0, duration: 2.4 },
    { seed: 0.63, color: WAVE_PALETTE.cyan, offset: 0.8, duration: 2.6 },
    { seed: 0.38, color: WAVE_PALETTE.rose, offset: 1.6, duration: 2.2 },
  ];

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    const localT = ((t - msg.offset) % msg.duration + msg.duration) % msg.duration;
    const p = localT / msg.duration;

    // Random endpoints derived from seed.
    const sLat = (rand(msg.seed) - 0.5) * Math.PI * 0.9;
    const sLon = rand(msg.seed + 7) * Math.PI * 2;
    const eLat = (rand(msg.seed + 13) - 0.5) * Math.PI * 0.9;
    const eLon = rand(msg.seed + 17) * Math.PI * 2;

    const sx = Math.cos(sLat) * Math.cos(sLon);
    const sy = Math.sin(sLat);
    const sz = Math.cos(sLat) * Math.sin(sLon);
    const ex = Math.cos(eLat) * Math.cos(eLon);
    const ey = Math.sin(eLat);
    const ez = Math.cos(eLat) * Math.sin(eLon);

    // Slerp along great-circle arc.
    const dot = Math.max(-1, Math.min(1, sx * ex + sy * ey + sz * ez));
    const omega = Math.acos(dot);
    const sinOmega = Math.sin(omega);
    let lx: number;
    let ly: number;
    let lz: number;
    if (sinOmega < 0.001) {
      lx = sx;
      ly = sy;
      lz = sz;
    } else {
      const a = Math.sin((1 - p) * omega) / sinOmega;
      const b = Math.sin(p * omega) / sinOmega;
      lx = a * sx + b * ex;
      ly = a * sy + b * ey;
      lz = a * sz + b * ez;
    }

    // Apply globe rotation and axis tilt.
    const r1x = lx * Math.cos(rotation) + lz * Math.sin(rotation);
    const r1z = -lx * Math.sin(rotation) + lz * Math.cos(rotation);
    const r2y = ly * cosTilt - r1z * sinTilt;
    const r2z = ly * sinTilt + r1z * cosTilt;
    const r2x = r1x;

    const px = center.x + r2x * R;
    const py = center.y + r2y * R;
    const pz = center.z + r2z * R;
    const front = r2z > -0.05;

    // Fade in at trip start, fade out near end for smooth loop.
    const fadeIn = Math.min(1, p / 0.15);
    const fadeOut = Math.min(1, (1 - p) / 0.15);
    const life = Math.min(fadeIn, fadeOut);

    setTarget(entities[i], px, py, pz, {
      size: 1.3 * life,
      alpha: (front ? 1 : 0.15) * life,
      warmth: "amber",
      halo: 0.4 * life,
      pulse: false,
      emit: 0.6 * life,
      emitFreq: 5.5,
      emitSpeed: 4,
      waveColor: msg.color,
    });
  }

  return {
    labels: [],
    decorations: [
      {
        kind: "globe",
        at: center,
        radius: R,
        rotation,
        axisTilt: AXIS_TILT,
        waveAmplitude: 0.05, // small surface ripples around each message
        warmth: "cream",
        alpha: 0.32,
      },
    ],
    meshAlphaMul: 0,
    cameraZoom: 1.15,
    cameraRotYOffset: -0.35,
    cameraTiltOffset: -0.12,
    occluderSphere: { x: center.x, y: center.y, z: center.z, radius: R },
  };
};

// ──────────────────────────────────────────────────────────────────────
// RADAR — shared config for beatUntilNow (boot) and beatRead (scan).
// Night-vision aesthetic: amber phosphor grid viewed from near-overhead.
// ──────────────────────────────────────────────────────────────────────

interface RadarPerson {
  id: number;
  angle: number;   // radial angle on the scope
  radius: number;  // distance from center
  freq: number;    // emotion frequency
  color: RGB;
}

const RADAR_PEOPLE: ReadonlyArray<RadarPerson> = [
  { id: 1, angle: 0.4,  radius: 0.95, freq: 5.5, color: [255, 215, 100] }, // happiness
  { id: 2, angle: 1.9,  radius: 1.25, freq: 2.4, color: [110, 145, 200] }, // sadness
  { id: 3, angle: 3.1,  radius: 0.85, freq: 6.8, color: [180, 130, 210] }, // anxiety
  { id: 4, angle: 4.3,  radius: 1.15, freq: 8.5, color: [240, 80, 95]  }, // anger
  { id: 5, angle: 5.4,  radius: 1.0,  freq: 1.8, color: [180, 230, 200] }, // calm
];

const RADAR_PHOSPHOR: RGB = [110, 240, 170];

// ──────────────────────────────────────────────────────────────────────
// beatHalfBlind — "we've been walking through them half-blind."
// First-person. We ARE the ball, grounded on the plane (not floating
// above it). The camera sits at the ball's perspective with a very
// shallow tilt so the plane lies just below us, nearly edge-on — the
// floor of a world we're walking through. Five other people are
// scattered around us as faint, still, ghost-colored dots with no
// emission: they exist, we pass near them, but we cannot see their
// signals. Mesh alpha stays very low so the plane is almost invisible
// too. No ripples, no flashes, no emission anywhere — the emptiness
// IS the point. "Half-blind" is rendered as "you can tell the world
// is there, and you cannot read any of it."
// ──────────────────────────────────────────────────────────────────────

interface HalfBlindPresence {
  id: number;
  x: number;
  z: number;
}
const HALF_BLIND_PRESENCES: ReadonlyArray<HalfBlindPresence> = [
  { id: 1, x: -0.95, z: -0.55 }, // front-left
  { id: 2, x:  0.85, z: -0.40 }, // front-right
  { id: 3, x: -0.45, z:  0.75 }, // back-left
  { id: 4, x:  1.05, z:  0.55 }, // back-right
  { id: 5, x: -1.20, z:  0.15 }, // distant left
];

// Dim cream/grey — invisible signals rendered as near-invisible bodies.
const HALF_BLIND_GHOST: RGB = [198, 193, 183];

export const beatHalfBlind: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    // Hero IS the camera POV. Grounded on the plane (y=0, not y=-0.5),
    // but alpha 0 because we look OUT from the ball, we don't watch it.
    const hero = entities[0];
    hero.x = 0; hero.y = 0; hero.z = 0;
    hero.size = 0.35; hero.alpha = 0; hero.halo = 0;
    hero.emit = 0; hero.targetEmit = 0;
    hero.warmth = "cream"; hero.targetWarmth = "cream";
    hero.waveR = 242; hero.waveG = 238; hero.waveB = 228;
    hero.targetWaveR = 242; hero.targetWaveG = 238; hero.targetWaveB = 228;

    // Presences — silent ghosts scattered across the plane. Grounded
    // at y=0, dim alpha, zero halo, zero emission. Visible as
    // suggestions, not readable as people.
    for (const p of HALF_BLIND_PRESENCES) {
      const e = entities[p.id];
      e.x = p.x; e.y = 0; e.z = p.z;
      e.size = 0.5; e.alpha = 0.16; e.halo = 0;
      e.emit = 0; e.targetEmit = 0;
      e.emitFreq = 0; e.targetEmitFreq = 0;
      e.emitSpeed = 0; e.targetEmitSpeed = 0;
      e.waveR = HALF_BLIND_GHOST[0];
      e.waveG = HALF_BLIND_GHOST[1];
      e.waveB = HALF_BLIND_GHOST[2];
      e.targetWaveR = HALF_BLIND_GHOST[0];
      e.targetWaveG = HALF_BLIND_GHOST[1];
      e.targetWaveB = HALF_BLIND_GHOST[2];
    }
  }

  // Walking head-sway: a slow rotation of the view (looking around as
  // we move) and a tiny tilt bob (gait). No jolts, no shoves — nothing
  // happens TO us in this scene. We just walk, blind.
  const headSway = Math.sin(t * 0.42) * 0.22;
  const headBob = Math.sin(t * 0.9) * 0.012;

  // Hero holds at origin, grounded, invisible.
  setTarget(entities[0], 0, 0, 0, {
    size: 0.35,
    alpha: 0,
    warmth: "cream",
    halo: 0,
    pulse: false,
    emit: 0,
  });

  // Presences hold their positions, silent and dim. No emission, no
  // waves, no patches.
  for (const p of HALF_BLIND_PRESENCES) {
    setTarget(entities[p.id], p.x, 0, p.z, {
      size: 0.5,
      alpha: 0.16,
      warmth: "cream",
      halo: 0,
      pulse: false,
      emit: 0,
      emitFreq: 0,
      emitSpeed: 0,
      waveColor: HALF_BLIND_GHOST,
    });
  }

  parkAsAmbient(entities, HALF_BLIND_PRESENCES.length + 1);

  return {
    labels: [],
    decorations: [],
    // Plane barely visible — present enough to ground the feet, not
    // bright enough to read as a wave field. You can tell there's a
    // floor. You cannot tell anything else.
    meshAlphaMul: 0.14,
    // Very shallow tilt — effective ~3° from horizontal. The plane
    // lies just below us, nearly edge-on, like looking across a table.
    // Zoom pulled tight for the first-person feel.
    cameraZoom: 1.25,
    cameraRotYOffset: headSway,
    cameraTiltOffset: -0.56 + headBob,
  };
};

// ──────────────────────────────────────────────────────────────────────
// EMOTION PHYSICS — single-person emotion demos that precede the pair
// demos. Each is a fast (~1.8s) reveal of one emotional state as wave
// physics, with its own color and camera angle. Together they expose
// the language: emotions ARE waves before relationships are.
// ──────────────────────────────────────────────────────────────────────

// All emotion beats share the same camera HEIGHT (zoom + tilt) so the
// viewer feels they're orbiting one subject at the same level — only
// the rotation around the subject changes. This matches the close,
// low, side-on framing of beatAnger which reads as the most intimate.
// Emotion-demo camera — eye level with the 3D wave mesh. Tilt -0.42
// gives effective ~3.4° from horizontal so the wave peaks/troughs
// read as surface undulations sweeping toward the viewer.
const EMOTION_ZOOM = 1.5;
const EMOTION_TILT = -0.42;

// beatEmotionsSetup — "but so are your emotions."
// Central cream "you" + 5 emotion satellites orbiting in a ring, each
// a preview color of the emotions that follow (yellow / blue / violet /
// red / sage). The waves they emit mix on the ground mesh — the palette
// of feelings living inside one person.
const EMOTION_PREVIEW_COLORS: ReadonlyArray<RGB> = [
  [255, 215, 100], // happiness — yellow
  [110, 145, 200], // sadness — blue
  [180, 130, 210], // anxiety — violet
  [240, 80, 95],   // anger — red
  [180, 230, 200], // calm — sage
];
export const beatEmotionsSetup: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    for (const e of entities) {
      e.emit = 0;
      e.targetEmit = 0;
      e.waveR = 242;
      e.waveG = 238;
      e.waveB = 228;
      e.targetWaveR = 242;
      e.targetWaveG = 238;
      e.targetWaveB = 228;
    }
  }
  const emerge = Math.min(1, t / 0.4);
  const breath = 0.5 + 0.5 * Math.sin(t * 1.0);
  // Central "you" — cream, breathing.
  setTarget(entities[0], 0, -0.5, 0, {
    size: (1.95 + breath * 0.1) * emerge,
    alpha: emerge,
    warmth: "cream",
    halo: (0.2 + breath * 0.08) * emerge,
    pulse: false,
    emit: 0,
  });
  // 5 emotion satellites orbit around the center at radius ~1.15. Each
  // rises into place at a staggered delay so the palette assembles one
  // color at a time before settling into rotation.
  const ORBIT_R = 1.15;
  const ORBIT_SPEED = 0.35;
  for (let i = 0; i < 5; i++) {
    const stagger = 0.08 + i * 0.07;
    const sEmerge = Math.max(0, Math.min(1, (t - stagger) / 0.35));
    const angle = (i / 5) * Math.PI * 2 + t * ORBIT_SPEED;
    const x = Math.cos(angle) * ORBIT_R;
    const z = Math.sin(angle) * ORBIT_R;
    const pulse = 0.5 + 0.5 * Math.sin(t * 1.6 + i * 1.2);
    const rgb = EMOTION_PREVIEW_COLORS[i];
    setTarget(entities[1 + i], x, -0.5, z, {
      size: (0.95 + pulse * 0.1) * sEmerge,
      alpha: 0.85 * sEmerge,
      warmth: "cream",
      halo: (0.18 + pulse * 0.08) * sEmerge,
      pulse: false,
      emit: 0.42 * sEmerge,
      emitFreq: 3.2,
      emitSpeed: 2.2,
      waveColor: rgb,
    });
  }
  parkAsAmbient(entities, 6);
  return {
    labels: [],
    decorations: [],
    cameraZoom: EMOTION_ZOOM,
    cameraRotYOffset: 0,
    cameraTiltOffset: EMOTION_TILT,
  };
};

// beatHappiness — bright yellow, energetic high-frequency pulse + bob.
// Wave BUILDS quickly (0.35s smooth-step) from a low initial emit to
// full amplitude, rather than snapping to 0.9 on frame 1. Snapping
// caused a visible jitter because the mesh went from flat to full-
// energy in one frame. Ramping gives the happy wave a visible
// BLOOM — you see the joy rising.
export const beatHappiness: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    const h = entities[0];
    h.x = 0; h.y = -0.5; h.z = 0;
    h.size = 1.85;
    h.alpha = 0.95;
    h.halo = 0.24;
    // Seed a HIGH initial emit + deeply pre-advanced phase so the
    // happy wave is already propagating across the mesh on frame 1
    // — no "empty then slowly warms up" phase. You see the full
    // wave pattern the moment she says "happiness."
    h.emit = 0.80;
    h.emitFreq = 5.5;
    h.emitSpeed = 4.5;
    h.waveR = 255; h.waveG = 215; h.waveB = 100;
    h.phase = 7.5;
  }
  // Subtle 0.35s swell — amplitude lifts from already-visible to
  // peak. Pattern is there from frame 1; this just intensifies it.
  const bloomT = Math.min(1, t / 0.35);
  const bloom = bloomT * bloomT * (3 - 2 * bloomT);
  const bob = Math.sin(t * 4) * 0.04;
  setTarget(entities[0], 0, -0.5 + bob, 0, {
    size: 1.85 + 0.15 * bloom,
    alpha: 0.95 + 0.05 * bloom,
    warmth: "amber",
    halo: 0.24 + 0.08 * bloom,
    pulse: true,
    emit: 0.80 + 0.30 * bloom,
    emitFreq: 5.5,
    emitSpeed: 4.5,
    waveColor: [255, 215, 100],
  });
  parkAsAmbient(entities, 1);
  return {
    labels: [],
    decorations: [],
    cameraZoom: EMOTION_ZOOM,
    cameraRotYOffset: 0.65,
    cameraTiltOffset: EMOTION_TILT,
  };
};

// beatSadness — muted blue, slow long-wavelength pulse, low halo.
// Waves already going — orb sits heavy from frame 1, emitting its
// slow melancholy wave signature into the mesh.
export const beatSadness: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    const h = entities[0];
    h.x = 0; h.y = -0.5; h.z = 0;
    h.size = 1.7;
    h.alpha = 0.85;
    h.halo = 0.14;
    // Heavy slow wave already propagating — the melancholy swell
    // is visible on the mesh from the first frame she says "sadness".
    h.emit = 0.58;
    h.emitFreq = 2.4;
    h.emitSpeed = 1.4;
    h.waveR = 110; h.waveG = 145; h.waveB = 200;
    h.phase = 4.8;
  }
  const bloomT = Math.min(1, t / 0.4);
  const bloom = bloomT * bloomT * (3 - 2 * bloomT);
  setTarget(entities[0], 0, -0.5, 0, {
    size: 1.7 + 0.08 * bloom,
    alpha: 0.85 + 0.05 * bloom,
    warmth: "cool",
    halo: 0.14 + 0.04 * bloom,
    pulse: false,
    emit: 0.58 + 0.18 * bloom,
    emitFreq: 2.4,
    emitSpeed: 1.4,
    waveColor: [110, 145, 200],
  });
  parkAsAmbient(entities, 1);
  return {
    labels: [],
    decorations: [],
    cameraZoom: EMOTION_ZOOM,
    cameraRotYOffset: -0.7,
    cameraTiltOffset: EMOTION_TILT,
  };
};

// beatAnxiety — muted violet, fast irregular high-freq pulse with
// constant low-amplitude jitter. Waves already going — the anxious
// pattern on the mesh reads from frame 1.
export const beatAnxiety: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    const h = entities[0];
    h.x = 0; h.y = -0.5; h.z = 0;
    h.size = 1.8;
    h.alpha = 1;
    h.halo = 0.20;
    // Tight high-freq wave already propagating — anxious ripples
    // are visible immediately, not building from flat.
    h.emit = 0.72;
    h.emitFreq = 6.8;
    h.emitSpeed = 5.5;
    h.waveR = 180; h.waveG = 130; h.waveB = 210;
    h.phase = 9.0;
  }
  const bloomT = Math.min(1, t / 0.3);
  const bloom = bloomT * bloomT * (3 - 2 * bloomT);
  const jitterX = Math.sin(t * 24) * 0.022 + Math.cos(t * 31) * 0.012;
  const jitterZ = Math.cos(t * 27) * 0.018;
  const racing = 0.5 + 0.5 * Math.sin(t * 5) * Math.cos(t * 3.7);
  setTarget(entities[0], jitterX, -0.5, jitterZ, {
    size: 1.8 + 0.1 * bloom + racing * 0.08,
    alpha: 1,
    warmth: "cool",
    halo: 0.20 + 0.06 * bloom + racing * 0.12,
    pulse: false,
    emit: 0.72 + 0.26 * bloom,
    emitFreq: 6.8,
    emitSpeed: 5.5,
    waveColor: [180, 130, 210],
  });
  parkAsAmbient(entities, 1);
  return {
    labels: [],
    decorations: [],
    cameraZoom: EMOTION_ZOOM,
    cameraRotYOffset: 1.25,
    cameraTiltOffset: EMOTION_TILT,
  };
};

// beatAnger — sharp red, very fast frequency, big position shake.
// Waves already going — anger's signature pattern fills the mesh
// from frame 1.
export const beatAnger: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    const h = entities[0];
    h.x = 0; h.y = -0.5; h.z = 0;
    h.size = 2.1;
    h.alpha = 1;
    h.halo = 0.36;
    // Raging wave already smashing through the mesh. Anger doesn't
    // build — it ARRIVES. High initial emit + deep phase advance.
    h.emit = 0.95;
    h.emitFreq = 8.5;
    h.emitSpeed = 7.0;
    h.waveR = 240; h.waveG = 80; h.waveB = 95;
    h.phase = 11.0;
  }
  const bloomT = Math.min(1, t / 0.3);
  const bloom = bloomT * bloomT * (3 - 2 * bloomT);
  const shake = Math.sin(t * 18) * 0.045;
  const shakeZ = Math.cos(t * 15) * 0.035;
  setTarget(entities[0], shake, -0.5, shakeZ, {
    size: 2.1 + 0.15 * bloom,
    alpha: 1,
    warmth: "amber",
    halo: 0.36 + 0.12 * bloom,
    pulse: true,
    emit: 0.95 + 0.30 * bloom,
    emitFreq: 8.5,
    emitSpeed: 7,
    waveColor: [240, 80, 95],
  });
  parkAsAmbient(entities, 1);
  return {
    labels: [],
    decorations: [],
    cameraZoom: EMOTION_ZOOM,
    cameraRotYOffset: -1.3,
    cameraTiltOffset: EMOTION_TILT,
  };
};

// beatCalm (rendered as "tranquility.") — soft sage/mint, very slow
// gentle wave. Waves already going — the deep tranquil breathing
// pattern is on the mesh from frame 1.
export const beatCalm: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    const h = entities[0];
    h.x = 0; h.y = -0.5; h.z = 0;
    h.size = 1.9;
    h.alpha = 0.95;
    h.halo = 0.20;
    // Long gentle wave already breathing — tranquility's slow
    // rhythm is visible from frame 1, even if subtle.
    h.emit = 0.62;
    h.emitFreq = 1.8;
    h.emitSpeed = 1.2;
    h.waveR = 180; h.waveG = 230; h.waveB = 200;
    h.phase = 3.6;
  }
  const bloomT = Math.min(1, t / 0.5);
  const bloom = bloomT * bloomT * (3 - 2 * bloomT);
  const breath = 0.5 + 0.5 * Math.sin(t * 0.8);
  setTarget(entities[0], 0, -0.5, 0, {
    size: 1.9 + 0.05 * bloom + breath * 0.1,
    alpha: 0.95 + 0.05 * bloom,
    warmth: "cream",
    halo: 0.20 + 0.06 * bloom + breath * 0.08,
    pulse: false,
    emit: 0.62 + 0.18 * bloom,
    emitFreq: 1.8,
    emitSpeed: 1.2,
    waveColor: [180, 230, 200],
  });
  parkAsAmbient(entities, 1);
  return {
    labels: [],
    decorations: [],
    cameraZoom: EMOTION_ZOOM,
    cameraRotYOffset: 0,
    cameraTiltOffset: EMOTION_TILT,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatFriendship — "this is friendship."
// Two amber emitters, matched frequency, matched phase. With ~1.1 units
// between them and a 0.92-unit wavelength (freq 6.8), viewers see a
// stable lattice of hyperbolic constructive-interference fringes — a
// woven pattern of bright peaks pulsing between the two sources. The
// slow emitSpeed (2.4) means each crest ROLLS through the pattern
// instead of flickering: hypnotic, steady, a shared rhythm breathing
// in the space between two people.
// ──────────────────────────────────────────────────────────────────────

export const beatFriendship: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    // Snap both orbs to full emitting state — interference lattice
    // already visible on the mesh on frame 1.
    const a = entities[0], b = entities[1];
    a.x = -0.55; a.y = -0.5; a.z = 0;
    a.size = 2.0; a.alpha = 1; a.halo = 0.22;
    a.emit = 0.82; a.emitFreq = 6.8; a.emitSpeed = 2.4;
    a.waveR = WAVE_PALETTE.amber[0]; a.waveG = WAVE_PALETTE.amber[1]; a.waveB = WAVE_PALETTE.amber[2];
    a.phase = 4.2;
    b.x = 0.55; b.y = -0.5; b.z = 0;
    b.size = 2.0; b.alpha = 1; b.halo = 0.22;
    b.emit = 0.82; b.emitFreq = 6.8; b.emitSpeed = 2.4;
    b.waveR = WAVE_PALETTE.yellow[0]; b.waveG = WAVE_PALETTE.yellow[1]; b.waveB = WAVE_PALETTE.yellow[2];
    b.phase = 4.2; // matched phase — their waves align constructively
  }
  setTarget(entities[0], -0.55, -0.5, 0, {
    size: 2.0, alpha: 1, warmth: "amber", halo: 0.22, pulse: false,
    emit: 0.82, emitFreq: 6.8, emitSpeed: 2.4,
    waveColor: WAVE_PALETTE.amber,
  });
  setTarget(entities[1], 0.55, -0.5, 0, {
    size: 2.0, alpha: 1, warmth: "amber", halo: 0.22, pulse: false,
    emit: 0.82, emitFreq: 6.8, emitSpeed: 2.4,
    waveColor: WAVE_PALETTE.yellow,
  });
  parkAsAmbient(entities, 2);
  return {
    labels: [],
    decorations: [],
    cameraZoom: 1.3,
    cameraRotYOffset: 0.15,
    cameraTiltOffset: 0.1,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatLove — "this is love."
// Two entities pulled close. Long wavelength (low freq) means their
// waves unify into one swelling field. Warm rose + warmpink — one body
// of feeling. Shared halo pulse. Both breathe together.
// ──────────────────────────────────────────────────────────────────────

export const beatLove: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    // Snap into the hypnotic state immediately — slow, long-wavelength
    // wave already rolling across the mesh. Matched frequency + matched
    // phase = one unified breathing field, not two separate pulses.
    const a = entities[0], b = entities[1];
    a.x = -0.22; a.y = -0.5; a.z = 0;
    a.size = 1.95; a.alpha = 1; a.halo = 0.38;
    a.emit = 1.00;
    // Very long wavelength (freq 2.3) + slow roll (speed 1.45). At this
    // scale, separation (0.44) is far less than one wavelength (~2.7
    // units), so the two emitters' fields fuse into ONE big breathing
    // swell. Not two waves overlapping — one field.
    a.emitFreq = 2.3;
    a.emitSpeed = 1.45;
    a.waveR = WAVE_PALETTE.warmpink[0]; a.waveG = WAVE_PALETTE.warmpink[1]; a.waveB = WAVE_PALETTE.warmpink[2];
    a.phase = 3.0;
    b.x = 0.22; b.y = -0.5; b.z = 0;
    b.size = 1.95; b.alpha = 1; b.halo = 0.38;
    b.emit = 1.00;
    b.emitFreq = 2.3;
    b.emitSpeed = 1.45;
    b.waveR = WAVE_PALETTE.rose[0]; b.waveG = WAVE_PALETTE.rose[1]; b.waveB = WAVE_PALETTE.rose[2];
    b.phase = 3.0; // phase-locked with partner — they breathe as one
  }
  // Shared breath — both orbs grow and shrink in perfect sync, AND the
  // wave amplitude itself breathes with them. The whole field inhales
  // and exhales as one body.
  const breath = 0.5 + 0.5 * Math.sin(t * 1.15);
  const emitBreath = 0.92 + breath * 0.22; // 0.92 → 1.14
  setTarget(entities[0], -0.22, -0.5, 0, {
    size: 1.95 + breath * 0.18,
    alpha: 1,
    warmth: "amber",
    halo: 0.38 + breath * 0.22,
    pulse: true,
    emit: emitBreath,
    emitFreq: 2.3,
    emitSpeed: 1.45,
    waveColor: WAVE_PALETTE.warmpink,
  });
  setTarget(entities[1], 0.22, -0.5, 0, {
    size: 1.95 + breath * 0.18,
    alpha: 1,
    warmth: "amber",
    halo: 0.38 + breath * 0.22,
    pulse: true,
    emit: emitBreath,
    emitFreq: 2.3,
    emitSpeed: 1.45,
    waveColor: WAVE_PALETTE.rose,
  });
  parkAsAmbient(entities, 2);
  return {
    labels: [],
    decorations: [],
    cameraZoom: 1.5,
    cameraRotYOffset: 0,
    cameraTiltOffset: -0.15,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatFight — "this is a fight."
// Physics of "almost but not quite" — two frequencies nearly aligned
// (6.5 and 5.6) that BEAT. The small frequency delta (~0.9 Hz) creates
// a visible amplitude envelope: the mesh between them alternately
// sums to near-2A constructive peaks and near-zero cancellation
// troughs, a throbbing pattern that keeps trying to resolve and
// keeps failing. Opposite initial phase makes the first throb a
// cancellation zone right in the middle. Colors clash (rose vs cyan),
// shake adds instability. Not noise — unresolved rhythm.
// ──────────────────────────────────────────────────────────────────────

export const beatFight: BeatLayoutFn = (entities, t) => {
  if (t < 0.04) {
    // Snap into the clash from frame 1 — two almost-matched waves
    // already beating against each other. No fade-in; the fight is
    // already happening.
    const a = entities[0], b = entities[1];
    a.x = -0.85; a.y = -0.5; a.z = 0;
    a.size = 2.15; a.alpha = 1; a.halo = 0.3;
    a.emit = 0.95;
    a.emitFreq = 6.5; // sharp — just above partner
    a.emitSpeed = 4.0;
    a.waveR = WAVE_PALETTE.rose[0]; a.waveG = WAVE_PALETTE.rose[1]; a.waveB = WAVE_PALETTE.rose[2];
    // Opposite phase — peaks of one align with troughs of the other.
    // Combined with the near-matched frequency, this creates a beating
    // pattern: constructive/destructive interference alternates across
    // the mesh every ~1.1s. "Almost in sync, never quite" made visible.
    a.phase = 0;
    b.x = 0.85; b.y = -0.5; b.z = 0;
    b.size = 2.15; b.alpha = 1; b.halo = 0.3;
    b.emit = 0.95;
    b.emitFreq = 5.6; // just below partner — beat delta ~0.9 Hz
    b.emitSpeed = 3.6;
    b.waveR = WAVE_PALETTE.cyan[0]; b.waveG = WAVE_PALETTE.cyan[1]; b.waveB = WAVE_PALETTE.cyan[2];
    b.phase = Math.PI; // opposite phase — first throb is cancellation
  }
  const shake = Math.sin(t * 14) * 0.045;
  const shakeZ = Math.cos(t * 11) * 0.03;

  setTarget(entities[0], -0.85 + shake, -0.5, shakeZ, {
    size: 2.15,
    alpha: 1,
    warmth: "cream",
    halo: 0.3,
    pulse: true,
    emit: 0.95,
    emitFreq: 6.5,
    emitSpeed: 4.0,
    waveColor: WAVE_PALETTE.rose,
  });
  setTarget(entities[1], 0.85 - shake, -0.5, -shakeZ, {
    size: 2.15,
    alpha: 1,
    warmth: "cream",
    halo: 0.3,
    pulse: true,
    emit: 0.95,
    emitFreq: 5.6,
    emitSpeed: 3.6,
    waveColor: WAVE_PALETTE.cyan,
  });
  parkAsAmbient(entities, 2);
  return {
    labels: [],
    decorations: [],
    cameraZoom: 1.1,
    cameraRotYOffset: -0.7,
    cameraTiltOffset: -0.12,
  };
};

// ──────────────────────────────────────────────────────────────────────
// beatGrowingApart — "this is growing apart."
// Start matched — amber, same frequency, a clean interference field
// between them. Over the beat the partner drifts outward AND its
// frequency decouples (4.5 → 6.0), so the mesh visibly shows shorter,
// faster wavelengths emerging on that side: "we don't speak the same
// rhythm anymore." Simultaneously the partner's emission + halo +
// color fade — the signal thins toward a grey ghost. Hero holds the
// original rhythm throughout. The connection dies as PHYSICS, not
// metaphor.
// ──────────────────────────────────────────────────────────────────────

export const beatGrowingApart: BeatLayoutFn = (entities, t) => {
  const p = Math.min(1, t / 2.0);
  const eased = p * p * (3 - 2 * p);

  // Entity 0 — holds steady, amber, emitting throughout at the
  // original shared rhythm (4.5 Hz). The anchor that DIDN'T change.
  setTarget(entities[0], -0.4 - eased * 0.45, -0.5, 0, {
    size: 2.0,
    alpha: 1,
    warmth: "amber",
    halo: 0.22,
    pulse: false,
    emit: 0.65,
    emitFreq: 4.5,
    emitSpeed: 3.0,
    waveColor: WAVE_PALETTE.amber,
  });

  // Entity 1 — drifts outward; emission + alpha + color all fade; AND
  // the emitted wavelengths shorten (freq 4.5 → 6.0) so the mesh
  // shows the rhythmic decoupling, not just the amplitude decay.
  const fadeEmit = 0.65 * (1 - eased * 0.9);
  const fadeAlpha = 1 - eased * 0.5;
  const partnerFreq = 4.5 + eased * 1.5;
  const [wR, wG, wB] = WAVE_PALETTE.amber;
  const desatColor: RGB = [
    wR + (170 - wR) * eased,
    wG + (170 - wG) * eased,
    wB + (170 - wB) * eased,
  ];
  setTarget(entities[1], 0.4 + eased * 0.85, -0.5, eased * 0.1, {
    size: 2.0 - eased * 0.45,
    alpha: fadeAlpha,
    warmth: "cream",
    halo: 0.22 * (1 - eased * 0.75),
    pulse: false,
    emit: fadeEmit,
    emitFreq: partnerFreq,
    emitSpeed: 3.0,
    waveColor: desatColor,
  });

  parkAsAmbient(entities, 2);
  return {
    labels: [],
    decorations: [],
    cameraZoom: 1.0,
    cameraRotYOffset: 0.4,
    cameraTiltOffset: 0.08,
  };
};

// ══════════════════════════════════════════════════════════════════════
// LAYOUTS — v13 "The Investigation" ordering.
//
// Maps 1:1 to BEATS in Chapter1.tsx. Each slot is the beat for that
// line of the monologue. Existing layouts are reused where the physics
// already matches the narrative; new v13 layouts (beatGlobeArcs,
// beatAlone, etc.) fill the gaps.
// ══════════════════════════════════════════════════════════════════════

export const LAYOUTS: BeatLayoutFn[] = [
  // Act I — The Observation
  beat1,                //  1. look at this.
  beatConnectedWorld,   //  2. we've built the most connected generation in history.
  beatMessageSent,      //  3. a message to anyone, any time, any place on earth.

  // Act II — The Paradox
  beatAlone,          //  4. and somehow — we've never felt more alone.
  beatChart,          //  5. loneliness is at the highest level ever recorded.
  beatCounter600,     //  6. the average person knows six hundred.
  beatClusterDozens,  //  7. works with dozens.
  beatScroll,         //  8. hundreds of contacts in their phone.
  beatSilence,        //  9. most nights, silence.

  // Act III — The Question
  beat5,              // 10. so we started asking —
  beatAllPairs,       // 11. what if the problem isn't how many people we know.
  beatMeet,           // 12. what if we've never been able to see the relationships themselves.
  beatExchange,       // 13. every moment between two people leaves a trace.
  beatScatter,        // 14. the drift.
  beatAlign,          // 15. the repair.
  beatQuiet,          // 16. the quiet moments that decide everything.

  // Act IV — The Insight
  beatMeet,             // 17. relationships are physics.

  // Emotion physics demo — same camera height, rotation varies per emotion.
  beatEmotionsSetup,    // 18. but so are your emotions.
  beatHappiness,        // 19. happiness.
  beatSadness,          // 20. sadness.
  beatAnxiety,          // 21. anxiety.
  beatAnger,            // 22. anger.
  beatCalm,             // 23. calm.
  beatMeet,             // 24. now put two together.

  // Pair physics demo
  beatFriendship,       // 25. this is friendship.
  beatLove,             // 26. this is love.
  beatFight,            // 27. this is a fight.
  beatGrowingApart,     // 28. this is growing apart.
  beatHalfBlind,        // 29. we've been walking through them half-blind.

  // Act V — The Timing
  beatRead,             // 30. for the first time, the invisible becomes visible.
  beatNoise,            // 32. not the posts. not the clicks.

  // Act VI — The Tool
  beatEmblem,           // 33. that's what we built.
  beatDrift,            // 35. shows you what's drifting.
  beatGrowing,          // 36. what's growing.
  beatPath,             // 37. and what to do next.
  beatHold,             // 36. so you can actually hold the people who matter.

  // Act VII — The Promise
  beatOutcomes,         // 39. every good thing in your life.
  beatPromiseJob,       // 40. the job.
  beatPromiseLove,      // 41. the love.
  beatPromiseLife,      // 42. the life you want.
  beat2,                // 43. it lives inside someone else.
  beatFound,            // 44. you just need to know how to reach them.

  // Finale
  beatNotAlone,         // 45. the crowd is over. the circle begins. suron.
];
